import type {CreativeContextSelection, CreativeFormatId, QuickStudyCommand} from '@digital-twins/shared-types';
import {useThreadQuickStudies, useThreadQuickStudyState} from '@/lib/hooks/use-thread-quick-studies'
import {quickStudiesApi} from '@/lib/api/quick-study-native'
import {ThreadQuickStudyCard} from './ThreadQuickStudyCard'
import {ThreadQuickStudyPanel} from './ThreadQuickStudyPanel'
import {DEFAULT_PLANNED_SAMPLE_SIZE} from '@digital-twins/shared-types'
import type {StudyArtifactAttentionTarget} from '@digital-twins/shared-types'
import {isQuickStudyAttentionTarget, isStudyArtifactAttentionTarget, isStudyQuestionnaireAttentionTarget} from '@digital-twins/shared-types'
import {studyFindingsApi, sameStudyArtifactTarget} from '@/lib/api/study-findings'
import {ThreadStudyFindings} from './ThreadStudyFindings'
import {suggestionActionId} from './suggestionLaunch'
import { hasStudyFindingsSource, resolveNativeQuickOrigin, resolveStudyDraftOrigin, resolveStudyOrigin, useThreadStudyOrigin } from './useThreadStudyOrigin'
import { useStudyArtifactAttention } from '@/lib/hooks/use-study-artifact-attention'
import type { AssistantImageRequest } from '@digital-twins/shared-types/assistant'
import { ThreadImageApprovalGroup } from './ThreadImageApprovalGroup'
import { useImageApprovalBatches } from './useImageApprovalBatches'
import type { ImageApprovalGroup, ImageApprovalSaved } from './imageApprovalGroups'
import { imageApprovalCardGroups, imageApprovalSlots, isImageApprovalSet, newestImageTurns, quotedImageFormatId, withImageRequestStatus } from './imageApprovalGroups'
import { imageFormatCaption } from '@/utils/imageFormatCopy'
import { CreativePresetLine } from './CreativePresetLine'
import { useThreadCreativeContext } from './useThreadCreativeContext'
import type { AssistantTurnSnapshot } from '@digital-twins/shared-types/assistant'
import { studyCitationTarget } from './studyCitationTarget'
import {
  assistantTurnFailureText,
  createAssistantStreamFailure,
  nestRefusalMessage,
} from './assistantTurnError'
import type { AssistantIntakeRequest } from '@digital-twins/shared-types/assistant'
import { AssistantCalculationEvidence, AssistantEvidenceItem } from '@digital-twins/shared-types/assistant'
import { studySuggestionPreparation, roomSuggestionModalProps, type RoomSuggestionLaunch } from './suggestionLaunch'
import { ThreadConversationLaunchCard } from './ThreadConversationLaunchCard'
import type { ConversationLaunchPendingRoom } from './ThreadConversationLaunchCard'
import { preparedConversationSuggestion } from './conversationLaunch'
import { takeRoomsReadMark, useConversationLaunchRooms } from './useConversationLaunchRooms'
import { Button } from '@/components/ui/button'
import { useReducer } from 'react'
import { assistantSessionsApi } from '@/lib/api/client'
import { ComposeModal } from '@/pages/ConversationsPage/ComposeModal/ComposeModal'
import { RoomSession } from './RoomSession/RoomSession'
import { SuggestionSlot, moveSuggestionDismissals } from './SuggestionSlot/SuggestionSlot'
import { SessionPane } from './SessionPane/SessionPane'
import { StudyFramePane } from './SessionPane/StudyFramePane'
import { ThreadShareControl } from './ThreadShareControl/ThreadShareControl'
import { sessionReducer } from './SessionPane/sessionState'
import { isProgressRun, sessionSectionForArtifact } from './SessionPane/sessionSections'
import type { TurnSuggestions } from '@digital-twins/shared-types/assistant'
import type { PreparedConversation, RoomSuggestion, WorkflowRun } from '@digital-twins/shared-types/assistant'

import { useTranslation } from 'react-i18next'
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { ArrowDown, MoreHorizontal, PanelRight, RotateCcw } from '@digital-twins/design-system/icons'
import { Helmet } from 'react-helmet-async'
import { toast } from 'sonner'
import { mutate } from 'swr'
import { useThreadStudyDrafts } from '@/lib/hooks/use-thread-study-drafts'
import {ThreadStudyCard} from './ThreadStudyCard'
import {
  ASSISTANT_MESSAGE_MAX_LENGTH,
  AnalystChartSpec,
  AnalystTableSpec,
  AssistantContextUsage,
  AssistantCreativeDraft,
  AssistantEffort,
  KnowledgeAskCitation,
  KnowledgeDocumentListItem,
  SocialTrackerProposal,
  ThreadArtifactDto,
  WebCitation,
  WorkflowTemplate,
  isWorkflowRunTerminal,
} from '@digital-twins/shared-types'
import { ChatComposer } from '@/components/chat/ChatComposer'
import { ChatFileUpload } from '@/components/chat/ChatFileUpload'
import { AttachmentTiles } from '@/components/chat/AttachmentTiles'
import { useChatFileIngest } from '@/components/chat/useChatFileIngest'
import {
  hasStimulusStillHosting,
  isVideoChatImage,
  uploadHostedChatStimulus,
} from '@/components/chat/stimulus-videos'
import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import { takeHomeStimuli } from '@/lib/home-lab/pendingStimuli'
import { composerTriggerClassName } from '@/components/composer/composerAppearance'
import { ContextRing } from '@/components/composer/ContextRing'
import { AssistantProfilePicker } from '@/components/composer/AssistantProfilePicker'
import {
  AssistantReplyCost,
  useAssistantThreadCredits,
} from '@/components/composer/AssistantReplyCost'
import { useMeteringEnrolled } from '@/lib/billing/metering'
import { contextRingEffort, useAssistantProfile } from '@/components/composer/useAssistantProfile'
import { ScopeChip } from '@/components/composer/ScopeChip'
import { PageContent } from '@/components/layout/PageContent'
import {
  QuickStudyCutPlotsContext,
  quickStudyCutPlotsSourceFor,
} from '@/components/quick-study-results/quickStudyCutPlots'
import { PageLayout } from '@/components/layout/PageLayout'
import { upsertSidebarThread } from '@/components/layout/useSidebarThreads'
import {
  asAnalystCharts,
  asAnalystTables,
  ThreadFeaturedExhibits,
} from '@/components/quick-study-results/featuredPlots/ThreadFeaturedExhibits'
import { hasStudyDocEmbeds } from '@/components/study-doc'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/config/auth-provider'
import { cn } from '@/lib/utils'
import { getThreadPlaceholders, resolveThreadLanguage } from '@/lib/constants/languages'
import {
  assistantTurnStream,
  assistantTurnsApi,
  observeAssistantTurnSnapshots,
  normalizeAssistantTurnFrame,
  isAssistantTurnActive,
  AssistantTurnDoneFrame,
  ChatImage,
  knowledgeApi,
  knowledgeThreadsApi,
  KnowledgeThreadMessage,
  KnowledgeThreadView,
  AssistantTurnPlanTail,
  threadAssetsApi,
  workflowRunsApi,
  ThreadAssetView,
} from '@/lib/api'
import { defaultContentLanguageFromUi } from '@/i18n'
import Link from '@/routing/Link'
import { ROUTES, THREAD_STUDY_PARAM, studyEmbedDraftPath, studyEmbedResultsPath } from '@/routes/routes'
import { useWorkflowRun } from '@/lib/workflows/useWorkflowRun'
import {
  connectWebSocket,
  onKnowledgeThreadMessageAdded,
  subscribeToKnowledgeThread,
  unsubscribeFromKnowledgeThread,
} from '@/lib/services/websocket'
import { ActiveWorkflowChip } from '@/pages/HomeLabPage/ActiveWorkflowChip'
import { cleanSegmentLabel } from '@/components/quick-study-results/respondentNoun'
import { QuickStudyNounProvider } from '@/components/quick-study-results/quickStudyNoun'
import { useStartWorkflowRun } from '@/pages/WorkflowsPage/useStartWorkflowRun'
import { useWorkflowScheduleAudiences } from '@/pages/WorkflowsPage/useWorkflowScheduleAudiences'
import { workflowRunSegmentFieldsForSlug } from '@/pages/WorkflowsPage/workflowRunSegmentFields'
import { useWorkflowTemplate } from '@/pages/WorkflowsPage/useWorkflowTemplate'
import { useWorkflowTemplates } from '@/pages/WorkflowsPage/useWorkflowTemplates'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useRecordRecentOpen } from '@/hooks/useRecordRecentOpen'
import {
  KnowledgeScopePicker,
  KnowledgeScopeSelection,
} from '../ThreadsPage/KnowledgeScopePicker'
import { continueThreadScope } from './continueThreadScope'
import { scopeChipLabel } from '../ThreadsPage/threadHelpers'
import { documentTitle } from '../KnowledgeHubPage/libraryHelpers'
import { ThreadRunGate } from './ThreadRunGate'
import { ThreadRunActivityIndicator } from './ThreadRunActivityIndicator'
import { settledStepRefreshSignature } from './deriveThreadRunActivityLabel'
import { cutGroupsFromRun, ThreadRunResults } from './ThreadRunResults'
import { artifactDisplayLabel } from './ThreadRunPane/artifactDisplayLabel'
import {
  GateRevisionHandle,
  pendingGateDocksOnComposer,
} from './ThreadRunPane/gateArtifacts'
import {
  collectThreadArtifacts,
  countPendingCreativeDrafts,
  enrichStimulusMedia,
  filterFoldedStimulusItems,
  foldAssetTokensFromCreativeDtos,
  isLivingArtifactMutationTool,
  STUDY_DOC_SLUG,
  ThreadArtifactItem,
} from './ThreadRunPane/collectThreadArtifacts'
import { useThreadArtifactTips } from './ThreadRunPane/useThreadArtifactTips'
import { useThreadLivingArtifacts } from './ThreadRunPane/useThreadLivingArtifacts'
import { ThreadInlineArtifacts } from './ThreadRunPane/ArtifactPreviewCard'
import { ThreadArtifactExpandedView } from './ThreadRunPane/ThreadArtifactExpandedView'
import { ThreadArtifactFullscreenView } from './ThreadRunPane/ThreadArtifactFullscreenView'
import { ThreadArtifactCardList, ThreadArtifactPane } from './ThreadRunPane/ThreadArtifactPane'
import { ThreadAssetTiles } from './ThreadRunPane/ThreadAssetTiles'
import { ThreadRunPane } from './ThreadRunPane/ThreadRunPane'
import { ThreadCalculationSources } from './ThreadCalculationSources'
import {
  segmentChipsFromFilter,
  segmentLabelFromChips,
} from './ThreadRunPane/runSegmentFilter'
import { ThreadCommandMenu, ThreadCommandSeed } from './ThreadCommandMenu'
import {
  hasStimulusGateSatisfied,
  hideFirstOptionalTextOnCard,
} from './ThreadCommandInputs'
import { hasBoundStimulusValue } from './ThreadStimulusInput'
import { buildPlanWorkflowCommandSeed } from './buildPlanWorkflowCommandSeed'
import { singleStepWorkflowSlug } from './singleStepWorkflowSlug'
import { audienceNameForInputs } from './audienceNameForInputs'
import {
  buildIntakeStartLatchKey,
  loadArmedWorkflowState,
  useArmedWorkflowState,
} from './useArmedWorkflowState'
import { audienceInputKey, composeRunStudyDoc } from './composeRunStudyDoc'
import {
  composeThreadExhibits,
  useThreadExhibitRuns,
} from './composeThreadExhibits'
import { useRunResults } from './useRunResults'
import { sourcePreviewMessageToken, useSourcePreviews } from './useSourcePreviews'
import { withSourcePreviews } from './sourcePreviews'
import { useRunTemplate } from './useRunTemplate'
import { studyPinAfterIndex } from './studyPinAnchor'
import {
  collectWorkflowRunStepNotes,
  currentAttemptWorkflowRunStepNotes,
} from './workflowRunStepNotes'
import {
  isPlainCreativeConfirm,
  isPlainGateContinue,
  isPlainGo,
  resolveCreativeDraftConfirmTarget,
  resolveGoTarget,
} from './threadGoIntent'
import { readOnlyThreadNotice } from './threadReadOnlyNotice'
import { CANONICAL_QUICK_STUDY_COMMAND_SLUG, matchesTypedCommand, ThreadCommand, useThreadCommands } from './useThreadCommands'
import {
  lastUserTurnId,
  originatingCommandUserTurnId,
  resolveWorkflowRunStartAt,
  userTurnAudienceChipLabel,
  userTurnCommandChipLabel,
} from './userTurnCommandChip'
import {
  findExpandableThreadArtifact,
  findThreadArtifactBySearchKey,
  readThreadArtifactKey,
  writeThreadArtifactSearchParam,
} from './threadArtifactSearch'
import { ThreadAnswerBody } from './ThreadAnswerBody'
import { ThreadQuickStudyFindingsMessage } from './ThreadQuickStudyFindingsMessage'
import { messageToTurn } from './messageToTurn'
import { adoptPersistedMessage } from './adoptPersistedMessage'
import {
  ThreadCreativeDraftSet,
  toPreviewItem,
} from './ThreadCreativeDraftSet'
import { ThreadSocialTrackerCard } from './ThreadSocialTrackerCard'
import { ThreadReportCard, ThreadReportLink } from './ThreadReportCard'
import { ThreadSocialTopicLink } from './ThreadSocialTopicLink'
import { ThreadIntakeStartActions } from './ThreadIntakeStartActions'
import { ThreadPlanCard } from './ThreadPlanCard'
import {
  stepHandoffFrom,
  useThreadPlanExecution,
} from './useThreadPlanExecution'
import {
  ThreadPlanProposal,
  ThreadPlanStepHandoff,
  ThreadPlanStepProposal,
} from './threadPlan.types'
import {
  ThreadActivityIndicator,
  ThreadActivityLeg,
} from './ThreadActivityIndicator'
import {
  applyActivityFrame,
} from './applyActivityFrame'
import { ThreadActivityTrail } from './ThreadActivitySteps'
import { GeneratingImageTile } from './GeneratingImageTile/GeneratingImageTile'
import {
  applyImageProgressFrame,
  ImageProgressByCallId,
  stopPendingImages,
  visibleImageTiles,
} from './GeneratingImageTile/imageProgressState'
import { ActivityPageData } from './resolveActivityObject'
import { ThreadSourcePanel, ThreadSourceTab } from './ThreadSourcePanel'
import {
  DRAWER_TRANSITION_MS,
  ThreadSourcesDrawer,
} from './ThreadSourcesDrawer'
import { isCitationUuid } from './injectCitationMarkers'
import { partitionAssistantCitations } from './partitionAssistantCitations'
import { CalculationFocus, calculationsFor } from './calculationsTurn'
import { aggregateThreadSources } from './threadSources'
import { ThreadSourcesSection } from './SessionPane/ThreadSourcesSection'
import type { PartitionedEvidenceV2 } from './partitionAssistantCitations'
import {
  isWorkflowRunAskId,
  mergeRunCompletionAnnouncements,
} from './mergeRunCompletionAnnouncements'
import {
  isWorkflowRunCompletionAskId,
  WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX,
} from './workflowRunCompletion'
import { withRunCommandChip } from './withRunCommandChip'
import { workflowRunAskTurnAskId } from './workflowRunAskTurnAskId'

interface ThreadLocationState {
  pendingMessage?: string
  /* An off-thread composer handed over an armed intake with nothing typed: open
     its scoping beat. Distinct from an arm made on this page, which waits. */
  pendingIntakeOpen?: boolean
  /* Home Lab's Ask composer carries its effort pick across the hand-off the same
     optional, omit-when-absent way it carries `pendingMessage` (see
     `useHomeLabSubmit.ts`'s own `pendingEffort` comment). Frontend review SHOULD FIX 2:
     this key existed on the write side already; this interface and the effect below are
     the read side that was missing. */
  pendingEffort?: AssistantEffort
  /* Step A2 (plan 2026-10-21): Home Lab's Assistant profile pick, omitted when absent. The
     first turn runs under it even before this page's own profile list has loaded. */
  pendingProfile?: string
  /**
   * Lobby example-prompt publicId (R3.6). Already stamped on the thread row at
   * create; kept here so the hand-off stays inspectable without a second fetch.
   */
  sourcePromptId?: string
  scope?: KnowledgeScopeSelection
}

interface LocalTurn {
  id: string
  role: 'user' | 'assistant'
  text: string
  createdAt?: string
  /**
   * Command name stamped at send when this turn launched an armed workflow
   * (`/test` intake or a direct start). Survives later follow-ups in the
   * same session; reload reconstructs it from the run via
   * `userTurnCommandChipLabel`.
   */
  commandDisplayName?: string
  /**
   * The audience that command was bound to, stamped beside
   * `commandDisplayName` (plan 2026-09-14-twin-test-bound-audience, B4). A
   * SECOND chip, never a rename of the first. Reload reconstructs it from the
   * run through `withRunCommandChip`, which takes the name this page resolved
   * because the run readback carries the id alone.
   */
  commandAudienceName?: string
  streaming?: boolean
  refused?: boolean
  stopped?: boolean
  gaps?: Array<string>
  /**
   * DOCUMENT citations only, already split out of the mixed
   * `KnowledgeAskCitation | AssistantCitation` array the server persists. Every
   * document renderer downstream (`groupCitationsByDocument`,
   * `injectCitationMarkers`, `ThreadAnswerBody`, `ThreadSourcePanel`) takes
   * this field unchanged, which is exactly why the web half is separated
   * before it ever reaches one of them.
   */
  citations?: Array<KnowledgeAskCitation>
  /**
   * The `kind: 'web'` half of the same array, disclosed as URL-keyed cards.
   * V2 inline anchors live separately in citationV2.
   */
  webCitations?: Array<WebCitation>
  citationV2?: PartitionedEvidenceV2
  /** P1: every calculation the answer computed, cited or not (done / reload). */
  calculations?: Array<AssistantCalculationEvidence>
  /** Offered library/social/web sources the answer read but did not cite (done / reload). */
  furtherReading?: Array<AssistantEvidenceItem>
  /**
   * `judgment` frame notes for this turn, in arrival order. One quiet note
   * under the answer marking where the model went past its sources.
   */
  judgmentNotes?: Array<string>
  /**
   * Retrieval legs this turn announced, in `tool_start` order, each flipped to
   * `done` by its own `tool_end`.
   *
   * Read only while the turn is still waiting for its first token: it is what
   * `ThreadActivityIndicator` says out loud instead of guessing from a clock.
   * Absent is a real state and the reason this is optional: the legacy grounded
   * ask below emits no tool frames at all, and an intake turn plans no legs.
   */
  activityLegs?: Array<ThreadActivityLeg>
  /**
   * Live `generate_image` calls of this turn, folded from `image_progress`
   * frames by call id (image plan Step 4b). Ordinary turns only, never
   * persisted: after a reload the asset shows through the rail and the
   * answer's `asset:` ref.
   */
  imageProgress?: ImageProgressByCallId
  /**
   * Agent C: validated plan proposal from the done-tail / message reload.
   * Only cardEligible (≥2 steps) plans are attached; the card mounts from
   * `useThreadPlanExecution` after `registerProposal`.
   */
  plan?: AssistantTurnPlanTail
  /**
   * Hosted composer attachments shown on the user bubble. Live turns copy
   * send-time tiles; reload joins GET-assets via `messageToken` (`id` is the
   * message token on rehydrate).
   */
  attachments?: Array<{ src: string; mimeType?: string; label?: string }>
  /** Persisted ask id; used to tag the durable run-completion announcement. */
  askId?: string | null
  /**
   * The plan card this turn was produced FOR, when the turn is a plan step's
   * own answer. Local only: it says which plan a bare "go" is still answering
   * once the step's prose is the newest thing on screen.
   */
  planMessageId?: string
  /**
   * The server token for a turn that was sent from this page, learned from the
   * `done` frame. A locally sent turn keeps its `newId()` id as its identity
   * for the whole session, because the plan cursor, the streaming updates and
   * the park effect all key off that id and renaming it mid-flight would
   * detach a plan card from its own message. This is the SECOND name for the
   * same turn, carried in case a later reload needs to recognise it by token
   * rather than id.
   *
   * Today's only consumer, `mergeRunCompletionAnnouncements`, first filters
   * incoming turns down to the durable run-completion announcement (its own
   * `askId`). That announcement is inserted directly by
   * `WorkflowRunCompletionListener`, an event-emitter callback that never runs
   * inside this page's own request/response cycle, so its message never
   * travels through a `done` frame this page receives: no locally sent turn
   * can ever carry a `serverToken` equal to one. The token match there is
   * defensive plumbing, kept for a future consumer of that merge that appends
   * non-announcement messages by token instead of id.
   *
   * Absent on a turn that arrived from the server (its `id` IS the token) and
   * on a turn whose `done` frame carried no tokens, an older backend.
   */
  serverToken?: string
  /** Featured analyst specs; rendered under assistant prose when present. */
  quickStudyFindings?: KnowledgeThreadMessage['quickStudyFindings']
  charts?: Array<AnalystChartSpec>
  tables?: Array<AnalystTableSpec>
  /**
   * Unconfirmed creative set from the done tail / message reload.
   * Confirm writes living rows from these bytes; the model is not on that path.
   */
  creativeDrafts?: Array<AssistantCreativeDraft>
  /** Tracker the agent suggested; the card creates it only on the client's click. */
  socialTrackerProposal?: SocialTrackerProposal
  /** Conversation the agent prepared; the card starts it only on the client's click. */
  preparedConversation?: PreparedConversation
  imageRequests?: AssistantImageRequest[]
  imageApprovalRequestId?: string
  imageApprovalFormatId?: string
  /** A12: the saved report this turn announced; renders the inline card. */
  report?: ThreadReportLink
  /** A12: the agreed brief the report was written from (never a bubble). */
  reportBrief?: string
}

/** A12: the report link a done frame carries, when Nest saved one. */
function reportLinkFromDone(done: AssistantTurnDoneFrame): ThreadReportLink | undefined {
  const publicId = done.report?.publicId?.trim()
  if (!publicId) {
    return undefined
  }
  return { publicId, title: done.report?.title ?? '' }
}

/** The saved ordinary answer has the same shape whether intake handed it off or not. */
function completedAssistantTurn(turn: LocalTurn, done: AssistantTurnDoneFrame, partial = '', notes: Array<string> = []): LocalTurn {
  const answer = done.text.trim() ? done.text : partial
  const citations = partitionAssistantCitations(done.citations, done)
  const plan = done.plan?.cardEligible ? done.plan : undefined
  /* A12: a saved report is content too (its card renders under the turn), so an
     empty hand-off text never reads as "No answer found". */
  const structured = Boolean(done.imageRequests?.length || done.creativeDrafts?.length || done.socialTrackerProposal || done.preparedConversation || plan || done.charts?.length || done.tables?.length || reportLinkFromDone(done))
  return {
    ...turn,
    text: answer.trim() || structured ? answer : 'No answer found in your sources.',
    streaming: false,
    refused: done.refused && !structured,
    gaps: done.gaps,
    citations: citations.documentCitations,
    webCitations: citations.webCitations,
    citationV2: citations.v2,
    calculations: done.calculations?.length ? done.calculations : undefined,
    furtherReading: done.further_reading?.length ? done.further_reading : undefined,
    judgmentNotes: notes.length ? notes : undefined,
    plan,
    charts: asAnalystCharts(done.charts),
    tables: asAnalystTables(done.tables),
    ...(done.creativeDrafts?.length ? { creativeDrafts: done.creativeDrafts } : {}),
    ...(done.socialTrackerProposal ? { socialTrackerProposal: done.socialTrackerProposal } : {}),
    ...(done.preparedConversation ? { preparedConversation: done.preparedConversation } : {}),
    ...(done.assistantMessageToken ? { serverToken: done.assistantMessageToken } : {}),
    imageRequests: done.imageRequests ?? undefined,
    imageProgress: stopPendingImages(turn.imageProgress),
    ...(reportLinkFromDone(done) ? { report: reportLinkFromDone(done) } : {}),
  }
}

/** Agent C: map a card-eligible done-tail / message plan into D's proposal shape. */
function planTailToProposal(
  messageId: string,
  plan: AssistantTurnPlanTail,
): ThreadPlanProposal | null {
  if (!plan.cardEligible || plan.steps.length < 2) {
    return null
  }
  return {
    messageId,
    steps: plan.steps.map((step) => {
      const mapped: ThreadPlanStepProposal = {
        slug: step.slug,
        kind: step.kind,
        displayName: step.displayName,
        flags: step.flags,
      }
      if (step.title?.trim()) {
        mapped.title = step.title.trim()
      }
      const audience = step.inputs?.audience?.trim()
      if (audience) {
        mapped.inputs = { audience }
      }
      return mapped
    }),
  }
}

/**
 * Room left above the answer when a question is scrolled to the top of the
 * conversation: enough for the question bubble itself plus breathing space.
 */
const QUESTION_HEADROOM_PX = 140

/** Stable empty list, so a turn with no inline images keeps its tiles. */
const NO_PLACED_ASSET_TOKENS: ReadonlyArray<string> = []
/**
 * How long after parking the newest question a scroller width change still
 * re-parks it. Long enough to cover the run pane opening on a command send
 * (same tick to a few hundred ms); short enough that a later resize is the
 * reader's own business.
 */
const REPARK_AFTER_PARK_MS = 1500

/**
 * The thread body spans the whole pane in every state: loading, loaded, rail
 * open, rail closed. The conversation scroller fills it so its scrollbar sits
 * on the pane's edge, never at the edge of a centered box; the side gutter is
 * padding on the scroller itself, which keeps the bar outside it. The rail
 * animates inside this row, and the reading column below holds the measure.
 */
const THREAD_GUTTER_CLASS = 'px-4 md:px-8'
/** How long a launched study's success page shows before the pane goes back to Studies. */
const STUDY_LAUNCH_HANDOFF_MS = 1200
/** How long the new study's row stays highlighted in the Studies list. */
const STUDY_LAUNCH_HIGHLIGHT_MS = 2400
/** Reading measure. Constant too: the rail shifts the column, never narrows it. */
const THREAD_COLUMN_CLASS = 'max-w-3xl'
/**
 * The page is a column: a full-bleed header over a measured body. The header
 * spans the viewport rather than the body width, so the title and the scope
 * chip sit at the page edges while the conversation stays on its measure.
 */
const THREAD_PAGE_SHELL_CLASS = 'flex h-full w-full flex-col gap-0'
/**
 * The header is a fixed 56px band, not padding around variable content: its
 * centre line has to land on the sidebar toggle glyph (rail `py-2` + a 40px
 * button = a centre at 28px), and `py-3` around a 26px chip put the row 3px
 * high.
 */
const THREAD_HEADER_CLASS =
  'pointer-events-none relative z-30 flex h-14 w-full shrink-0 items-center justify-between gap-3 px-4 md:px-6 [&_input]:pointer-events-auto [&_button]:pointer-events-auto'
// min-w-0: a flex item's floor is its min-content width, so a wide thread
// block would otherwise grow this row and push the fixed-width rail past the
// window edge.
const THREAD_BODY_ROW_CLASS =
  'flex min-h-0 min-w-0 w-full flex-1 flex-row gap-4 pb-2 md:gap-6 md:pb-4'
/**
 * The row under the header is full-bleed so the sources pane can sit against
 * the right edge of the viewport, the way the Quick Study composition drawer
 * does. The reading column inside it holds the measure (`max-w-3xl`); a pane
 * docked inside a narrower box reads as a floating card, not as part of the window.
 */
const THREAD_PAGE_ROW_CLASS = 'flex min-h-0 min-w-0 w-full flex-1 flex-row overflow-x-clip'

/**
 * How long the drawer takes to animate shut. Owned by ThreadSourcesDrawer,
 * which sets both the clip and the slide: the content stays mounted for exactly
 * this long on close, so the drawer clips real content on the way out instead
 * of animating an empty gap.
 */
const RAIL_TRANSITION_MS = DRAWER_TRANSITION_MS
const RAIL_OPEN_STORAGE_KEY = 'thread-detail:sources-rail-open'

function ThreadDetailLoadingShell() {
  return (
    <div
      className="flex min-w-0 flex-1 flex-col"
      data-testid="thread-detail-loading"
    >
      <div
        className={cn(
          'relative mx-auto flex min-h-0 w-full flex-1 flex-col',
          THREAD_COLUMN_CLASS,
        )}
        data-testid="thread-reading-column"
      >
        <Skeleton className="min-h-0 flex-1 rounded-2xl" />
        <div className="absolute inset-x-0 bottom-0 pt-8">
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  )
}

/**
 * Rail preference, per thread: a new thread starts with the pane closed and
 * each thread reopens the way it was left. Every localStorage touch is guarded: `window.localStorage`
 * is undefined in this repo's jsdom test env and throws in private browsing.
 * Pattern copied from `quickStudyPure.ts` readPersistedCompositionPaneWidth.
 */
function railOpenStorageKey(threadToken: string): string {
  return `${RAIL_OPEN_STORAGE_KEY}:${threadToken}`
}

function readPersistedRailOpen(threadToken: string): boolean {
  try {
    return localStorage.getItem(railOpenStorageKey(threadToken)) === 'true'
  } catch {
    return false
  }
}

function persistRailOpen(threadToken: string, open: boolean): void {
  try {
    localStorage.setItem(railOpenStorageKey(threadToken), String(open))
  } catch {
    /* private browsing / no storage */
  }
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * The key of the input a settled intake direction fills: the template's FIRST
 * `text` input, which is the same one the composer already borrows its
 * placeholder from and the same one `useStartWorkflowRun` would have mapped a
 * message onto.
 *
 * Derived from the template rather than spelled `'ask'` here, so this page
 * carries no template's vocabulary. Undefined for a template that declares no
 * text input, and the direction is simply not sent.
 */
function askInputKey(template: WorkflowTemplate): string | undefined {
  return template.inputs.find((spec) => spec.type === 'text')?.key
}

/**
 * The reader's own words for a just-armed prepared run, when the armed
 * template turns out to be the composer-owned-optional-ask kind (D3,
 * twin-test-question-shape-and-no-command-path plan, Step 4). `pending` is
 * `pendingPreparedReaderAskRef`'s snapshot from the prepared-run seed site:
 * `null` for a typed `/test` arm and for the approved-plan "go ahead" path,
 * both of which keep reading the seeded `rest` value at the call site.
 *
 * Checked here rather than at the seed site because the template's shape
 * (`hideFirstOptionalTextOnCard`, the same check the confirm card uses to
 * hide that field) is only known once the confirm card has fetched the full
 * template — which is exactly the `template` `handleArmCommand` receives.
 */
function preparedReaderAsk(
  template: WorkflowTemplate,
  inputs: Record<string, string | null>,
  pending: { slug: string; message: string; rest: string } | null,
): string | null {
  if (!pending || pending.slug !== template.slug) {
    return null
  }
  /* The card being armed must be THAT prepared card: its hidden ask input
     still carries the `rest` the seed put there. A typed `/test` armed later
     for the same slug carries its own words (or nothing) and keeps them. */
  const seededRest = inputs[askInputKey(template) ?? ''] ?? ''
  if (seededRest !== pending.rest) {
    return null
  }
  return hideFirstOptionalTextOnCard(template.inputs) ? pending.message : null
}

function settledAudienceId(
  template: WorkflowTemplate | null | undefined,
  inputs: Record<string, string | null>,
): string | null {
  const key = template ? audienceInputKey(template) : 'audience'
  const raw = key ? inputs[key] : inputs.audience
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return null
  }
  return raw.trim()
}

/**
 * Stand-in audience for a report-kind intake, which has no audience input to
 * settle: report mode reads the thread, never a panel.
 *
 * The auto-start gate is a three-part tuple (thread, direction, audience) and
 * a report brief still needs its own latch, so this fills the third slot with
 * a value no audience id can collide with rather than making the gate itself
 * nullable.
 */
const REPORT_INTAKE_SETTLED_AUDIENCE = 'report'

/**
 * What a settled report intake hands the writing turn.
 *
 * An OBJECT, not a bare string, on purpose. The scoping tail carries the
 * agreed brief today (`intake.ask`), and a structured coverage list (topics,
 * evidence ids, cuts) is an open proposal against the same tail. Shaped this
 * way, that field would ride ALONGSIDE the brief rather than replace it, and
 * neither `confirmIntakeDirection` nor the turn itself has to be reshaped to
 * carry it. Nothing here assumes `ask` is the only payload the tail can have.
 */
interface SettledReportScope {
  /** `intake.ask` verbatim: the agreed brief, and the turn's message. */
  brief: string
}

/**
 * The SERVER's own sentence out of a failed report turn.
 *
 * `assistantTurnStream` throws with the raw response body, so Nest's D13
 * refusal ("Nothing to report yet…") arrives wrapped in its JSON envelope.
 * That copy is the whole point of the refusal, so it is unwrapped and shown
 * verbatim; anything that is not that 4xx envelope is the fallback copy.
 */
function reportTurnErrorMessage(error: unknown, fallback: string): string {
  const fromResponse = (
    error as { response?: { data?: { message?: unknown } } }
  )?.response?.data?.message
  if (typeof fromResponse === 'string' && fromResponse.trim()) {
    return fromResponse.trim()
  }
  /* Only Nest's own 4xx envelope is the reader's to see. A stream `error`
     frame, a 5xx body or any other throw is not: provider text must never
     reach the toast. */
  const refusal = error instanceof Error ? nestRefusalMessage(error) : null
  return refusal ?? fallback
}

const THREAD_ASSET_REGISTER_ATTEMPTS = 3
/** Ordinary turns reject empty `message`; attachment-only send uses this. */
const ATTACHMENT_ONLY_ASK = 'Please look at the attached.'

function hostableKindFromChatImage(
  image: ChatImage,
): 'image' | 'video' | null {
  if (isVideoChatImage(image)) {
    return 'video'
  }
  const mime = (image.mimeType ?? '').toLowerCase()
  if (mime.startsWith('image/')) {
    return 'image'
  }
  return null
}

/** The user message an image retry resends as a new turn, attachments included. */
interface ImageRetryRequest {
  text: string
  attachments: Array<{ src: string; mimeType?: string; label?: string }>
  attachmentTokens: Array<string>
}

function chatImagesToTurnAttachments(
  images: Array<ChatImage>,
): Array<{ src: string; mimeType?: string; label?: string }> {
  const items: Array<{ src: string; mimeType?: string; label?: string }> = []
  for (const image of images) {
    if (!image.url) {
      continue
    }
    items.push({
      src: image.url,
      mimeType: image.mimeType,
      label: image.label,
    })
  }
  return items
}

function listedAssetFileId(asset: ThreadAssetView): string | null {
  const fileId = asset.ref.fileId ?? asset.ref.id
  return fileId || null
}

function turnAttachmentsFromAssets(
  assets: Array<ThreadAssetView>,
  messageToken: string,
): Array<{ src: string; mimeType?: string; label?: string }> | undefined {
  const items: Array<{ src: string; mimeType?: string; label?: string }> = []
  for (const asset of assets) {
    if (asset.messageToken !== messageToken) {
      continue
    }
    const src = asset.ref.publicUrl
    if (!src) {
      continue
    }
    items.push({
      src,
      mimeType: asset.ref.mimeType,
      label: asset.ref.label,
    })
  }
  return items.length > 0 ? items : undefined
}

/**
 * Step whose settling turned `previous` into `next` (`settledStepRefreshSignature`,
 * a `stepId:state` list). The LAST new entry, so a burst that settles two steps
 * scrolls to the one the reader is furthest from. Null when nothing was added.
 */
function newestSettledStepId(previous: string, next: string): string | null {
  const seen = new Set(previous.split('|'))
  let newest: string | null = null
  for (const entry of next.split('|')) {
    if (entry.length === 0 || seen.has(entry)) {
      continue
    }
    newest = entry.slice(0, entry.lastIndexOf(':'))
  }
  return newest && newest.length > 0 ? newest : null
}

/** The try an approval runs, on the first image; the rest of a set has none. */
function approvalTry(approval: { step?: 'first' | 'try' | 'rest'; tries?: number }): ImageApprovalSaved {
  const step = approval.step ? { step: approval.step } : {}
  if (approval.step === 'try') return { ...step, tryNumber: approval.tries }
  return approval.step === 'first' ? { ...step, tryNumber: 1 } : step
}

/** Drops the local ask and answer of an attempt that ended, when the same image starts again. */
function withoutEndedImageAttempt(turns: Array<LocalTurn>, requestId: string, formatId?: string): Array<LocalTurn> {
  const isSameImage = (turn: LocalTurn) => turn.imageApprovalRequestId === requestId && turn.imageApprovalFormatId === formatId
  const ended = new Set<string>()
  turns.forEach((turn, index) => {
    if (turn.role === 'assistant' && isSameImage(turn) && (turn.refused || turn.stopped)) {
      ended.add(turn.id)
      const ask = turns[index - 1]
      if (ask?.role === 'user' && isSameImage(ask)) ended.add(ask.id)
    }
  })
  return ended.size ? turns.filter((turn) => !ended.has(turn.id)) : turns
}

function turnMessageToken(turn: { id: string; serverToken?: string }): string {
  return turn.serverToken ?? turn.id
}

function stampCreativeDraftsFromConfirm(args: {
  drafts: Array<AssistantCreativeDraft>
  artifacts: Array<ThreadArtifactDto>
  creativeDrafts?: Array<AssistantCreativeDraft>
  confirmedAt: string
}): Array<AssistantCreativeDraft> {
  const { drafts, artifacts, creativeDrafts, confirmedAt } = args
  if (creativeDrafts && creativeDrafts.length > 0) {
    return creativeDrafts
  }
  const remaining = artifacts.slice()
  return drafts.map((draft) => {
    const matchIndex = remaining.findIndex((artifact) => {
      if (draft.publicId && artifact.publicId === draft.publicId) {
        return true
      }
      if (artifact.body && artifact.body === draft.body) {
        return true
      }
      if (
        artifact.title === draft.title ||
        (draft.title.length > 0 && artifact.title.includes(draft.title))
      ) {
        return true
      }
      return false
    })
    if (matchIndex < 0) {
      return {
        ...draft,
        confirmedAt,
        publicId: draft.publicId ?? null,
      }
    }
    const match = remaining.splice(matchIndex, 1)[0]
    return {
      ...draft,
      confirmedAt,
      publicId: match?.publicId ?? draft.publicId ?? null,
    }
  })
}

function isConflictStatus(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  return (error as { response?: { status?: number } }).response?.status === 409
}

function confirmErrorMessage(error: unknown): string {
  const fromResponse = (
    error as { response?: { data?: { message?: string } } }
  )?.response?.data?.message
  if (typeof fromResponse === 'string' && fromResponse.trim()) {
    return fromResponse
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'That save failed'
}

export default function ThreadDetailPage() {
  const { t } = useTranslation('app')
  const { user: authUser } = useAuth()
  const { token = '' } = useParams<{ token: string }>()
  useRecordRecentOpen('thread', token)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const studyAttention = useStudyArtifactAttention(token || undefined)
  const findingsAttention = useMemo(() => studyAttention.items.flatMap((receipt) => isStudyArtifactAttentionTarget(receipt.target) ? [{...receipt, target: receipt.target}] : []), [studyAttention.items])
  const threadStudies = useThreadStudyDrafts(token || undefined)
  const [asking, setAsking] = useState(false)
  /* Credit metering (enrolled `on` only): per reply cost and the conversation's running total. */
  const isMeteringEnrolled = useMeteringEnrolled()
  const nativeQuickStudies = useThreadQuickStudies(token || undefined, asking)
  const [postingQuickRun, setPostingQuickRun] = useState<string | null>(null)
  const [postedQuickRuns, setPostedQuickRuns] = useState<Set<string>>(new Set())
  const requestedNativeQuick = searchParams.get('quickStudy')
  const requestedNativeRun = searchParams.get('quickRun') ?? undefined
  const requestedNativeRevisionRaw = searchParams.get('quickRevision')
  const requestedNativeRevision = requestedNativeRevisionRaw === null ? undefined : Number(requestedNativeRevisionRaw)
  const selectedNativeQuick = useThreadQuickStudyState(token, requestedNativeQuick ?? undefined, requestedNativeRun, requestedNativeRevision)

  const [quickFindingsTarget, setQuickFindingsTarget] = useState<StudyArtifactAttentionTarget | null>(null)
  const [confirmedFindingsTarget, setConfirmedFindingsTarget] = useState<StudyArtifactAttentionTarget | null>(null)
  const [postingFindings, setPostingFindings] = useState<string | null>(null)
  const postingFindingsRef = useRef<string | null>(null)
  const preparedOriginThreadRef = useRef(token)
  preparedOriginThreadRef.current = token
  const questionAttentionInFlight = useRef(new Set<string>())
  const requestedStudyOrigin = searchParams.get('originMessage') ?? ''
  const requestedStudyToken = searchParams.get('study') ?? ''
  const requestedStudyDraftToken = searchParams.get('studyDraft') ?? ''
  const requestedDraftRevisionRaw = searchParams.get('draftRevision')
  const requestedDraftRevision = requestedDraftRevisionRaw !== null && Number.isInteger(Number(requestedDraftRevisionRaw)) && Number(requestedDraftRevisionRaw) > 0 ? Number(requestedDraftRevisionRaw) : undefined
  const requestedAttention = searchParams.get('attention') ?? ''
  useEffect(() => {
    if (!requestedAttention) return
    const receipt = findingsAttention.find((item) => item.attentionToken === requestedAttention &&
      item.target.threadToken === token && item.target.studyToken === requestedStudyToken && item.target.originMessageToken === requestedStudyOrigin)
    if (receipt && isStudyArtifactAttentionTarget(receipt.target)) setQuickFindingsTarget((current) => current && sameStudyArtifactTarget(current, receipt.target) ? current : receipt.target)
  }, [requestedAttention, requestedStudyToken, requestedStudyOrigin, token, findingsAttention])
  const locationState = (location.state || {}) as ThreadLocationState
  /* Frontend review BLOCKER 1: same hook, same shape as every other new Assistant
     Platform v1 surface, so the thread composer's effort toggle and workflow door
     agree with the server-enforced flag rather than rendering unconditionally. */
  const assistantPlatformEnabled = useAssistantPlatformEnabled()

  // --- Agent C + D seam: plan proposal card lifecycle (D owns execution) ---
  const {
    plansByMessageId,
    registerProposal,
    confirmSoundsGood,
    dismiss: dismissPlan,
    skipCurrent,
    retryCurrent,
    markCurrentRunning,
    markCurrentDone,
    markCurrentFailed,
    refreshAfterTurn,
    prepareStepExecution,
  } = useThreadPlanExecution(token)
  /**
   * Which proposing message's workflow Start last opened ThreadCommandSeed.
   * Used to mark that step running/done/failed when the confirm path arms or
   * starts a run. Cleared when the run reaches a terminal status.
   */
  const planRunMessageIdRef = useRef<string | null>(null)
  /**
   * A prepared run's reader message, held for `handleArmCommand` (D3, Step 4
   * of the twin-test-question-shape plan). The seed itself still carries
   * `preparedSeed.rest` unchanged: the template's shape (whether the first
   * text input is the composer-owned optional ask, `hideFirstOptionalTextOnCard`)
   * is only known once the confirm card has fetched it, which is exactly
   * when `handleArmCommand` receives the full `template`. Set only for a
   * real ask (`planMessageId` null); cleared for the approved-plan "go
   * ahead" path so that mirror keeps reading `rest` as it always has.
   */
  const pendingPreparedReaderAskRef = useRef<{
    slug: string
    message: string
    rest: string
  } | null>(null)
  const askingRef = useRef(false)
  const pendingCreativeAutosavesRef = useRef(
    new Map<string, Map<string, Promise<void>>>(),
  )
  const stoppedCreativeAutosaveRef = useRef(new Set<string>())
  const [confirmingDraftMessageToken, setConfirmingDraftMessageToken] =
    useState<string | null>(null)
  const [savedDraftMessageTokens, setSavedDraftMessageTokens] = useState<
    Record<string, boolean>
  >({})
  // --- end Agent C + D seam ---

  const [thread, setThread] = useState<KnowledgeThreadView | null>(null)

  /* The thread's own prose, in the language its answers are written in (D3 and D4). The
     language is carried BY THE THREAD, chosen once when it was created and persisted, so
     nothing here holds language state of its own and a follow-up can never drift out of
     the language the thread has been answering in. Null falls back to English. */
  const threadCopy = getThreadPlaceholders(thread?.language)
  /* A thread minted by a workflow is readable by the whole organization but
     writable only by its owner (Step 1 / D0), so this page can be looking at
     somebody else's thread. Explicit `=== false`: while the thread record is
     still loading, and on a response written before the flag existed, the
     composer stays as it was rather than flashing a read-only notice at the
     owner of their own thread. */
  const isReadOnlyForViewer = thread?.isOwner === false
  const creativeContext = useThreadCreativeContext(token, Boolean(token && assistantPlatformEnabled && thread?.isOwner !== false))
  const isThreadShared = thread?.isShared === true
  const [isForking, setIsForking] = useState(false)
  const [turns, setTurns] = useState<Array<LocalTurn>>([])
  const threadCredits = useAssistantThreadCredits(
    token,
    isMeteringEnrolled && assistantPlatformEnabled,
    `${turns.length}:${asking}`,
  )
  const [loading, setLoading] = useState(true)
  /* Web source thumbnails load after the answer, once per message. */
  const sourcePreviews = useSourcePreviews(token, turns)
  const [suggestions, setSuggestions] = useState<TurnSuggestions["suggestions"]>([])
  const [suggestionTurnKey, setSuggestionTurnKey] = useState('')
  const [answerStillRevealing, setAnswerStillRevealing] = useState(false)
  /* The live turn's current dismissal key, read by the stream loop at `done`
     (its closure would otherwise see the key from before `beginTurn`). */
  const suggestionTurnKeyRef = useRef('')
  const [sessionState, dispatchSession] = useReducer(sessionReducer, { selection: { kind: 'runs' }, roomTokens: [] })
  const sessionSelectionRef = useRef(sessionState.selection)
  sessionSelectionRef.current = sessionState.selection
  // Opening a card's room re-reads the list too: the room may have been deleted since it loaded.
  const [launchRoomsReads, setLaunchRoomsReads] = useState(0)
  const launchRooms = useConversationLaunchRooms(
    token,
    !isReadOnlyForViewer && turns.some((turn) => Boolean(turn.preparedConversation)),
    `${sessionState.roomTokens.join(',')}|${launchRoomsReads}`,
  )
  const [reviewedLaunchRooms, setReviewedLaunchRooms] = useState<Record<string, ConversationLaunchPendingRoom>>({})
  const requestStudyCloseRef = useRef<(() => void) | null>(null)
  // False until the reader (or a citation, an artifact) picks a drawer tab on this thread.
  const [sessionTabChosen, setSessionTabChosen] = useState(false)
  const [roomSuggestion, setRoomSuggestion] = useState<{ suggestion: RoomSuggestion; personaTokens: Array<string>; reason: string; launch?: RoomSuggestionLaunch } | null>(null)
  const [pickingTwins, setPickingTwins] = useState(false)
  const preparingStudyRef = useRef(false)
  const [roomOpeningMessages, setRoomOpeningMessages] = useState<Record<string, string>>({})

  const [titleDraft, setTitleDraft] = useState('')
  const [documents, setDocuments] = useState<Array<KnowledgeDocumentListItem>>(
    [],
  )
  const [scope, setScope] = useState<KnowledgeScopeSelection>({
    web: true,
    library: true,
    mode: 'all',
    documentTokens: [],
    collectionTokens: [],
  })
  const [pickerOpen, setPickerOpen] = useState(false)
  const [railOpen, setRailOpen] = useState<boolean>(() => readPersistedRailOpen(token))
  // The page stays mounted across threads; switching threads restores that
  // thread's own rail state (closed for a thread never opened).
  const railThreadRef = useRef(token)
  useEffect(() => {
    if (railThreadRef.current === token) return
    railThreadRef.current = token
    setRailOpen(readPersistedRailOpen(token))
  }, [token])
  // P1: the figure chip the reader followed, so the drawer shows that
  // answer's own calculation rather than the newest answer's.
  const [calculationFocus, setCalculationFocus] = useState<CalculationFocus | null>(null)
  const [railMounted, setRailMounted] = useState<boolean>(railOpen)
  // Mounted vs. open-on-screen. A rail restored from storage is both on the
  // first paint — there is nothing to animate into on a page that is still
  // loading — so this starts where `railOpen` does.
  const [railSlidIn, setRailSlidIn] = useState<boolean>(railOpen)
  const [sourceTabs, setSourceTabs] = useState<Array<ThreadSourceTab>>([])
  const [activeSourceKey, setActiveSourceKey] = useState<string | null>(null)
  /* Which artifact, if any, has taken over the drawer in place of the Outputs
     list — set by an `ArtifactPreviewCard`'s expand affordance, wherever it is
     clicked (the inline stack under the conversation or the rail itself). Key
     rather than the item: a run refresh can reflow `runArtifacts`, and a
     stale key should fall back to the list rather than pin stale content, the
     same way `sourceTabs` never freezes citation data. */
  const [expandedArtifactKey, setExpandedArtifactKey] = useState<
    string | null
  >(null)
  /* Full-viewport Expand (ChatGPT Maximize). Separate from the rail pane
     takeover so the pane can stay open while the reader goes fullscreen. */
  const [fullscreenArtifactKey, setFullscreenArtifactKey] = useState<
    string | null
  >(null)
  /* Session latch: the user turn that owned the latest run when it first
     appeared. Follow-ups must not steal the study pin if timestamps are
     missing or all equal. */
  const [latchedStudyAsk, setLatchedStudyAsk] = useState<{
    runToken: string
    turnId: string
  } | null>(null)
  /* Per-turn reasoning effort (`EffortToggle` with `appearance="external"`).
     Local to the thread session: it is a knob on the next question, not a
     property of the thread, so it is not persisted and not sent to the server
     until Step 7.1 wires the turn param. */
  const [effort, setEffort] = useState<AssistantEffort>('medium')
  /* Step A2 (plan 2026-10-21): the Assistant profile picker replaces the effort toggle here;
     a profile decides the effort, so a turn sends one or the other. The selection is shared
     with Home Lab and survives reload (`useAssistantProfile`). Read through a ref at send
     time so a hand-off's `pendingProfile` reaches the first turn fired in the same effect. */
  const assistantProfile = useAssistantProfile(assistantPlatformEnabled)
  const turnProfileRef = useRef<string | null>(assistantProfile.profileId)
  turnProfileRef.current = assistantProfile.profileId ?? turnProfileRef.current
  /* What the context ring shows, taken from the LAST completed assistant turn's
     `done` frame and from nowhere else: `contextUsage` is not persisted on the
     message row, so after a reload this is legitimately null until the session's
     first turn finishes and the ring renders nothing at all. Persisting it is a
     schema change and out of scope for v1. `historyTruncated` is named for what
     the wire field `truncated` actually means — history dropped oldest-first to
     fit the ceiling — and says nothing about retrieval, which is truncated
     server-side and never reported. */
  const [contextUsage, setContextUsage] =
    useState<AssistantContextUsage | null>(null)
  const [historyTruncated, setHistoryTruncated] = useState(false)
  const [resultsSegmentBy, setResultsSegmentBy] = useState<string | null>(null)
  /* The composer's workflow entry (decision D3), the thread-side half of the
     same door `HomeLabStage` opens. Slug rather than the whole record, so the
     armed choice survives a template list refresh. Persisted in sessionStorage
     for this thread token so F5 / HMR does not silently disarm mid-intake.
     `armedTemplate` is the record the command menu handed over: `GET /workflows`
     hides every `thread-action-*` slug from the lobby list, so a command armed
     by slug alone would never resolve. Null when `WorkflowMenu` armed a lobby
     workflow, which the slug path resolves. `armedInputs` is what the confirm
     screen answered (`{ audience: <id> }` for `/test`). `intakeDirection` is
     the settled ask from the last intake `direction` tail — set, the page
     auto-starts once and latches so a failed start does not loop. */
  /* Remembered-audience scope: the current organization, not the browser
     tab (Decision 1, thread-audience-remember-and-avatar-fallback plan).
     `useCurrentOrg` resolves asynchronously, so `useArmedWorkflowState`
     re-hydrates `rememberedAudience` in its own effect once `org.id` lands
     and again on every later org switch. */
  const { org: currentOrg } = useCurrentOrg()
  const {
    slug: armedWorkflowSlug,
    template: armedTemplate,
    inputs: armedInputs,
    intakeDirection,
    audienceName: armedAudienceName,
    latchedStartKey,
    wasDropped: wasArmedWorkflowDropped,
    rememberedAudience,
    armSlug,
    clear: clearArmedWorkflow,
    armCommand,
    setIntakeDirection,
    latchIntakeStart,
    dismissDrop: dismissArmedWorkflowDrop,
    rememberAudience,
    forgetRememberedAudience,
    proceedWithoutStimulus: seededProceedWithoutStimulus,
  } = useArmedWorkflowState(token, currentOrg?.id ?? null)
  /* Lazy-init reads the D1 hand-off's seed on the SAME render the hook itself
     hydrates it from sessionStorage — a composer that already resolved "proceed
     without a stimulus" must not ask again once this page starts the armed run.
     Every later reset of this ref (disarm, run start, drop) still happens exactly
     where it always did; this only changes what the ref starts as. */
  const armedProceedWithoutStimulusRef = useRef(seededProceedWithoutStimulus)
  const [referencesStimulus, setReferencesStimulus] = useState(false)
  /** Typed ask held when the armed (non-intake) media gate blocks start. */
  const [blockedArmedAsk, setBlockedArmedAsk] = useState<string | null>(null)
  const stimulusReferenceCacheRef = useRef<{
    key: string
    referencesStimulus: boolean
  } | null>(null)
  /** Latch the audience fetch once this session arms — never on remount. */
  const [audiencesPrelatched, setAudiencesPrelatched] = useState(false)
  /* What a typed command hands to the menu. A nonce rather than a boolean so
     the same command typed twice in a row still opens its confirm screen the
     second time. */
  const [commandSeed, setCommandSeed] = useState<ThreadCommandSeed | null>(null)
  /**
   * A `/<skill-slug>` tag picked with nothing typed (D2 of
   * docs/plans/2026-10-21-assistant-prompt-cleanup.md). Its own state, never
   * `armedWorkflowSlug`: that slug drives `armedCommand`, intake and the
   * `/report` scoping send, and a tag is none of those. The next send carries
   * `stepSkillSlug` once, then disarms.
   */
  const [armedSkillCommand, setArmedSkillCommand] =
    useState<ThreadCommand | null>(null)
  /* Arming any workflow or command disarms a skill tag. The reverse lives in
     `handleArmSkillCommand`. */
  useEffect(() => {
    if (armedWorkflowSlug !== null) {
      setArmedSkillCommand(null)
    }
  }, [armedWorkflowSlug])
  /** Prefill the composer after `/test` arms with a seeded or plan ask. */
  const [composerAskSeed, setComposerAskSeed] = useState<{
    text: string
    nonce: number
  } | null>(null)
  const conversationRef = useRef<HTMLDivElement>(null)
  const [commandConfirmationTarget, setCommandConfirmationTarget] = useState<HTMLDivElement | null>(null)
  /* The composer floats over the bottom of the conversation, so the column has
     to reserve exactly as much room as the composer actually takes. Measured
     rather than a fixed padding class: the box grows with the action chips, an
     armed workflow chip, the turn chrome row and the textarea itself, and the
     thing that must never be covered is the last thing in the thread (today the
     docked approval gate on the composer, which is where the money is spent). */
  const [composerHeight, setComposerHeight] = useState(0)
  const composerResizeRef = useRef<ResizeObserver | null>(null)
  /* A callback ref, not an effect: the composer mounts only once the thread has
     loaded, which is AFTER this component's early returns, and an effect placed
     where it could see that node would be a hook after a conditional return. */
  const measureComposerWrap = useCallback((node: HTMLDivElement | null) => {
    composerResizeRef.current?.disconnect()
    composerResizeRef.current = null

    if (!node) {
      setComposerHeight(0)
      return
    }

    const measure = () => setComposerHeight(node.getBoundingClientRect().height)
    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    composerResizeRef.current = observer
  }, [])
  const lastQuestionRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const observationEpochRef = useRef(0)
  const observationMountedRef = useRef(true)
  const observedThreadRef = useRef(token)
  observedThreadRef.current = token
  const activeExecutionRef = useRef<{ threadToken: string; token: string } | null>(null)
  const stopRequestedRef = useRef(false)
  const notifiedPauseRef = useRef<string | null>(null)
  const nextContinuationRef = useRef<{ parentToken: string; token: string } | null>(null)

  function isCurrentObservation(controller: AbortController): boolean {
    return observationMountedRef.current && abortRef.current === controller && observedThreadRef.current === token
  }

  function acceptedExecution(snapshot: AssistantTurnSnapshot) {
    activeExecutionRef.current = { threadToken: snapshot.threadToken, token: snapshot.token }
    if (stopRequestedRef.current) {
      void assistantTurnsApi.cancel(snapshot.threadToken, snapshot.token)
        .then(() => nativeQuickStudies.refresh())
        .catch(() => toast.error('Could not stop the assistant. Try Stop again.'))
    }
  }
  function notePausedContinuation(snapshot: AssistantTurnSnapshot) {
    if (snapshot.continuation?.status === 'started' && snapshot.continuation.turnToken) {
      nextContinuationRef.current = { parentToken: snapshot.token, token: snapshot.continuation.turnToken }
    }
    if (snapshot.continuation?.status === 'paused' && notifiedPauseRef.current !== snapshot.token) {
      notifiedPauseRef.current = snapshot.token
      toast.info(snapshot.continuation.reason === 'assistant_turn_cancelled'
        ? 'The remaining plan steps were stopped. You can start the next step from the plan.'
        : 'The next plan step is waiting. Start it from the plan when you are ready.')
    }
  }

  async function finishObservation(controller: AbortController) {
    if (!isCurrentObservation(controller)) return
    const accepted = activeExecutionRef.current
    const continuation = nextContinuationRef.current?.parentToken === accepted?.token ? nextContinuationRef.current : null
    try {
      // Keep busy while resolving the explicit next execution. A child may
      // already have finished by the time this viewer attaches to it.
      const next = continuation
        ? await assistantTurnsApi.get(token, continuation.token, controller.signal)
        : await assistantTurnsApi.active(token, controller.signal)
      if (!isCurrentObservation(controller)) return
      activeExecutionRef.current = null
      abortRef.current = null
      endTurn()
      if (next && (continuation || isAssistantTurnActive(next))) void resumeBackgroundTurn(next)
    } catch {
      if (!isCurrentObservation(controller)) return
      activeExecutionRef.current = null
      abortRef.current = null
      endTurn()
      // Saved results remain visible; reloading can reconnect to later work.
    }
  }

  const pendingSentRef = useRef(false)
  const didInitialScrollRef = useRef(false)
  /* When the newest question was last parked, and the scroller width that park
     was measured against. See the resize observer below. */
  const lastParkAtRef = useRef(0)
  /* Stamped when a thread is opened. While it is fresh, content that
     renders in late (living artifacts arrive on their own fetch) re-lands
     the reader at the end instead of leaving them mid-column. */
  const arrivedAtRef = useRef(0)
  const lastConversationWidthRef = useRef<number | null>(null)
  /* True while the docked gate is tweening its own height open or shut. That
     tween moves the composer's border box every frame, which both observers
     below read as layout movement and would answer with a scroll correction
     per frame, chasing a scrollHeight that is still moving: the dock appears
     to overshoot and snap back as the last correction lands. A height we are
     animating deliberately is not content arriving late, so the corrections
     sit the animation out. */
  const dockTweeningRef = useRef(false)
  /* A ref and not state: flipping this must not itself render the page, or
     the render would be one more layout change for the observers to answer. */
  const handleDockTweenChange = useCallback((active: boolean) => {
    dockTweeningRef.current = active
  }, [])
  /* Read from scroll effects and from the resize observer, which is created
     once per `loading` and would otherwise close over a stale `isRunActive`.
     A layout effect declared before those keeps it current for the commit
     they run in. */
  const isRunActiveRef = useRef(false)
  /* Signature of the settled steps this thread has already reacted to, plus
     the step whose group still owes a scroll. See the effect below. */
  const settledScrollRef = useRef<{
    signature: string | null
    stepId: string | null
  }>({ signature: null, stepId: null })
  /**
   * Unanchored-citation results, keyed on the turn object itself. A streaming
   * token replaces only the newest turn, so every older answer keeps its entry
   * and `injectCitationMarkers` is not re-run across the whole thread on each
   * frame. Weak so finished turns are collectable with the turn list.
   */
  const [conversationHeight, setConversationHeight] = useState(0)

  const docsByToken = useMemo(() => {
    const map = new Map<string, KnowledgeDocumentListItem>()
    for (const doc of documents) {
      map.set(doc.token, doc)
    }
    return map
  }, [documents])

  /* The ONE way this page reads a run. The hook returns fixtures today and the
     live socket after Step 7.1; nothing below knows which. */
  const {
    run,
    template: runTemplate,
    isLoading: runLoading,
    /* A read or an action the server refused, notably the payment guard, which
       covers the plain thread lookup too. The pane says so rather than showing
       the "no run in this thread" empty state, which would read as "nothing
       here" for an org that has runs it is simply not allowed to read. */
    error: runError,
    submitGate,
    reviseStep,
    updateSegment,
    retryStep,
    cancelRun,
    adoptRun,
  } = useWorkflowRun(token || null)
  // W6-QS-c: the run (and its revision) a Compare-segments pick asks cut plots for.
  const cutPlotsSource = useMemo(() => quickStudyCutPlotsSourceFor(run), [run])
  /* The pending run's segment as one composer chip: its label, else what its
     filter's conditions say. Null for a full-audience run. */
  const runSegmentChipLabel = useMemo(() => {
    if (!run?.populationFilter) {
      return null
    }
    const label =
      cleanSegmentLabel(run.segmentLabel) ||
      segmentLabelFromChips(segmentChipsFromFilter(run.populationFilter))
    return label || null
  }, [run?.populationFilter, run?.segmentLabel])

  /* Living thread library — rail / inline / deep-link source of truth.
     `useWorkflowRun` stays for the run pane only. */
  const {
    artifacts: livingArtifactDtos,
    isLoading: livingArtifactsLoading,
    error: livingArtifactsError,
    refresh: refreshLivingArtifacts,
  } = useThreadLivingArtifacts(token || null)

  /* ONE answer to "can this thread start a workflow right now": the rule the
     server already enforces (one active run per thread, the 409 behind
     `uq_workflow_runs_active_per_thread`) and the one `ThreadCommandMenu`
     already applies to itself. Engages when a run starts, lifts on a terminal
     status. */
  const isRunActive = Boolean(run && !isWorkflowRunTerminal(run.status))

  /* Questions preview stays on the composer. Every other pending gate —
     scan, brief, report — already has its body in the thread, so Continue
     lives there too. One mount owns the revision handle either way.
     Computed here, beside `isRunActive` and off the same two values, because
     `handleComposerSend` reads it: a hoisted function declaration that ran
     before the old declaration site further down would have hit the TDZ. */
  const docksGateOnComposer = pendingGateDocksOnComposer(run, runTemplate)

  /**
   * Keep the sidebar Recent rail in sync with the thread this page is looking
   * at. Create sites also upsert immediately, but the first ask retitles the
   * thread ("Untitled" → the user's sentence) and only this page sees that GET.
   */
  useEffect(() => {
    if (!thread) {
      return
    }
    upsertSidebarThread(thread)
  }, [thread])

  /**
   * Plan workflow step completion: when the run that a plan Start seeded
   * reaches a terminal status, advance the cursor (or mark failed). Spend /
   * gate UX stays on ThreadRunGate — this only moves the plan playlist.
   */
  useEffect(() => {
    const planMessageId = planRunMessageIdRef.current
    if (!planMessageId || !run || !isWorkflowRunTerminal(run.status)) {
      return
    }
    const plan = plansByMessageId[planMessageId]
    if (!plan || plan.lifecycle !== 'active') {
      return
    }
    const current =
      plan.currentIndex != null ? plan.steps[plan.currentIndex] : null
    if (!current || current.status !== 'running') {
      return
    }
    if (run.status === 'completed') {
      /* Advance the cursor, and STOP. The next step is left `current` for the
         reader to start, from the card's Start or by saying "go" to the run
         completion announcement, which now names that step and asks for
         exactly that.

         Applying the handoff here is what opened a command dialog the moment
         a run finished: the reader had not asked for the next step, had not
         read the results yet, and the announcement telling them what came
         next arrived to find the dialog already on screen. A modal is
         something the reader opens, never something the product opens on
         their behalf. */
      markCurrentDone(planMessageId)
      planRunMessageIdRef.current = null
    } else {
      markCurrentFailed(planMessageId)
      planRunMessageIdRef.current = null
    }
  }, [
    run,
    plansByMessageId,
    markCurrentDone,
    markCurrentFailed,
  ])

  /* Workflow entry, decision D3: the same three hooks `HomeLabStage` mounts, so
     the two composers arm a workflow through one code path rather than two. */
  const { templates: workflowTemplates } = useWorkflowTemplates()
  const { template: armedLobbyWorkflow, isError: isArmedLobbyWorkflowError } =
    useWorkflowTemplate(armedWorkflowSlug ?? undefined)
  /* One armed workflow, two doors. The record the command menu handed over
     wins, because a thread-action template is invisible to the lookup above;
     everything downstream reads this and never has to know which door it came
     through. */
  const armedWorkflow = armedTemplate ?? armedLobbyWorkflow
  /* THE PAGE'S OWN READY-AUDIENCE LIST (plan 2026-09-14-twin-test-bound-audience,
     D4). `ThreadCommandMenu` keeps its own latch for the confirm card and is
     untouched; this second, page-level read is what lets the composer name the
     audience a LIVE run is bound to after an F5, when the menu may never open and
     `clearArmedWorkflow` has already dropped the persisted `audienceName`.
     Requested only when a template that declares an `audience` input is actually
     in play — armed, or running — so no other thread pays for the list. */
  const pageAudiencesEnabled =
    (armedWorkflow?.inputs.some((spec) => spec.type === 'audience') ?? false) ||
    (run !== null &&
      run !== undefined &&
      (runTemplate?.inputs.some((spec) => spec.type === 'audience') ?? false))
  const { audiences: pageAudiences } =
    useWorkflowScheduleAudiences(pageAudiencesEnabled)
  /* What the activity checklist can name a step's object from: only lists
     this page already loads, never a new request. A step whose id matches
     none of them shows its verb alone, never the raw id
     (docs/plans/thread-activity-steps.md, D5). */
  const activityPageData = useMemo<ActivityPageData>(
    () => ({
      audiences: pageAudiences,
      documents: documents.map((doc) => ({
        token: doc.token,
        name: doc.title?.trim() || doc.originalFilename,
      })),
      artifacts: livingArtifactDtos,
      workflows: workflowTemplates,
      runs:
        run?.token && runTemplate?.displayName
          ? [{ token: run.token, name: runTemplate.displayName }]
          : [],
    }),
    [
      pageAudiences,
      documents,
      livingArtifactDtos,
      workflowTemplates,
      run?.token,
      runTemplate?.displayName,
    ],
  )
  /**
   * The audience the ARMED command is bound to: the confirm screen's own answer
   * first, then the one resolver over the page's list and the remembered
   * audience. Null when nothing can name it — the composer then shows the
   * command chip alone rather than a blank second chip (D4).
   */
  const armedAudienceChipName =
    armedAudienceName ||
    (armedWorkflow
      ? audienceNameForInputs(
          armedWorkflow,
          armedInputs,
          pageAudiences,
          rememberedAudience,
        )
      : null)
  /**
   * The same name for a LIVE run, which no longer has an armed snapshot to read:
   * the id the run was started with, resolved against the list, and only then the
   * `quick_study` step's own `audienceName` — which exists only once the panel has
   * been drawn, and is therefore the fallback rather than the source.
   */
  const runAudienceChipName = useMemo(() => {
    if (!run) {
      return null
    }
    const fromInputs = runTemplate
      ? audienceNameForInputs(
          runTemplate,
          run.inputs,
          pageAudiences,
          rememberedAudience,
        )
      : null
    if (fromInputs) {
      return fromInputs
    }
    for (const step of run.steps) {
      const output = step.outputOverride ?? step.output
      if (output && output.kind === 'quick_study' && output.audienceName) {
        return output.audienceName
      }
    }
    return null
  }, [run, runTemplate, pageAudiences, rememberedAudience])
  /* Latest-value refs for the long-lived socket callback in the settled-step
     effect below, the same shape `isRunActiveRef` uses. `run` itself must NOT
     join that effect's deps: the object changes on every `:step` and `:sync`
     event, so depending on it would unsubscribe, resubscribe and re-list the
     thread's messages once per frame of a live run. Declared before the
     effect so the value it reads belongs to the commit it runs in. */
  const runRef = useRef(run)
  const runAudienceChipNameRef = useRef(runAudienceChipName)
  useLayoutEffect(() => {
    runRef.current = run
  }, [run])
  useLayoutEffect(() => {
    runAudienceChipNameRef.current = runAudienceChipName
  }, [runAudienceChipName])
  const hasArmedStimulus =
    armedWorkflow?.inputs.some(
      (spec) =>
        spec.type === 'stimulus' &&
        hasBoundStimulusValue(armedInputs[spec.key]),
    ) ?? false
  const { startRun: startWorkflowRun, isStarting: isStartingRun } =
    useStartWorkflowRun()

  /* The same list the `Commands` menu shows, mounted a second time on purpose:
     the hook is SWR-backed, so both consumers share one request (D6). The
     composer needs it to know what a typed `/name` resolves to. In-thread
     (D5): the full command list, not the off-thread `/test`-only surface. */
  const { commands: threadCommands, isLoading: threadCommandsLoading } = useThreadCommands(
    assistantPlatformEnabled && Boolean(token),
    { surface: 'thread' },
  )

  /** The builtin command's persisted stable identity cannot turn into a
   * workflow spend when its catalog read fails. Other workflows keep their
   * existing typed command metadata and explicit execution path. */
  const armedCommand = armedWorkflowSlug
    ? (threadCommands.find((entry) => entry.slug === armedWorkflowSlug) ?? null)
    : null
  const isCanonicalQuickStudyArmed = armedWorkflowSlug === CANONICAL_QUICK_STUDY_COMMAND_SLUG
  const isIntakeArmed = isCanonicalQuickStudyArmed || Boolean(armedCommand?.intake)
  /**
   * The armed command is `/report` (D12): its send goes to the SCOPING beat of
   * report mode, and the settled brief is written by an assistant turn rather
   * than started as a run.
   *
   * Read off the command's own `intakeKind`, never off the slug, for the same
   * reason `isIntakeArmed` reads `arms`: the marker travels with the command.
   */
  const isReportIntakeArmed = armedCommand?.intakeKind === 'report'
  /**
   * A slug-only arm (e.g. a hand-off that could not carry the template record)
   * resolves through `armedLobbyWorkflow`'s own SWR fetch. When that fetch
   * fails outright — never for the ordinary "not found" case, which is
   * `isError: false` — the pending dispatch below must not strand the
   * message waiting on a `armedWorkflow` that will never resolve; it toasts,
   * disarms, and falls back to the plain ask. `armedTemplate` already being
   * set (the D1 hand-off's usual path) means there is nothing to fetch, so
   * the lobby fetch's own error is irrelevant.
   */
  const armedTemplateError =
    armedWorkflowSlug !== null && !armedTemplate && isArmedLobbyWorkflowError
  /* A report-kind intake settles the moment it has a brief: there is no
     audience input anywhere in report mode, so waiting on one would leave the
     auto-start gate permanently shut. */
  const settledAudience = isReportIntakeArmed
    ? REPORT_INTAKE_SETTLED_AUDIENCE
    : settledAudienceId(armedWorkflow, armedInputs)
  const intakeStartKey =
    token && intakeDirection && settledAudience
      ? buildIntakeStartLatchKey(token, intakeDirection, settledAudience)
      : null
  const canAutoStartIntake = Boolean(
    isIntakeArmed &&
      (armedWorkflow || isReportIntakeArmed || isCanonicalQuickStudyArmed) &&
      intakeStartKey &&
      /* A report brief WRITES INTO the conversation, so unlike a twin-test
         start (which creates a run and touches no turn) it has to wait for the
         conversation to be its own:

         - `!loading`: a brief settled before this mount (F5 mid-conversation)
           would otherwise race the initial load, whose `listMessages` ends in
           a `setTurns` that wipes the turn out from under it.
         - `!asking`: the scoping turn sets the direction BEFORE its own
           `finally` clears `asking`, so firing on that render would meet
           `runReportTurn`'s one-turn-at-a-time guard, bail, and leave the
           latch spent with nothing written.

         Both only delay the fire; the latch is still written exactly once,
         when it actually happens. Twin-test timing is left alone. */
      (!isReportIntakeArmed || (!loading && !asking)),
  )
  const isIntakeStartLatched = Boolean(
    intakeStartKey && latchedStartKey === intakeStartKey,
  )
  /* Retry is failure/remount chrome, not a second CTA. Hydrate with a
     persisted latch shows it immediately; a live auto-start waits until
     `confirmIntakeDirection` resolves null. */
  const [showIntakeRetry, setShowIntakeRetry] = useState(() =>
    Boolean(loadArmedWorkflowState(token).state.latchedStartKey),
  )
  /** Keep talking hid the intake start row; stays until a new direction arrives. */
  const [intakeStartUiDismissed, setIntakeStartUiDismissed] = useState(false)
  /**
   * `confirmIntakeDirection` is in flight. The auto-start awaits the stimulus
   * classifier BEFORE `startWorkflowRun` raises `isStartingRun`, so without
   * this the "Keep talking" row flashed for the length of that call: a
   * direction with no run yet looked like a direction waiting on the reader.
   * The row only comes back when the start resolves with something to show
   * (the stimulus gate unmet, or a refused start with its Retry).
   */
  const [isConfirmingIntake, setIsConfirmingIntake] = useState(false)

  /**
   * The open gate's own handle on chat-driven revision, published by
   * `ThreadRunGate` (Step 8, which owns everything behind it).
   *
   * ROUTE BY THE HANDLE AND NOTHING ELSE. Non-null means a typed message is a
   * revision of the questions the gate is holding; null means it is an
   * ordinary question for the assistant. All three conditions the plan names
   * (a pending gate, `viewerOwnsRun`, an editable gate) are already folded
   * into that null by the gate, so this page re-derives none of them and holds
   * no gate knowledge of its own.
   */
  const [gateRevisionHandle, setGateRevisionHandle] =
    useState<GateRevisionHandle | null>(null)

  /**
   * Bumped once per composer send while the gate docks there (D6, Fernando's
   * ask): `ThreadRunGate` collapses to one line on a genuine increment and
   * never re-collapses on a re-render carrying the same number. Read by
   * nothing else on this page — the gate owns what "collapsed" means.
   */
  const [gateCollapseSignal, setGateCollapseSignal] = useState(0)

  /* Stimuli Anywhere Step 1: hosted composer attachments (ChatImage + url/objectKey/fileId).
     Register on host so send can pass registry tokens. Living artifact promote
     waits for send — do not refresh the Artifacts rail here.
     Step 3 seeds the stimulus input from this list via applySeedStimulus — do not
     send these as thread-message body bytes. */
  const [threadAttachments, setThreadAttachments] = useState<Array<ChatImage>>(
    [],
  )
  const [threadPdfText, setThreadPdfText] = useState<string | null>(null)
  const [threadPdfName, setThreadPdfName] = useState<string | null>(null)
  const [threadAssets, setThreadAssets] = useState<Array<ThreadAssetView>>([])
  /* Per assistant turn: the `asset:` images its answer renders inline right
     now, as the answer body reports them. An image tile stays until its token
     is here, so a ref still being written out never leaves a gap. */
  const [placedAssetTokensByTurn, setPlacedAssetTokensByTurn] = useState<
    Record<string, Array<string>>
  >({})
  const recordPlacedAssetTokens = useCallback(
    (turnId: string, tokens: Array<string>) => {
      setPlacedAssetTokensByTurn((prev) => {
        const current = prev[turnId] ?? []
        if (
          current.length === tokens.length &&
          current.every((item, index) => item === tokens[index])
        ) {
          return prev
        }
        return { ...prev, [turnId]: tokens }
      })
    },
    [],
  )
  const threadAttachmentsRef = useRef<Array<ChatImage>>([])
  const registeredByFileIdRef = useRef<Map<string, string>>(new Map())
  const registerFailedByFileIdRef = useRef<Set<string>>(new Set())
  const inFlightRegisterRef = useRef<Map<string, Promise<string | null>>>(
    new Map(),
  )
  const composerScopedTokenRef = useRef(token)
  const didSwitchThread = composerScopedTokenRef.current !== token
  if (didSwitchThread) {
    composerScopedTokenRef.current = token
    registeredByFileIdRef.current = new Map()
    registerFailedByFileIdRef.current = new Set()
    inFlightRegisterRef.current = new Map()
    setThreadAttachments([])
    setThreadPdfText(null)
    setThreadPdfName(null)
    setThreadAssets([])
  }
  const composerTiles = didSwitchThread ? [] : threadAttachments
  threadAttachmentsRef.current = composerTiles
  /** Composer-state only. Never DELETE registry rows. */
  const clearThreadComposerAttachments = useCallback(() => {
    setThreadAttachments([])
    setThreadPdfText(null)
    setThreadPdfName(null)
  }, [])
  const handleThreadPdfChange = useCallback(
    (text: string | null, name: string | null) => {
      setThreadPdfText(text)
      setThreadPdfName(name)
    },
    [],
  )

  /** What an image retry resends, per assistant turn (image plan Step 4b). */
  const imageRetryRequestsRef = useRef<Map<string, ImageRetryRequest>>(new Map())

  const refreshThreadAssets = useCallback(async () => {
    if (!token) {
      return
    }
    try {
      const epoch = observationEpochRef.current
      const listed = await threadAssetsApi.list(token)
      if (!observationMountedRef.current || observedThreadRef.current !== token || observationEpochRef.current !== epoch) return
      setThreadAssets(listed)
    } catch {
      /* Pane/tiles refresh on reload; a failed list must not break the thread. */
    }
  }, [token])

  const registerComposerTile = useCallback(
    async (threadToken: string, tile: ChatImage) => {
      const fileId = tile.fileId
      const kind = hostableKindFromChatImage(tile)
      if (!fileId || !tile.url || !kind) {
        return null
      }
      if (registeredByFileIdRef.current.has(fileId)) {
        return registeredByFileIdRef.current.get(fileId) ?? null
      }
      const existingInFlight = inFlightRegisterRef.current.get(fileId)
      if (existingInFlight) {
        return existingInFlight
      }
      const promise = (async () => {
        for (let attempt = 0; attempt < THREAD_ASSET_REGISTER_ATTEMPTS; attempt += 1) {
          try {
            const result = await threadAssetsApi.register(threadToken, {
              fileToken: fileId,
              kind,
              ...(tile.label ? { label: tile.label } : {}),
              ...(tile.mimeType ? { mimeType: tile.mimeType } : {}),
            })
            if (composerScopedTokenRef.current !== threadToken) {
              return result.token
            }
            registeredByFileIdRef.current.set(fileId, result.token)
            registerFailedByFileIdRef.current.delete(fileId)
            setThreadAttachments((prev) =>
              prev.map((image) =>
                image.fileId === fileId
                  ? { ...image, registerFailed: undefined }
                  : image,
              ),
            )
            await refreshThreadAssets()
            return result.token
          } catch {
            /* bounded retry */
          }
        }
        if (composerScopedTokenRef.current !== threadToken) {
          return null
        }
        registerFailedByFileIdRef.current.add(fileId)
        setThreadAttachments((prev) =>
          prev.map((image) =>
            image.fileId === fileId ? { ...image, registerFailed: true } : image,
          ),
        )
        return null
      })()
      inFlightRegisterRef.current.set(fileId, promise)
      try {
        return await promise
      } finally {
        inFlightRegisterRef.current.delete(fileId)
      }
    },
    [refreshThreadAssets],
  )

  useEffect(() => {
    if (!token || !assistantPlatformEnabled) {
      return
    }
    for (const tile of composerTiles) {
      const fileId = tile.fileId
      if (!fileId || !tile.url || tile.registerFailed) {
        continue
      }
      if (registerFailedByFileIdRef.current.has(fileId)) {
        continue
      }
      if (registeredByFileIdRef.current.has(fileId)) {
        continue
      }
      if (inFlightRegisterRef.current.has(fileId)) {
        continue
      }
      if (!hostableKindFromChatImage(tile)) {
        continue
      }
      void registerComposerTile(token, tile)
    }
  }, [
    assistantPlatformEnabled,
    registerComposerTile,
    composerTiles,
    token,
  ])

  const handleRemoveAsset = useCallback(
    (assetToken: string) => {
      if (!token) {
        return
      }
      void threadAssetsApi
        .remove(token, assetToken)
        .then(() => {
          const fileIds: Array<string> = []
          for (const [fileId, listedToken] of registeredByFileIdRef.current.entries()) {
            if (listedToken === assetToken) {
              fileIds.push(fileId)
              registeredByFileIdRef.current.delete(fileId)
            }
          }
          if (fileIds.length > 0) {
            const removedFileIds = new Set(fileIds)
            setThreadAttachments((prev) =>
              prev.filter(
                (image) => !image.fileId || !removedFileIds.has(image.fileId),
              ),
            )
          }
          void refreshLivingArtifacts()
          return refreshThreadAssets()
        })
        .catch(() => {
          toast.error(t('threads.detail.toast.removeAttachmentFailed'))
          void refreshThreadAssets()
        })
    },
    [refreshLivingArtifacts, refreshThreadAssets, token, t],
  )

  const handleRemoveComposerTile = useCallback(
    (image: ChatImage) => {
      const fileId = image.fileId
      if (!fileId) {
        return
      }
      registerFailedByFileIdRef.current.delete(fileId)
      const inFlight = inFlightRegisterRef.current.get(fileId)
      if (inFlight) {
        void inFlight.then((assetToken) => {
          if (!assetToken) {
            return
          }
          handleRemoveAsset(assetToken)
        })
        return
      }
      const assetToken = registeredByFileIdRef.current.get(fileId)
      if (!assetToken) {
        registeredByFileIdRef.current.delete(fileId)
        return
      }
      handleRemoveAsset(assetToken)
    },
    [handleRemoveAsset],
  )

  async function collectAttachmentTokens(): Promise<Array<string>> {
    const tokens: Array<string> = []
    const seen = new Set<string>()
    for (const tile of threadAttachmentsRef.current) {
      const fileId = tile.fileId
      if (!fileId || tile.registerFailed) {
        continue
      }
      const inFlight = inFlightRegisterRef.current.get(fileId)
      const resolved = inFlight
        ? await inFlight
        : registeredByFileIdRef.current.get(fileId) ?? null
      if (!resolved || seen.has(resolved)) {
        continue
      }
      seen.add(resolved)
      tokens.push(resolved)
    }
    return tokens
  }
  /* Re-seed on thread switch, never wipe. This effect runs after the first
     commit, so writing `false` here threw away the D1 hand-off the lazy init
     at `armedProceedWithoutStimulusRef` had just read: both consumers fire
     strictly later (the hand-off dispatch waits on `loading`, the auto-start
     on `canAutoStartIntake`), so they always saw `false`. Reading the new
     thread's OWN persisted seed keeps the reset on a real thread switch and
     stops the mount wipe. */
  useEffect(() => {
    armedProceedWithoutStimulusRef.current =
      loadArmedWorkflowState(token).state.proceedWithoutStimulus === true
    setBlockedArmedAsk(null)
  }, [token])
  const { ingestFiles: ingestThreadFiles, pendingCount: threadFilePendingCount } =
    useChatFileIngest({
      images: composerTiles,
      onImagesChange: setThreadAttachments,
      onImagesMerge: setThreadAttachments,
      onPdfChange: handleThreadPdfChange,
      maxImages: 3,
      uploadHosted: uploadHostedChatStimulus,
      acceptVideo: true,
    })
  const stimulusStillHosting = hasStimulusStillHosting(composerTiles)
  const threadSendBlockedHint =
    threadFilePendingCount > 0 || stimulusStillHosting
      ? stimulusStillHosting &&
        composerTiles.some((im) => isVideoChatImage(im) && !im.url)
        ? t('threads.detail.uploadingVideo')
        : t('threads.attach.uploading')
      : undefined

  /* D7: an armed workflow owns the textarea — its placeholder IS the workflow's
     text input label — so a reply that happens to start with `/` must reach the
     workflow, not the command parser. Withholding both props turns every typed
     command path off inside `ChatComposer` until the workflow is cleared. */
  const composerCommands = armedWorkflowSlug
    ? undefined
    : threadCommands.map((entry) => ({
        command: entry.command,
        label: entry.label,
        description: entry.summary,
      }))

  /* Returns false for a name nothing matches, which is what makes the composer
     keep the text and say "No such command" rather than sending it as prose
     (D5). A command typed before the list resolves lands here too, and is
     honestly unknown: the text survives, so a second Enter works. */
  function handleTypedCommand(command: string, rest: string): boolean {
    /* Case-insensitive, to match how the dropdown filters: `/Report` offers the
       report row, so `/Report` + Enter has to run it rather than report itself
       as unknown. */
    const typed = command.toLowerCase()
    const match = threadCommands.find((entry) => matchesTypedCommand(entry, typed))
    if (!match) {
      return false
    }

    /* Opens the confirm screen and nothing more. The typed path and the picked
       path converge on the menu's own gate, which is the only place a run can
       start (D4). */
    setCommandSeed((current) => ({
      command: match.command,
      rest,
      nonce: (current?.nonce ?? 0) + 1,
    }))
    return true
  }

  // One piece of state drives both the docked rail and the narrow-viewport
  // overlay: `ThreadReferencesSidebar` decides which it is from its own media
  // query, so the page only has to say "open".
  /** A room just created from this thread: its opening message, then the side pane on it. */
  function openStartedRoom(roomToken: string, openingQuestion: string) {
    setRoomOpeningMessages((current) => ({ ...current, [roomToken]: openingQuestion }))
    dispatchSession({ type: 'room_created', token: roomToken })
    changeRailOpen(true)
  }

  function changeRailOpen(next: boolean) {
    setRailOpen(next)
    persistRailOpen(token, next)
    if (!next) {
      setCalculationFocus(null)
    }
  }

  function writeStudyParam(studyToken: string | null) {
    setSearchParams((prev) => {
      const current = prev.get(THREAD_STUDY_PARAM)
      if (studyToken) {
        if (current === studyToken) {
          return prev
        }
        const next = new URLSearchParams(prev)
        next.set(THREAD_STUDY_PARAM, studyToken)
        return next
      }
      if (!current) {
        return prev
      }
      const next = new URLSearchParams(prev)
      next.delete(THREAD_STUDY_PARAM)
      return next
    }, { replace: true })
  }

  function openStudyFrame(studyToken: string, surface: 'draft' | 'results') {
    setSearchParams(previous => {const next = new URLSearchParams(previous); next.delete('quickStudy'); next.delete('quickRun'); next.delete('quickRevision'); return next}, {replace: true})
    const current = sessionSelectionRef.current
    const already =
      current.kind === 'study' &&
      current.token === studyToken &&
      (current.studySurface ?? 'draft') === surface
    if (!already) {
      setSessionTabChosen(true)
      dispatchSession({
        type: 'select',
        selection: { kind: 'study', token: studyToken, studySurface: surface },
      })
    } else {
      setSessionTabChosen(true)
    }
    setRailOpen(true)
    writeStudyParam(studyToken)
  }

  function openNativeQuickStudy(draftToken: string) {
    setSessionTabChosen(true)
    dispatchSession({type: 'select', selection: {kind: 'quick_study', token: draftToken}})
    setRailOpen(true)
    setSearchParams(previous => {const next = new URLSearchParams(previous); next.set('quickStudy', draftToken); const draft = nativeQuickStudies.items.find(item => item.draftToken === draftToken); if (draft) {next.set('originMessage', draft.originMessageToken); next.set('quickRevision', String(draft.revision))}; const run = draft?.activeRun; if (run) next.set('quickRun', run.runToken); else next.delete('quickRun'); next.delete(THREAD_STUDY_PARAM); return next}, {replace: true})
  }
  function closeNativeQuickStudy() {
    dispatchSession({type: 'select', selection: {kind: 'sources'}})
    changeRailOpen(false)
    setSearchParams(previous => {const next = new URLSearchParams(previous); next.delete('quickStudy'); next.delete('quickRun'); next.delete('quickRevision'); return next}, {replace: true})
  }
  useEffect(() => {
    if (!requestedNativeQuick || !nativeQuickStudies.items.some(item => item.draftToken === requestedNativeQuick)) return
    if (sessionSelectionRef.current.kind === 'quick_study' && sessionSelectionRef.current.token === requestedNativeQuick) return
    setSessionTabChosen(true)
    dispatchSession({type: 'select', selection: {kind: 'quick_study', token: requestedNativeQuick}})
    setRailOpen(true)
  }, [requestedNativeQuick, nativeQuickStudies.items])

  // After a launch the success page shows briefly, then the pane returns to
  // the Studies list with the new study's row highlighted.
  const [launchedStudyToken, setLaunchedStudyToken] = useState<string | null>(null)
  const launchTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([])

  useEffect(() => {
    return () => {
      launchTimersRef.current.forEach((timer) => clearTimeout(timer))
    }
  }, [])

  function handleStudyLaunched(studyToken: string) {
    openStudyFrame(studyToken, 'results')
    launchTimersRef.current.forEach((timer) => clearTimeout(timer))
    launchTimersRef.current = [
      setTimeout(() => {
        const current = sessionSelectionRef.current
        if (current.kind !== 'study' || current.token !== studyToken) {
          return
        }
        setLaunchedStudyToken(studyToken)
        clearStudyFrame('studies')
      }, STUDY_LAUNCH_HANDOFF_MS),
      setTimeout(() => {
        setLaunchedStudyToken((current) => (current === studyToken ? null : current))
      }, STUDY_LAUNCH_HANDOFF_MS + STUDY_LAUNCH_HIGHLIGHT_MS),
    ]
  }

  function clearStudyFrame(next: 'hide' | 'studies') {
    setSessionTabChosen(true)
    dispatchSession({ type: 'select', selection: { kind: 'studies' } })
    writeStudyParam(null)
    if (next === 'hide') {
      changeRailOpen(false)
      return
    }
    setRailOpen(true)
  }

  const bindStudyClose = useCallback((request: (() => void) | null) => {
    requestStudyCloseRef.current = request
  }, [])

  const studyParam = searchParams.get(THREAD_STUDY_PARAM)?.trim() ?? ''

  useEffect(() => {
    if (!token || !studyParam) {
      return
    }
    const current = sessionSelectionRef.current
    if (current.kind === 'study' && current.token === studyParam) {
      setRailOpen(true)
      return
    }
    let cancelled = false
    void assistantSessionsApi.studies(token).then((items) => {
      if (cancelled) {
        return
      }
      const launched = items.some((item) => item.token === studyParam)
      openStudyFrame(studyParam, launched ? 'results' : 'draft')
    }).catch(() => {
      if (cancelled) {
        return
      }
      openStudyFrame(studyParam, 'draft')
    })
    return () => {
      cancelled = true
    }
    // openStudyFrame reads the selection ref and stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, studyParam])

  // A figure chip opens the drawer on the Context section at that answer's
  // calculation; an open source or expanded artifact steps aside for it.
  function openCalculation(turnId: string, evidenceId: string) {
    setSourceTabs([])
    setActiveSourceKey(null)
    if (expandedArtifactKey !== null) {
      collapseExpandedArtifact()
    }
    setCalculationFocus((current) => ({ turnId, evidenceId, nonce: (current?.nonce ?? 0) + 1 }))
    changeRailOpen(true)
  }

  // Opening mounts the drawer closed, then opens it on the next frame: an
  // element that mounts already in its open position has no start frame to
  // transition from, so it would snap. Closing keeps the content mounted for
  // the length of the transition so `overflow-hidden` clips it on the way out.
  // Unmounting on frame one is what made the close animate a blank gap.
  //
  // `railSlidIn` — not `railOpen` — drives both the wrapper width and the
  // drawer transform, so the clip and the slide start on the same frame and
  // stay in lockstep. Driving them from different frames leaves a sliver of
  // page background running down the open edge for the length of the animation.
  useEffect(() => {
    if (railOpen) {
      setRailMounted(true)
      const frame = window.requestAnimationFrame(() => setRailSlidIn(true))
      return () => {
        window.cancelAnimationFrame(frame)
      }
    }
    setRailSlidIn(false)
    const timer = window.setTimeout(() => {
      setRailMounted(false)
    }, RAIL_TRANSITION_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [railOpen])

  // A run does NOT fly the rail open on its own. The gate that needs a
  // decision already renders inline in the thread (`ThreadRunGate`, right
  // where the reader already is), and the header toggle is an expand icon
  // whether or not this drawer is open. The pane is a convenience view of
  // the same steps, not the place the work happens, so starting a run must
  // not cover the conversation the reader is in the middle of. The rail
  // stays exactly as the reader left it; only the explicit header toggle
  // (`changeRailOpen`) moves it.

  function citationLabel(citation: KnowledgeAskCitation): string {
    const lookupKeys = [citation.documentToken, citation.documentName].filter(
      (value): value is string => Boolean(value && value.trim()),
    )
    for (const key of lookupKeys) {
      const doc = docsByToken.get(key)
      if (doc) {
        return documentTitle(doc)
      }
    }
    const name = (citation.documentName || '').trim()
    if (name && !isCitationUuid(name) && name.toLowerCase() !== 'source') {
      return name
    }
    return t('library.facet.source')
  }

  // Tabs are per document, not per citation: following several citations into
  // the same file should land in one tab rather than pile up duplicates.
  function sourceTabKey(citation: KnowledgeAskCitation): string {
    return citation.documentToken || citation.documentName || 'source'
  }

  function openCitation(index: number, citation: KnowledgeAskCitation) {
    if (citation.granularity === 'study') {
      void studyCitationTarget(citation.documentToken, knowledgeApi.getDocument)
        .then((target) => navigate(target))
        .catch(() => toast.error(t('failedToLoad', { ns: 'common' })))
      return
    }
    dispatchSession({ type: 'select', selection: { kind: 'sources' } })
    setSessionTabChosen(true)
    const key = sourceTabKey(citation)
    // Following a citation means the reader wants evidence: closing every tab
    // afterwards should return them to the source cards, not to nothing.
    // Session intent, not a preference: this deliberately does not write the
    // stored rail preference, so one chip click never reopens the rail on every
    // future thread. Only the explicit header toggle persists.
    setRailOpen(true)
    setSourceTabs((prev) => {
      if (!prev.some((tab) => tab.key === key)) {
        return [
          ...prev,
          {
            key,
            label: citationLabel(citation),
            citationIndex: index,
            citation,
          },
        ]
      }
      // Keep the tab (and its resolved label), retarget it at this citation.
      return prev.map((tab) =>
        tab.key === key ? { ...tab, citationIndex: index, citation } : tab,
      )
    })
    setActiveSourceKey(key)
  }

  function relabelSourceTab(key: string, label: string) {
    setSourceTabs((prev) =>
      prev.map((tab) =>
        tab.key === key && tab.label !== label ? { ...tab, label } : tab,
      ),
    )
  }

  const calculationSources = useMemo(
    () => calculationsFor(turns, calculationFocus),
    [turns, calculationFocus],
  )
  /* Every source the thread's settled answers cited, once each, the most cited first. */
  const threadSources = useMemo(() => aggregateThreadSources(turns), [turns])

  /* The drawer holds stacked Outputs / Workflows / Context / Progress, or an
     opened source that takes over the same shell. Mount when any of those have
     something to show — never auto-open. */
  const hasRun = run !== null
  /* A refused or failed run READ is not the same as a thread with no run:
     `getForThread` returns null for the ordinary "nothing started here" case, so
     an error means the run could not be read at all (the payment guard covers
     the reads too). The pane is the only place that can say so, which is why it
     counts as pane content even with no run in hand. It deliberately does NOT
     feed any auto-open: an org that is refused every read should not have this
     drawer fly open on every thread it visits. */
  const hasRunPane = hasRun || runError !== null
  const runArtifacts = useMemo(() => {
    const foldTokens = foldAssetTokensFromCreativeDtos(livingArtifactDtos)
    const mapped = collectThreadArtifacts(livingArtifactDtos)
    const enriched = enrichStimulusMedia(mapped, threadAssets)
    /* Fold AFTER enrichment so blind shift() leftover pairing is unchanged. */
    return filterFoldedStimulusItems(
      enriched,
      foldTokens,
      livingArtifactDtos,
    )
  }, [livingArtifactDtos, threadAssets])
  /* Study pin shows only this run's living artifacts — not the whole thread library. */
  const studyPinArtifacts = useMemo(() => {
    if (!run?.token) {
      return []
    }
    return runArtifacts.filter((item) => item.runToken === run.token)
  }, [runArtifacts, run?.token])
  const studyDocParts = useMemo(
    () => composeRunStudyDoc(run, runTemplate),
    [run, runTemplate],
  )
  const studyDocPlots = studyDocParts?.plots
  const studyDocQuotes = studyDocParts?.quotes
  const studyDocPopulationId = studyDocParts?.populationId
  const studyDocStrips = studyDocParts?.strips
  const hasArtifacts = runArtifacts.length > 0
  const pendingCreativeDraftCount = useMemo(() => {
    let pending = 0
    for (const turn of turns) {
      pending += countPendingCreativeDrafts(turn.creativeDrafts)
    }
    return pending
  }, [turns])
  const creativeDraftPreviewItems = useMemo(() => {
    const items: Array<ThreadArtifactItem> = []
    for (const turn of turns) {
      const drafts = turn.creativeDrafts
      if (!drafts || drafts.length === 0) {
        continue
      }
      drafts.forEach((draft, index) => {
        items.push(toPreviewItem(draft, draft.body, index))
      })
    }
    return items
  }, [turns])
  const hasThreadAssets = threadAssets.length > 0
  /* The drawer's default tab follows what the thread holds: with no quick
     study, a reload opens on Sources (what the pane shows) rather than
     marking "Quick studies" over source content. A tab the reader picked wins. */
  const sessionSelection: typeof sessionState.selection =
    !sessionTabChosen && sessionState.selection.kind === 'runs' && !sessionState.selection.token && !hasRunPane &&
    calculationSources.length > 0
      ? { kind: 'sources' }
      : sessionState.selection
  /* Resolved by key every render rather than held as the item itself: a key
     that no longer matches anything in `runArtifacts` (a living-store refresh
     that dropped or reordered artifacts) reads as "nothing expanded" and the
     drawer falls back to the Outputs list instead of showing stale content. */
  const expandedArtifact = useMemo(
    () =>
      expandedArtifactKey
        ? (findExpandableThreadArtifact(
            runArtifacts,
            creativeDraftPreviewItems,
            expandedArtifactKey,
          ) ?? null)
        : null,
    [expandedArtifactKey, runArtifacts, creativeDraftPreviewItems],
  )
  const fullscreenArtifact = useMemo(
    () =>
      fullscreenArtifactKey
        ? (findExpandableThreadArtifact(
            runArtifacts,
            creativeDraftPreviewItems,
            fullscreenArtifactKey,
          ) ?? null)
        : null,
    [fullscreenArtifactKey, runArtifacts, creativeDraftPreviewItems],
  )

  /**
   * StudyDoc lookups for the artifact the reader actually has open, keyed off
   * the ARTIFACT's run rather than the page's.
   *
   * A thread accumulates runs, and the newest one is not the one that wrote
   * the document being read: open a study doc from an earlier run and the
   * plots, quote cards and portrait strip were resolved against a run that
   * knows nothing about its tokens, so the body falls back to `Plot: <id>`
   * placeholders on a document whose markdown is completely intact. A run
   * that is stuck or failed still occupies the newest seat, which is why the
   * fix is to key on the artifact, not to skip unhealthy runs.
   *
   * Lazy: nothing is fetched unless a study doc from another run is open.
   * While that fetch is in flight, or if it fails, the current run's lookups
   * stand, so this can only ever match or improve on the old behavior. Once
   * the run does land, its own parts are used even when they are empty: a
   * document with no plots is honest, a document showing another study's
   * plots is not.
   */
  /* The study doc among whatever is open, fullscreen first. Taking simply the
     topmost artifact would drop these lookups to null whenever a non-doc
     artifact was opened over an expanded doc, quietly regressing the doc
     underneath to the current run's plots. */
  const focusedArtifact =
    (fullscreenArtifact?.slug === STUDY_DOC_SLUG ? fullscreenArtifact : null) ??
    (expandedArtifact?.slug === STUDY_DOC_SLUG ? expandedArtifact : null)
  /* Same SWR key as ThreadRunResults/ThreadInlineArtifacts for this run, so
     this costs no extra fetch — it only needs read access to the map. */
  const { tipForItem: focusedArtifactTipForItem } = useThreadArtifactTips(
    focusedArtifact?.runToken,
  )
  const orgName = (authUser?.organizationName ?? authUser?.companyName)?.trim() || null
  const reportHeadline = focusedArtifact
    ? artifactDisplayLabel({
        ...focusedArtifact,
        body: reconcileArtifactBody(
          focusedArtifact.body,
          focusedArtifactTipForItem(focusedArtifact),
        ),
      }).headline.trim() || null
    : null
  const reportPageTitle = reportHeadline
    ? orgName
      ? `${reportHeadline} · ${orgName}`
      : reportHeadline
    : null
  const focusedRunToken = focusedArtifact?.runToken ?? null
  const foreignRunToken =
    focusedRunToken && focusedRunToken !== run?.token ? focusedRunToken : null
  const { run: focusedRun } = useRunResults(foreignRunToken)
  const { template: focusedTemplate } = useRunTemplate(
    focusedRun?.templateToken ?? null,
  )
  const focusedStudyDocParts = useMemo(() => {
    if (!foreignRunToken) {
      return studyDocParts
    }
    if (!focusedRun) {
      /* Nothing, rather than the current run's. While the fetch is in flight,
         or once it has failed for good, the honest answer is that this
         document's lookups are not available: plots and quotes are keyed by
         uuid and would simply miss, but `populationId` and
         `decisionConfidence` are not, and handing another study's values to
         this document is the exact defect being fixed. Placeholders say "not
         loaded"; the wrong population says nothing at all. */
      return undefined
    }
    return composeRunStudyDoc(focusedRun, focusedTemplate)
  }, [foreignRunToken, focusedRun, focusedTemplate, studyDocParts])
  const focusedPlots = focusedStudyDocParts?.plots
  const focusedQuotes = focusedStudyDocParts?.quotes
  const focusedPopulationId = focusedStudyDocParts?.populationId
  const focusedStrips = focusedStudyDocParts?.strips
  const focusedDecisionConfidence = focusedStudyDocParts?.decisionConfidence

  /**
   * Exhibit lookups for an open REPORT, keyed by `question_id` across the
   * thread rather than against one run (Step 8, D11).
   *
   * A report is written by an assistant turn, not by a run. It has no
   * `sourceRunToken` of its own to key on, and it synthesises across every
   * twin test in the thread, so `{{plot:q1}}` in its body addresses the
   * THREAD's findings and the per-artifact keying above has nothing to
   * answer with.
   *
   * Widening the source widens the id space onto a key that was never
   * thread-unique: `question_id` is `q1`, `q2`, … per run, so two twin tests
   * in one thread collide on it by default. `composeThreadExhibits` retires
   * every id more than one finding answers to instead of picking one, because
   * nothing in a report body says which run a sentence came from. The
   * placeholder that leaves behind is the same honesty the study-doc path
   * above protects: a document with no plots is honest, a document showing
   * another study's plots is not. A report that DOES carry lineage (the
   * retired `thread-action-report` workflow's own artifacts) hands its run
   * token in as `preferRunToken`, and that run's ids win outright.
   *
   * Lazy: a thread nobody opened a report in fetches nothing.
   */
  const openReport =
    (fullscreenArtifact?.kind === 'report' ? fullscreenArtifact : null) ??
    (expandedArtifact?.kind === 'report' ? expandedArtifact : null)
  const needsThreadExhibits =
    openReport !== null && hasStudyDocEmbeds(openReport.body)
  const threadExhibitRunTokens = useMemo(() => {
    if (!needsThreadExhibits) {
      return []
    }
    /* The page's own run is passed in below as the object it already holds,
       so it is deliberately not in the fetch list. */
    const tokens: Array<string> = []
    const seen = new Set<string>()
    if (run?.token) {
      seen.add(run.token)
    }
    for (const item of runArtifacts) {
      const itemRunToken = item.runToken
      if (!itemRunToken || seen.has(itemRunToken)) {
        continue
      }
      seen.add(itemRunToken)
      tokens.push(itemRunToken)
    }
    return tokens
  }, [needsThreadExhibits, runArtifacts, run?.token])
  const threadExhibitRuns = useThreadExhibitRuns(threadExhibitRunTokens)
  const threadExhibits = useMemo(() => {
    if (!needsThreadExhibits) {
      return undefined
    }
    return composeThreadExhibits([run, ...threadExhibitRuns], {
      preferRunToken: openReport?.runToken ?? null,
    })
  }, [needsThreadExhibits, run, threadExhibitRuns, openReport?.runToken])
  const reportPlots = threadExhibits?.plots
  const reportQuotes = threadExhibits?.quotes
  const reportStrips = threadExhibits?.strips

  /* Opens the drawer straight to one artifact's full body. Reused by the
     rail's own Outputs list and deep links (`?artifact=`), so the pane stays
     the place for in-drawer reading. Card Maximize uses fullscreen instead. */
  function setArtifactSearchKey(key: string | null) {
    setSearchParams(
      (prev) => writeThreadArtifactSearchParam(prev, key),
      { replace: true },
    )
  }

  function expandArtifact(item: ThreadArtifactItem) {
    dispatchSession({ type: 'select', selection: { kind: 'runs' } })
    setSessionTabChosen(true)
    const searchKey = item.publicId ?? item.key
    setExpandedArtifactKey(searchKey)
    changeRailOpen(true)
    setArtifactSearchKey(searchKey)
  }

  function openArtifactFullscreen(item: ThreadArtifactItem) {
    setFullscreenArtifactKey(item.publicId ?? item.key)
  }

  function collapseExpandedArtifact() {
    setExpandedArtifactKey(null)
    setArtifactSearchKey(null)
  }

  function closeArtifactFullscreen() {
    setFullscreenArtifactKey(null)
  }

  useEffect(() => {
    const key = readThreadArtifactKey(searchParams)
    if (!key) {
      setExpandedArtifactKey((current) => (current === null ? current : null))
      return
    }
    const match = findThreadArtifactBySearchKey(runArtifacts, key)
    if (!match) {
      return
    }
    const resolvedKey = match.publicId ?? match.key
    setExpandedArtifactKey((current) =>
      current === resolvedKey ? current : resolvedKey,
    )
    if (!railOpen) {
      changeRailOpen(true)
    }
  }, [searchParams, runArtifacts, railOpen])

  /* Living cards and outcome lines refresh when a watched step settles
     (`completed` / `failed`) or the run itself finishes. The hook already
     merges `:step` into `run`; this effect does not fetch the run again.
     Do not invent a second report body here — the markdown lives on the
     artifact, and the announcement is already a short assistant turn. */
  const settledStepRefreshKey = useMemo(
    () => settledStepRefreshSignature(run),
    [run],
  )
  useEffect(() => {
    if (!run) {
      return
    }
    const isTerminal = isWorkflowRunTerminal(run.status)
    if (!isTerminal && settledStepRefreshKey.length === 0) {
      return
    }
    void refreshLivingArtifacts()
    if (!token) {
      return
    }
    let cancelled = false
    function applyMessages(messages: Array<KnowledgeThreadMessage>) {
      /* ONLY workflow-run outcome lines, and only when they are not already
         on screen under either of their two names. Appending anything else is
         what made a whole exchange appear twice, which moved
         `lastQuestionIndex` onto the copy, pushed the study pin into the
         historic slot and parked the scroll on the duplicate: the results read
         as vanishing (D0). Both guards live in the merge. */
      setTurns((prev) =>
        withRunCommandChip(
          mergeRunCompletionAnnouncements(prev, messages.map(messageToTurn)),
          runRef.current,
          runAudienceChipNameRef.current,
        ),
      )
    }
    if (run.status !== 'completed') {
      void knowledgeThreadsApi
        .listMessages(token)
        .then((messages) => {
          if (!cancelled) {
            applyMessages(messages)
          }
        })
        .catch(() => {
          /* A missed refresh still lands on the next full thread load. */
        })
      return () => {
        cancelled = true
      }
    }
    /* The announcement is written by a listener on the run's OUTCOME event,
       while this effect fires on the run's STATUS event, and the two race:
       the status reaches the socket first, and the listener still has several
       round trips and an insert to do. Subscribe to thread message events and
       run a stall repair instead of timed polling. */
    const announcementAskId = `${WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX}${run.token}`
    async function refreshMessages() {
      const messages = await knowledgeThreadsApi.listMessages(token)
      if (cancelled) {
        return false
      }
      applyMessages(messages)
      return messages.some((message) => message.askId === announcementAskId)
    }
    void refreshMessages().catch(() => {
      /* A missed refresh still lands on the next full thread load. */
    })
    connectWebSocket()
    subscribeToKnowledgeThread(token)
    const offMessage = onKnowledgeThreadMessageAdded(token, (event) => {
      if (
        event.askId === announcementAskId ||
        event.askId === null ||
        isWorkflowRunAskId(event.askId)
      ) {
        void refreshMessages().catch(() => undefined)
      }
    })
    return () => {
      cancelled = true
      offMessage()
      unsubscribeFromKnowledgeThread(token)
    }
  }, [
    run?.token,
    run?.status,
    settledStepRefreshKey,
    token,
    refreshLivingArtifacts,
  ])

  /* M17: Re-stamp the launch turn's command chip when the run first becomes
     available after a mid-run reload.
     The initial thread-load effect calls withRunCommandChip(turns, run) with
     run = null when useWorkflowRun has not yet resolved; this effect corrects
     it the moment the run token is known, without waiting for a step to
     settle. Depends only on run?.token so status / step changes do not
     trigger an unnecessary re-stamp (the settledStepRefreshKey effect
     already handles those). */
  useEffect(() => {
    if (!run) return
    setTurns((prev) => withRunCommandChip(prev, run, runAudienceChipName))
    /* `runAudienceChipName` is a trigger as well as an input: the ready-audience
       list lands AFTER the run does on an F5, so the launch turn has to be
       re-stamped the moment the id can finally be named (B4). */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.token, runAudienceChipName])

  /* Every run the reader is allowed to see, finished ones included. Progress
     is the thread's ONLY step surface now that the template recipe no longer
     sits beside it, so gating this on a live run would leave a completed
     thread with no record of what ran at all. A terminal run renders the same
     rows in their settled states, which is exactly the record. */
  const showRailProgress = run !== null || runError !== null
  /* A12 owner decision: the pane's Progress section holds the latest run only
     while it runs (or a step failed); once it finishes it lists under Reports
     with the other finished runs and drills into its run view from there. */
  const isLatestRunInProgress = run !== null && isProgressRun(run)
  const showPaneProgress = runError !== null || isLatestRunInProgress
  const railToggleLabel = railOpen
    ? t('chatChrome.collapseSidebar')
    : t('chatChrome.expandSidebar')

  /**
   * Where the current exchange starts. The newest question and everything the
   * thread has produced since it — the answer, the run's busy line, its
   * results, its artifacts — are ONE group, and that group is what carries the
   * parking floor below. An index rather than an id because the render has to
   * split the turn list at it.
   */
  useEffect(() => {
    if (requestedNativeQuick && selectedNativeQuick.data?.activeRun?.status === 'completed') closeNativeQuickStudy()
  }, [requestedNativeQuick, selectedNativeQuick.data?.activeRun?.status])

  /* The "approve every image" loop lives here, not in the card: the first
     approval appends a question, which moves the card's turn out of the
     current exchange and remounts it. */
  // Page-owned like the batches: approving remounts the card.
  const [imageCardSelections, setImageCardSelections] = useState<Record<string, CreativeContextSelection>>({})
  useEffect(() => { setImageCardSelections({}) }, [token])
  /* What the card held when clicked, for the turns that click starts. Absent on the rest: they follow the image approved. */
  const imageCardClickRef = useRef<Record<string, CreativeContextSelection | undefined>>({})
  const imageApprovalRenderRef = useRef<Record<string, ImageApprovalSaved['render']>>({})
  const imageApprovalBatches = useImageApprovalBatches({
    threadToken: token,
    approve: async (messageToken, request, quality, threadToken, format, step, tries) => {
      // The previous approval's execution can still be attached in the background.
      await waitForTurnIdle()
      // Never spend on a thread the reader has left while this one waited.
      if (!observationMountedRef.current || observedThreadRef.current !== threadToken || threadToken !== token) return undefined
      return runAsk(t('threadImage.approval.generate'), {
        imageApproval: {
          messageToken, requestId: request.requestId,
          // A format approval always names its quality: on a reload the view shows the admitted
          // quality, not the saved quote the server would fall back to. Otherwise only a changed
          // choice is sent. The server re-prices it from the shared table.
          ...(format || quality !== request.arguments.quality ? { quality } : {}),
          ...(format ?? {}),
          // With no selection (mixed sizes) the turn still names its size's format, as a reload does.
          turnFormatId: format?.formatId ?? quotedImageFormatId(request) ?? undefined,
          ...(step ? { step } : {}),
          ...(tries ? { tries } : {}),
          ...(imageCardClickRef.current[messageToken] ? { selection: imageCardClickRef.current[messageToken] } : {}),
          quoteBrandToken: request.contextSummary?.brandToken ?? null,
        },
        // The card's choices became the chat's, so the next message carries the new revision.
        onImageAccepted: () => void creativeContext.reload(),
      })
    },
  })

  /* One image batch reads as one ask and one block of images, though each
     image is its own paid turn. A card with several images holds them itself; a read-only
     viewer has no card, so they stay in the thread. */
  const imageApprovalSlotsByTurn = useMemo(
    () => imageApprovalSlots(turns, (requests, cardToken) =>
      !isReadOnlyForViewer && isImageApprovalSet(requests, imageApprovalBatches.batches[cardToken]?.formats ?? imageApprovalBatches.batches[cardToken]?.pickedFormats)),
    [turns, isReadOnlyForViewer, imageApprovalBatches.batches],
  )
  const imageApprovalCards = useMemo(() => imageApprovalCardGroups(imageApprovalSlotsByTurn), [imageApprovalSlotsByTurn])
  const lastQuestionIndex = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      // A folded approval ask is not a new question; its batch's first ask is.
      if (turns[i].role === 'user' && imageApprovalSlotsByTurn.get(turns[i].id)?.placement !== 'hidden') return i
    }
    return -1
  }, [turns, imageApprovalSlotsByTurn])
  const lastQuestionId =
    lastQuestionIndex >= 0 ? turns[lastQuestionIndex].id : null
  // Origin-only links resolve through the authorized thread catalog too.
  const requestedNativeOrigin = requestedNativeQuick ? selectedNativeQuick.data
    : requestedStudyToken || requestedStudyDraftToken ? undefined
      : resolveNativeQuickOrigin(requestedStudyOrigin, nativeQuickStudies.items)
  const nativeOriginTarget = requestedNativeOrigin && requestedStudyOrigin === requestedNativeOrigin.originMessageToken
    ? {kind: 'quick_study' as const, stage: 'questions' as const, threadToken: token, originMessageToken: requestedNativeOrigin.originMessageToken,
      draftToken: requestedNativeOrigin.draftToken, draftRevision: requestedNativeOrigin.revision} : null
  const studyOriginTarget = useMemo(() => requestedStudyDraftToken ? resolveStudyDraftOrigin({
    threadToken: token, draftToken: requestedStudyDraftToken, originMessageToken: requestedStudyOrigin, draftRevision: requestedDraftRevision,
  }, studyAttention.items, threadStudies.items) : resolveStudyOrigin({
    threadToken: token, studyToken: requestedStudyToken, originMessageToken: requestedStudyOrigin,
  }, studyAttention.items, livingArtifactDtos, confirmedFindingsTarget), [
    token, requestedStudyToken, requestedStudyDraftToken, requestedDraftRevision, requestedStudyOrigin, studyAttention.items, livingArtifactDtos, threadStudies.items, confirmedFindingsTarget,
  ])
  // The same saved binding chooses the findings surface, its session label,
  // and its new-tab link. A ready analysis does not imply a full report.
  const findingsStudyTokens = useMemo(() => new Set([
    ...threadStudies.items.filter((draft) => draft.presentation === 'quick' && draft.launchedStudyToken)
      .map((draft) => draft.launchedStudyToken!),
    ...(quickFindingsTarget?.threadToken === token ? [quickFindingsTarget.studyToken] : []),
    ...livingArtifactDtos.filter((artifact) => artifact.kind === 'findings' && artifact.sourceStudyToken && typeof artifact.revision === 'number' && artifact.revision > 0)
      .map((artifact) => artifact.sourceStudyToken!),
  ]), [threadStudies.items, quickFindingsTarget, token, livingArtifactDtos])
  // Only a Quick Study's saved plots print one decimal in the findings panel.
  const quickFindingsStudyTokens = useMemo(() => new Set([
    ...threadStudies.items.filter((draft) => draft.presentation === 'quick' && draft.launchedStudyToken)
      .map((draft) => draft.launchedStudyToken!),
    ...(quickFindingsTarget?.threadToken === token ? [quickFindingsTarget.studyToken] : []),
  ]), [threadStudies.items, quickFindingsTarget, token])
  const originNavigation = useThreadStudyOrigin({
    container: conversationRef,
    threadToken: token,
    requestedOrigin: requestedStudyOrigin,
    requestedStudy: requestedStudyToken || requestedStudyDraftToken || requestedNativeQuick || nativeOriginTarget?.draftToken || '',
    target: nativeOriginTarget ?? studyOriginTarget,
    loading: loading || livingArtifactsLoading || studyAttention.isLoading || threadStudies.isLoading || nativeQuickStudies.isLoading || selectedNativeQuick.isLoading,
    navigationKey: location.key,
    layoutRevision: conversationHeight,
    messageTokens: turns.map(turnMessageToken),
  })
  /** `turnId:callId:size` for every image tile of a streaming turn. */
  const liveImageTileKey = useMemo(() => {
    const keys: Array<string> = []
    for (const turn of turns) {
      if (turn.role !== 'assistant' || !turn.streaming || !turn.imageProgress) {
        continue
      }
      for (const image of Object.values(turn.imageProgress)) {
        keys.push(`${turn.id}:${image.callId}:${image.size ?? ''}`)
      }
    }
    return keys.join('|')
  }, [turns])
  const seenLiveImageTilesRef = useRef<Set<string>>(new Set())
  const runStartAt = resolveWorkflowRunStartAt(run)
  const resolvedOriginatingAskId = originatingCommandUserTurnId(
    turns,
    runStartAt,
  )

  useEffect(() => {
    if (!run) {
      setLatchedStudyAsk(null)
      return
    }
    if (latchedStudyAsk?.runToken === run.token) {
      return
    }
    const turnId = resolvedOriginatingAskId ?? lastUserTurnId(turns)
    if (!turnId) {
      return
    }
    setLatchedStudyAsk({ runToken: run.token, turnId })
  }, [run, resolvedOriginatingAskId, latchedStudyAsk, turns])

  const originatingCommandTurnId =
    run && latchedStudyAsk?.runToken === run.token
      ? latchedStudyAsk.turnId
      : resolvedOriginatingAskId

  /**
   * Room enough under the newest question that it can actually reach the top of
   * the scroller, so a one-line answer parks the same way a long one does.
   *
   * A FLOOR ON THE WHOLE EXCHANGE, never a height on the prose. Carried by the
   * answer alone it stopped being trailing the moment anything rendered after
   * the turn list: the run's busy line, its results and its artifacts are all
   * appended to the reading column, so the floor became a viewport-tall hole
   * wedged between the answer and a run block it had no idea existed. On the
   * group it self-solves — a run reports back, the group grows past the floor,
   * and the padding is simply gone. Nothing here needs to know how tall the run
   * block is, or that there is one.
   *
   * Not always the applied value: a live run whose pin sits above this box
   * suppresses the floor (`appliedExchangeMinHeight`, D6).
   */
  const exchangeMinHeight =
    lastQuestionIndex >= 0 && conversationHeight > 0
      ? Math.max(0, conversationHeight - QUESTION_HEADROOM_PX)
      : 0

  // Park the newest question at the top of the conversation rather than pinning
  // the bottom. A long answer then grows downward into empty space instead of
  // dragging the question off-screen, so the reader keeps what they asked in
  // view while it is being answered. The first park is instant so opening a
  // thread does not animate from the top; later questions still ease in.
  /**
   * Land the reader on the newest thing in the thread.
   *
   * A run's output reads in run order (`groupArtifactsByStep`), so the
   * deliverable is the LAST group, under the working material that produced
   * it. Opening a finished thread at its end is therefore how the reader
   * meets the answer first, and it is what arriving at any conversation
   * normally does.
   */
  function scrollToEnd(behavior: 'smooth' | 'instant') {
    if (originNavigation.holdOriginRef.current) return
    const container = conversationRef.current
    if (!container) return
    const top = container.scrollHeight
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top, behavior })
    } else {
      container.scrollTop = top
    }
  }

  function parkLastQuestion(behavior: 'smooth' | 'instant') {
    if (originNavigation.holdOriginRef.current) return
    const container = conversationRef.current
    const question = lastQuestionRef.current
    if (!container || !question) return
    const offset =
      question.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop
    // The floating header is 56px; leave another 8px above the question.
    const top = Math.max(0, offset - 64)
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top, behavior })
    } else {
      container.scrollTop = top
    }
  }

  /* Declared before every effect that reads the ref, so the value they see
     belongs to the commit they are running in. */
  useLayoutEffect(() => {
    isRunActiveRef.current = isRunActive
  }, [isRunActive])

  useLayoutEffect(() => {
    if (originNavigation.holdOrigin || !lastQuestionId) return
    const isArrival = !didInitialScrollRef.current
    didInitialScrollRef.current = true
    /* Stamped here, not inside `parkLastQuestion`: a re-park must not extend
       its own window, or a width that keeps changing keeps re-parking. */
    lastParkAtRef.current = Date.now()
    if (isArrival) {
      /* Arriving: go to the end. Parking the question at the top is for a
         question asked in this session, where the answer has yet to arrive
         and the reader wants to keep what they asked in view. Someone opening
         a thread that already ran wants its outcome, not its opening line. */
      arrivedAtRef.current = Date.now()
      scrollToEnd('instant')
      return
    }
    if (isRunActiveRef.current) {
      /* A message typed while a run is in flight is not a new question
         waiting on an answer: the run's cards are above it and still
         arriving, and parking the message at the top would hide them (D6).
         Go to the end, where the message and the busy line both are. */
      scrollToEnd('smooth')
      return
    }
    parkLastQuestion('smooth')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastQuestionId, originNavigation.holdOrigin])

  /* The park only guarantees the question and a viewport-tall floor; an image
     tile is taller than the room left under a parked question, so a new turn's
     tile landed behind the composer until the reader scrolled. When a live
     turn's tile first appears (or learns its final size), land on the end the
     same way a message sent mid-run does. Live turns only: a reload has no
     `imageProgress`, so arriving at a thread never triggers this. */
  useLayoutEffect(() => {
    if (originNavigation.holdOrigin || !liveImageTileKey) return
    const seen = seenLiveImageTilesRef.current
    const fresh = liveImageTileKey.split('|').filter((key) => !seen.has(key))
    if (fresh.length === 0) return
    for (const key of fresh) {
      seen.add(key)
    }
    scrollToEnd('smooth')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveImageTileKey])

  /* A step settling mid-run replaces or adds its card several screens down.
     Bring that group into view once it is actually rendered: the signature
     changes on the `:step` event, while the card itself lands on the living
     artifacts fetch the effect above starts, so the pending step id is held
     until a group carrying it exists. The first pass on a live run only
     seeds the signature — opening a thread mid-run must not yank the
     reader. */
  useEffect(() => {
    const state = settledScrollRef.current
    if (originNavigation.holdOrigin) return
    if (!isRunActive) {
      state.signature = null
      state.stepId = null
      return
    }
    if (state.signature !== settledStepRefreshKey) {
      const seenSignature = state.signature
      state.stepId =
        seenSignature === null
          ? null
          : newestSettledStepId(seenSignature, settledStepRefreshKey)
      state.signature = settledStepRefreshKey
    }
    if (!state.stepId) return
    const container = conversationRef.current
    const group = container?.querySelector(
      `[data-step-id="${state.stepId}"]`,
    )
    if (!group || typeof group.scrollIntoView !== 'function') return
    state.stepId = null
    group.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [isRunActive, settledStepRefreshKey, studyPinArtifacts.length])

  /* Living artifacts and run steps land on their own fetches, after the
     arrival scroll has already run against a shorter column. Re-land while
     the arrival is still fresh, on the same window the re-park uses.
     H6: include conversationHeight so this fires once the trailing-padding
     floor is measured (initial scrollToEnd runs before that layout effect
     and reads a short scrollHeight; the first non-zero conversationHeight
     triggers this correction).
     M15: skip if the reader has scrolled away from the end — a yank while
     they are reading the thread is hostile. 40 px slop absorbs momentum /
     fractional-pixel rounding without masking a real user scroll. */
  useLayoutEffect(() => {
    if (originNavigation.holdOrigin || !arrivedAtRef.current) return
    if (dockTweeningRef.current) return
    if (Date.now() - arrivedAtRef.current >= REPARK_AFTER_PARK_MS) return
    const container = conversationRef.current
    if (container) {
      const distanceFromEnd =
        container.scrollHeight - container.scrollTop - container.clientHeight
      if (distanceFromEnd > 40) return
    }
    scrollToEnd('instant')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns.length, studyPinArtifacts.length, run?.steps?.length, conversationHeight])

  // The trailing answer is padded to fill the viewport so scrolling the
  // question to the top is actually possible on short answers too. Layout
  // effect so the first paint already has the height — measuring after paint
  // is the bubble growing from small to large.
  useLayoutEffect(() => {
    const container = conversationRef.current
    if (!container) return
    setConversationHeight(container.clientHeight)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      setConversationHeight(entry.contentRect.height)
      /* A park measures the question BEFORE the layout it lands in: sending a
         command opens the run pane in the same beat, the column narrows, and
         everything above the question reflows taller, so the scroll lands a
         screen short of it. A width change inside the window after a park
         re-parks against the settled layout; an ordinary window resize a
         minute later leaves the reader's scroll alone. */
      const width = entry.contentRect.width
      const previousWidth = lastConversationWidthRef.current
      lastConversationWidthRef.current = width
      if (
        previousWidth !== null &&
        width !== previousWidth &&
        !dockTweeningRef.current &&
        Date.now() - lastParkAtRef.current < REPARK_AFTER_PARK_MS
      ) {
        /* Correcting the same landing the park effect made, so it has to
           land the same way: a live run went to the end, not to the top. */
        if (isRunActiveRef.current) {
          scrollToEnd('smooth')
        } else {
          parkLastQuestion('smooth')
        }
      }
    })
    observer.observe(container)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])


  useEffect(() => {
    // A browser subscription belongs to this view; execution belongs to the server.
    observationMountedRef.current = true
    askingRef.current = false
    setAsking(false)
    activeExecutionRef.current = null
    stopRequestedRef.current = false
    nextContinuationRef.current = null
    return () => {
      observationMountedRef.current = false
      observationEpochRef.current += 1
      const controller = abortRef.current
      abortRef.current = null
      controller?.abort()
    }
  }, [token])

  useEffect(() => {
    suggestionTurnKeyRef.current = suggestionTurnKey
  }, [suggestionTurnKey])

  useEffect(() => {
    if (!token) return
    let cancelled = false
    setSuggestions([])
    setRoomSuggestion(null)
    setRoomOpeningMessages({})
    dispatchSession({ type: 'reset' })
    setSessionTabChosen(false)
    pendingSentRef.current = false
    didInitialScrollRef.current = false
    arrivedAtRef.current = 0
    void (async () => {
      setLoading(true)
      try {
        // Read execution first: if none remains, all canonical reads below
        // necessarily happen after its final persistence.
        const activeExecution = assistantPlatformEnabled ? await assistantTurnsApi.latest(token) : null
        const [threadView, messages, docs, listedAssets] = await Promise.all([
          knowledgeThreadsApi.get(token),
          knowledgeThreadsApi.listMessages(token),
          knowledgeApi.listDocuments().catch(() => []),
          assistantPlatformEnabled
            ? threadAssetsApi.list(token).catch(() => [])
            : Promise.resolve([] as Array<ThreadAssetView>),
        ])
        if (cancelled) return
        setThread(threadView)
        setTitleDraft(threadView.title)
        setScope(
          locationState.scope ||
            threadView.scope || {
              web: true,
              library: true,
              mode: 'all',
              documentTokens: [],
              collectionTokens: [],
            },
        )
        setTurns(
          withRunCommandChip(
            messages.map((message) => {
              const turn = messageToTurn(message)
              const attachments = turnAttachmentsFromAssets(
                listedAssets,
                message.token,
              )
              return attachments ? { ...turn, attachments } : turn
            }),
            run,
            runAudienceChipName,
          ),
        )
        /* D6: the box hydrates from the stored cards of the latest assistant
           message, only when it is the thread's last message (a stopped turn
           ends in a user row and hydrates nothing). Viewers receive null. */
        const lastMessage = messages[messages.length - 1]
        if (
          !askingRef.current &&
          lastMessage?.role === 'assistant' &&
          lastMessage.suggestions &&
          lastMessage.suggestions.length > 0
        ) {
          setSuggestions(lastMessage.suggestions)
          setSuggestionTurnKey(lastMessage.token)
        }
        setThreadAssets(listedAssets)
        const listedFileIds = new Set<string>()
        for (const asset of listedAssets) {
          const fileId = listedAssetFileId(asset)
          if (!fileId) {
            continue
          }
          listedFileIds.add(fileId)
          registeredByFileIdRef.current.set(fileId, asset.token)
        }
        for (const tile of threadAttachmentsRef.current) {
          const fileId = tile.fileId
          if (!fileId || !tile.url) {
            continue
          }
          if (listedFileIds.has(fileId)) {
            registerFailedByFileIdRef.current.delete(fileId)
            continue
          }
          if (
            !registeredByFileIdRef.current.has(fileId) &&
            !inFlightRegisterRef.current.has(fileId) &&
            assistantPlatformEnabled
          ) {
            registerFailedByFileIdRef.current.delete(fileId)
            void registerComposerTile(token, tile)
          }
        }
        // --- Agent C: rehydrate Suggested plan cards from message-attached proposals ---
        for (const message of messages) {
          if (
            message.role !== 'assistant' ||
            !message.plan ||
            !message.plan.cardEligible
          ) {
            continue
          }
          const proposal = planTailToProposal(message.token, message.plan)
          if (proposal) {
            registerProposal(proposal)
          }
        }
        // --- end Agent C ---
        setDocuments(docs)
        if (activeExecution && (activeExecution.status !== 'completed' || activeExecution.continuation?.status === 'pending')) {
          void resumeBackgroundTurn(activeExecution)
        }
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? t('threads.detail.toast.threadNotFound')
        toast.error(message)
        navigate('/threads', { replace: true })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // Reload when the assistant flag hydrates so GET-assets is not skipped forever.
  }, [token, assistantPlatformEnabled])

  /**
   * D1's hand-off lands here: an off-thread composer seeds `ArmedWorkflowState`
   * and navigates with `{ pendingMessage, pendingEffort, scope }`, and this is
   * what actually starts the run (or opens intake) — never the composer.
   *
   * Readiness gate: with no armed slug, the ordinary ask is always ready the
   * moment loading clears. With an armed slug, this WAITS for the command
   * table (`isIntakeArmed` reads it) and for the armed template to either
   * resolve or fail — never dispatches on a read that is still in flight,
   * which would misroute an intake-armed command to `runAsk` before its
   * `arms` marker has loaded.
   *
   * Dispatch keys on the COMMAND TABLE, not the template record:
   * `isIntakeArmed` → `runIntakeTurn`; a resolved `armedWorkflow` (and not
   * intake-armed) → `startArmedWorkflow`; a failed template read → toast,
   * disarm, then `runAsk` so the message is never stranded; no arm at all →
   * `runAsk`. `pendingSentRef` latches ONLY once one of those actually fires,
   * so a still-loading command list or template read gets another pass at
   * the next render instead of silently dropping the message.
   *
   * An empty `pendingMessage` with an armed intake still opens intake
   * (`runIntakeTurn('')` is a valid scoping opener); an empty message with a
   * non-intake arm still starts the run with its own `inputs`. With NO arm at
   * all and no message, there is nothing to dispatch.
   */
  useEffect(() => {
    if (loading || !token || pendingSentRef.current) return
    const armedSlug = armedWorkflowSlug
    const isReady =
      armedSlug === null ||
      (!threadCommandsLoading &&
        (isIntakeArmed || armedWorkflow !== null || armedTemplateError))
    if (!isReady) return

    const pending = locationState.pendingMessage?.trim() ?? ''
    if (!pending && !(locationState.pendingIntakeOpen && isIntakeArmed)) {
      /* Nothing handed off from Home Lab's composer. A workflow armed with
         no accompanying message (e.g. picked straight from this page's own
         WorkflowMenu, never routed through the Home Lab hand-off) is not
         this effect's concern — the reader's own composer send is what
         starts it, same as any other armed workflow. Calling
         `startArmedWorkflow('')`/`runIntakeTurn('')` here would silently
         start a run with empty input the instant arming finishes, racing
         ahead of whatever the reader is about to type. Only a broken arm
         still needs cleanup with no pending text to fall back to. */
      if (armedTemplateError && !isCanonicalQuickStudyArmed) {
        pendingSentRef.current = true
        toast.error(t('workflows.run.couldNotStart'))
        clearArmedWorkflow()
      }
      return
    }

    pendingSentRef.current = true
    /* Frontend review SHOULD FIX 2: honour the effort the home composer already picked,
       carried as `pendingEffort` (mirrors `pendingMessage`, `useHomeLabSubmit.ts`).
       Omitted rather than falsy-checked against a default, so a hand-off that never set
       one (every pre-existing caller) leaves this page's own Medium default untouched. */
    if (locationState.pendingEffort) {
      setEffort(locationState.pendingEffort)
    }
    if (locationState.pendingProfile) {
      turnProfileRef.current = locationState.pendingProfile
      assistantProfile.setProfileId(locationState.pendingProfile)
    }
    navigate(location.pathname, { replace: true, state: {} })

    const dispatchPending = () => {
      if (isIntakeArmed) {
        void runIntakeTurn(pending)
      } else if (armedWorkflow) {
        void startArmedWorkflow(pending)
      } else if (armedTemplateError) {
        toast.error(t('workflows.run.couldNotStart'))
        clearArmedWorkflow()
        void runAsk(pending)
      } else {
        void runAsk(pending)
      }
    }

    /* The home `+` tiles (`pendingStimuli.ts`), already hosted. They land in this
       composer and are registered BEFORE the first turn, so the turn carries their
       asset tokens exactly as if they had been attached here. */
    const handedOffStimuli = takeHomeStimuli(token)
    if (handedOffStimuli.length === 0) {
      dispatchPending()
      return
    }
    threadAttachmentsRef.current = handedOffStimuli
    setThreadAttachments(handedOffStimuli)
    void Promise.all(
      handedOffStimuli.map((tile) => registerComposerTile(token, tile)),
    ).then(() => {
      /* The ref is rewritten from state on every render, and a render that
         runs before the state update above lands writes the empty composer
         back into it. Re-assert the hand-off so the first turn reads it. */
      threadAttachmentsRef.current = handedOffStimuli
      dispatchPending()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    token,
    armedWorkflowSlug,
    threadCommandsLoading,
    isIntakeArmed,
    armedWorkflow,
    armedTemplateError,
  ])

  async function handleWrongPage(citation: KnowledgeAskCitation) {
    if (!citation.citationToken) return
    try {
      await knowledgeApi.reportWrongPage(citation.citationToken)
      toast.success(t('chatChrome.wrongPageLogged'))
    } catch {
      toast.error(t('chatChrome.wrongPageReportFailed'))
    }
  }

  function handleStop() {
    const controller = abortRef.current
    if (!controller || controller.signal.aborted) return
    stopRequestedRef.current = true
    const execution = activeExecutionRef.current
    if (!execution) return // Acceptance may still be in flight; cancel once acknowledged.
    // Acknowledging Stop is only a durable request. Keep observing until
    // the worker actually stops (or commits a result that beat the request).
    void assistantTurnsApi.cancel(execution.threadToken, execution.token).then(() => nativeQuickStudies.refresh()).catch(() => toast.error('Could not stop the assistant. Try Stop again.'))
  }

  /* An armed workflow never reaches `runAsk`: it is not a question for the
     thread's assistant, it is a run to start. Same split `HomeLabStage` makes,
     through the same hook, so a workflow starts one way on both composers.

     THREE DESTINATIONS NOW:

     1. A gate holding a revision handle takes the message. The handle is
        already the whole test — the gate publishes null the moment a typed
        message is not a revision — so this is one branch, not three.
     2. The persisted built-in Quick Study identity or a typed intake command
        sends to intake; any other explicit workflow starts, unchanged.
     3. Everything else is an ordinary question. */
  function handleComposerSend(text: string) {
    if (creativeContext.saving) return
    if (gateRevisionHandle && isPlainGateContinue(text)) {
      setTurns((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'user' as const,
          text: text.trim(),
          createdAt: new Date().toISOString(),
        },
      ])
      gateRevisionHandle.approve()
      return
    }
    if (gateRevisionHandle) {
      /* D6: collapse the dock on send, docked placement only — a revisable
         gate rendered inline in the thread has nothing to collapse. */
      if (docksGateOnComposer) {
        setGateCollapseSignal((count) => count + 1)
      }
      void runIntakeTurn(text, gateRevisionHandle)
      return
    }
    /* An armed skill tag: one ordinary turn carrying that skill, no intake
       and no run. It yields to a revisable gate above, so a revision is never
       sent as a tagged turn. `runAsk` disarms it only once the turn is
       claimed, so a send its guards refuse keeps the tag armed for the next
       one. */
    if (armedSkillCommand) {
      void runAsk(text, {
        stepSkillSlug: armedSkillCommand.slug,
        disarmsSkillTag: true,
      })
      return
    }
    /* Intake is tested BEFORE the template, not inside it: `/report` is a
       native command with no template record at all, so gating the intake
       branch on a resolved `armedWorkflow` would drop a report follow-up into
       the ordinary assistant. `/test` always has one, so this reads the same
       for it either way. */
    if (isIntakeArmed) {
      void runIntakeTurn(text)
      return
    }
    if (armedWorkflow) {
      /* LOCKED while a run is live: the server would refuse a second run with
         its one-active-run 409 anyway, so the message falls through to the
         assistant instead of into a refusal. */
      if (!isRunActive) {
        void startArmedWorkflow(text)
        return
      }
    }
    /* D6: a docked gate that is not chat-revisable (not the viewer's run, or
       not editable) publishes no `gateRevisionHandle`, so an ordinary question
       reaches here while it is still on screen. Collapse it the same way. */
    if (docksGateOnComposer) {
      setGateCollapseSignal((count) => count + 1)
    }
    void runAsk(text)
  }

  function latestUserAsk(): string {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i].role === 'user' && turns[i].text.trim()) {
        return turns[i].text.trim()
      }
    }
    return ''
  }

  /** Plan Start, one-step plan tail, and `done.preparedRun` all share this. */
  function seedPlanWorkflowStart(
    messageId: string,
    slug: string,
    rest = '',
    audience = '',
    audienceHint = '',
    segment?: {
      populationFilter?: ThreadCommandSeed['populationFilter']
      segmentLabel?: string
    },
  ) {
    planRunMessageIdRef.current = messageId
    setCommandSeed((current) =>
      buildPlanWorkflowCommandSeed(
        slug,
        current?.nonce ?? 0,
        rest,
        audience,
        audienceHint,
        segment,
      ),
    )
  }

  function applyPlanStepHandoff(
    messageId: string,
    handoff: ThreadPlanStepHandoff | null,
  ) {
    if (!handoff) {
      return
    }
    if (handoff.kind === 'workflow') {
      const proposing = turns.find((turn) => turn.id === messageId)
      seedPlanWorkflowStart(
        messageId,
        handoff.slug,
        latestUserAsk(),
        handoff.audience,
        proposing?.text ?? '',
      )
      return
    }
    void runAsk(handoff.intent, {
      stepSkillSlug: handoff.slug,
      planMessageId: messageId,
    })
  }

  /**
   * Command menu started a real run (report / slack / lobby sweep). Adopt it
   * and, when the Start came from a plan card, mark that workflow step running.
   */
  function handleCommandRunStarted(
    started: Parameters<typeof adoptRun>[0],
  ) {
    clearThreadComposerAttachments()
    adoptRun(started)
    const planMessageId = planRunMessageIdRef.current
    if (planMessageId) {
      markCurrentRunning(planMessageId)
    }
  }

  /** The workflow menu's door: a slug and nothing else to carry. */
  function handleArmSlug(slug: string) {
    armedProceedWithoutStimulusRef.current = false
    setBlockedArmedAsk(null)
    setReferencesStimulus(false)
    stimulusReferenceCacheRef.current = null
    armSlug(slug)
  }

  /**
   * ARM `/report` and open its scoping beat in one move.
   *
   * Unlike `/test`, a report command collects nothing: there is no audience to
   * pick and no ask the reader has to write first, so the menu's confirm
   * screen would be an empty card. Arming and asking are therefore the same
   * gesture, and the opener is whatever followed the typed name (`/report just
   * the pricing question`) or an empty message, which the scoping prompt reads
   * as "tell me what this thread established".
   *
   * The kind is passed EXPLICITLY rather than read back off `armedCommand`:
   * `armSlug` has not landed in state yet on this tick, and a scoping turn
   * that sent the twin-test prompt would ask about audiences.
   */
  function handleArmReportCommand(command: ThreadCommand, rest: string) {
    handleArmSlug(command.slug)
    setIntakeStartUiDismissed(false)
    void runIntakeTurn(rest, undefined, { kind: 'report' })
  }

  /**
   * A skill tag (D2). Typed with an ask (`/desk-research what changed`), it
   * sends that ask now as an ordinary turn carrying the one skill. Picked with
   * nothing typed, it arms the composer instead and clears any armed
   * workflow; the next send carries the skill. Never intake, never a run.
   */
  function handleArmSkillCommand(command: ThreadCommand, rest: string) {
    const ask = rest.trim()
    if (ask) {
      void runAsk(ask, { stepSkillSlug: command.slug })
      return
    }
    if (armedWorkflowSlug !== null) {
      handleClearArmedWorkflow()
    }
    setArmedSkillCommand(command)
  }

  /**
   * ARM the composer from the command menu's confirm screen (`/test`).
   *
   * Both halves are kept: the template, because the lobby lookup cannot see a
   * thread action, and the confirm screen's answers, because the audience the
   * reader picked there is what the run will eventually carry.
   *
   * Confirm never drafts questions. A seeded sentence (`/test how likely…`
   * or a plan `rest`) is dropped into the composer so the reader can edit
   * it; send is what starts intake. An empty arm waits on the placeholder.
   *
   * When Start came from a Suggested plan, mark that workflow step running —
   * twin spend still waits for intake + ThreadRunGate.
   */
  function handleArmCommand(
    template: WorkflowTemplate,
    inputs: Record<string, string | null>,
    armDetails?: { audienceName: string | null },
  ) {
    setAudiencesPrelatched(true)
    /* A fresh arm answers the media gate from scratch: whatever a previous
       arm resolved does not carry into this one. */
    armedProceedWithoutStimulusRef.current = false
    setBlockedArmedAsk(null)
    setReferencesStimulus(false)
    stimulusReferenceCacheRef.current = null
    armCommand(template, inputs, armDetails?.audienceName ?? null)

    const planMessageId = planRunMessageIdRef.current
    if (planMessageId) {
      markCurrentRunning(planMessageId)
    }

    const readerAsk = preparedReaderAsk(
      template,
      inputs,
      pendingPreparedReaderAskRef.current,
    )
    pendingPreparedReaderAskRef.current = null
    const seededAsk = readerAsk ?? inputs[askInputKey(template) ?? '']
    if (typeof seededAsk === 'string' && seededAsk.trim().length > 0) {
      setComposerAskSeed((current) => ({
        text: seededAsk.trim(),
        nonce: (current?.nonce ?? 0) + 1,
      }))
    }
  }

  /**
   * Chip dismiss: drop the arm only. The org-wide remembered audience is NOT
   * erased here (plan 2026-09-14-twin-test-bound-audience, C2/D5): dismissing
   * the armed chip is a per-run undo, not an instruction to forget the
   * audience the org keeps reaching for. Forgetting is now a deliberate act
   * on the confirm card's own checkbox (C3) — see `ThreadCommandMenu`'s
   * `onForgetRememberedAudience`.
   */
  function handleClearArmedWorkflow() {
    armedProceedWithoutStimulusRef.current = false
    setBlockedArmedAsk(null)
    setIntakeStartUiDismissed(false)
    setReferencesStimulus(false)
    stimulusReferenceCacheRef.current = null
    clearArmedWorkflow()
  }

  /**
   * Start the run this conversation settled on.
   *
   * EXPLICIT inputs, never the hook's message mapping: the ask is the sentence
   * intake reported as the direction, verbatim, and the audience is the one
   * the confirm screen picked. Mapping a message here would hand the run
   * whatever happened to be in the textarea instead of the thing both sides
   * agreed to.
   *
   * A refusal — the one-active-run 409 above all, already toasted by the hook —
   * resolves null and leaves the composer ARMED with the direction intact, so
   * the conversation survives and the reader can try again once the other run
   * finishes. Losing it would mean paying for the whole intake a second time.
   */
  async function isStimulusGateSatisfiedAtRunStart(params: {
    template: WorkflowTemplate
    inputs: Record<string, string | null>
    ask: string
    proceedWithoutStimulus: boolean
  }): Promise<boolean> {
    const hasAttachedStimulus = params.template.inputs.some(
      (spec) =>
        spec.type === 'stimulus' &&
        hasBoundStimulusValue(params.inputs[spec.key]),
    )
    const cacheKey = `${hasAttachedStimulus}\u0000${params.ask.trim()}`
    let resolvedReferencesStimulus = false

    if (stimulusReferenceCacheRef.current?.key === cacheKey) {
      resolvedReferencesStimulus =
        stimulusReferenceCacheRef.current.referencesStimulus
    } else {
      try {
        const result = await knowledgeApi.classifyStimulusReference({
          ask: params.ask,
          hasAttachedStimulus,
        })
        resolvedReferencesStimulus = result.referencesStimulus === true
      } catch {
        /* The gate fails open. A classifier outage cannot prevent a run. */
        resolvedReferencesStimulus = false
      }
      stimulusReferenceCacheRef.current = {
        key: cacheKey,
        referencesStimulus: resolvedReferencesStimulus,
      }
    }

    setReferencesStimulus(resolvedReferencesStimulus)
    return hasStimulusGateSatisfied(
      params.template.inputs,
      params.inputs,
      params.proceedWithoutStimulus,
      resolvedReferencesStimulus,
    )
  }

  async function confirmIntakeDirection() {
    if (!token || !intakeDirection || !settledAudience) {
      return
    }
    setIsConfirmingIntake(true)
    try {
      await confirmIntakeDirectionInner()
    } finally {
      setIsConfirmingIntake(false)
    }
  }

  async function confirmIntakeDirectionInner() {
    if (!token || !intakeDirection || !settledAudience) {
      return
    }

    /* D12: a settled REPORT brief is written, never run. No `workflowRunsApi`,
       no template, no spend gate — the brief becomes the message of one
       report-mode assistant turn. Disarm first so the "Keep talking" row and
       the armed chip come down as the writing starts; the turn owns the thread
       from here. */
    if (isReportIntakeArmed) {
      /* Read off the armed state BEFORE the disarm below wipes it. Everything
         the settled scope carries is collected here, so a second field on the
         tail is one more line at this seam and nothing else. */
      const scope: SettledReportScope = { brief: intakeDirection }
      setShowIntakeRetry(false)
      clearArmedWorkflow()
      await runReportTurn(scope)
      return
    }

    if (isCanonicalQuickStudyArmed) {
      // Old direction state has no verified message origin. Preserve its literal
      // brief for an explicit new send; never start the retired per-question run.
      setComposerAskSeed((current) => ({text: intakeDirection, nonce: (current?.nonce ?? 0) + 1}))
      setIntakeDirection(null)
      setShowIntakeRetry(false)
      toast.info(t('threads.quickStudy.resumeBrief'))
      return
    }

    if (!armedWorkflow) {
      return
    }

    const askKey = askInputKey(armedWorkflow)
    const runInputs = {
      ...armedInputs,
      ...(askKey ? { [askKey]: intakeDirection } : {}),
    }
    if (
      !(await isStimulusGateSatisfiedAtRunStart({
        template: armedWorkflow,
        inputs: runInputs,
        ask: intakeDirection,
        proceedWithoutStimulus: armedProceedWithoutStimulusRef.current,
      }))
    ) {
      return
    }
    const startedRun = await startWorkflowRun({
      template: armedWorkflow,
      threadToken: token,
      inputs: runInputs,
      /* Intake is the other door to the same place: a command armed from the
         menu (`/test`) is as much a thread action as the armed start below
         tags itself, so the provenance on the run matches whichever door the
         reader came through. */
      ...(armedCommand ? { trigger: 'thread_action' as const } : {}),
      ...workflowRunSegmentFieldsForSlug(commandSeed, armedWorkflow.slug),
    })
    if (startedRun) {
      setShowIntakeRetry(false)
      armedProceedWithoutStimulusRef.current = false
      clearThreadComposerAttachments()
      adoptRun(startedRun)
      clearArmedWorkflow()
    } else {
      setShowIntakeRetry(true)
    }
  }

  /**
   * THE REPORT TURN: the settled brief, written up.
   *
   * The same endpoint every other turn on this page uses, in `mode: 'report'`
   * (`docs/plans/2026-09-03-assistant-report-mode.md`, D1). Three things make
   * it its own function rather than a flag on `runAsk`:
   *
   * 1. The settled brief IS the user turn, exactly as `/test` renders its own
   *    settled direction. Nest stores it as a `role: 'user'` row on this same
   *    request (`knowledge.service.ts`, the `if (trimmed)` insert), so a page
   *    that skipped it read one bubble short until the next reload — the live
   *    column and the reloaded one disagreed about what the reader agreed to.
   * 2. No `delta` frames arrive (D3). The draft is buffered so the grounding
   *    gate sees all of it, and what streams meanwhile is stage frames in the
   *    ordinary `tool_start` / `tool_end` shape, which the activity line
   *    already renders. An empty `streamed` is therefore normal here.
   * 3. `done.text` is a NOTICE, not the report. The report is `done.report`,
   *    which Nest has already persisted as a living artifact by the time this
   *    frame lands, so the only thing left to do is refetch the library.
   *
   * A refusal (D13's empty thread, 400) is shown with the server's own words:
   * the sentence IS the product copy for that case.
   */
  /**
   * The ONE busy guard the three streaming loops share (`runReportTurn`,
   * `runIntakeTurn`, `runAsk`). The ref is the authority; `asking` is
   * render-only.
   *
   * Two sends in the same tick both read the same stale `asking === false`,
   * both insert their turns, and the second `abortRef.current?.abort()` kills
   * the first stream — which then finishes as "Stopped" for no reason a
   * reader can see. Claiming a ref leaves no gap between the check and the
   * claim, so the second send is simply refused.
   *
   * `endTurn()` in `finally`, always. The intake→ask hand-off then works by
   * construction rather than by `runAsk`'s closure having captured `asking`
   * from an earlier render.
   */
  function beginTurn(): boolean {
    if (askingRef.current) {
      return false
    }
    originNavigation.releaseOrigin()
    askingRef.current = true
    setSuggestions([])
    setSuggestionTurnKey(newId())
    setAsking(true)
    return true
  }

  function endTurn() {
    askingRef.current = false
    setAsking(false)
    if (stopRequestedRef.current) void nativeQuickStudies.refresh()
  }

  async function runReportTurn(scope: SettledReportScope) {
    const trimmed = scope.brief.trim()
    if (!token || !trimmed || !beginTurn()) {
      return
    }

    const assistantId = newId()
    /* A12: the agreed brief is not a user bubble. It rides the request with
       `skipUserMessage` (Nest stores no user row and keeps the brief on the
       assistant row as `reportBrief`), so live and reload agree. */
    setTurns((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant' as const, text: '', streaming: true },
    ])

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    activeExecutionRef.current = null
    stopRequestedRef.current = false
    nextContinuationRef.current = null

    try {
      let streamed = ''
      let doneFrame: AssistantTurnDoneFrame | null = null

      if (!isCurrentObservation(controller)) return
      for await (const frame of assistantTurnStream(token, trimmed, {
        ...(creativeContext.revision !== undefined ? { creativeContextRevision: creativeContext.revision } : {}),
        signal: controller.signal,
        onAccepted: (snapshot) => {
          if (isCurrentObservation(controller)) acceptedExecution(snapshot)
        },
        onSnapshot: (snapshot) => {
          if (isCurrentObservation(controller)) notePausedContinuation(snapshot)
        },
        mode: 'report',
        skipUserMessage: true,
      })) {
        if (!isCurrentObservation(controller)) return
        if (frame.type === 'delta') {
          /* Not expected under D3, and still handled: a build that starts
             streaming the draft must not render as an empty bubble. */
          streamed += frame.text
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? { ...t, text: streamed, streaming: true }
                : t,
            ),
          )
        } else if (frame.type === 'tool_start' || frame.type === 'tool_end') {
          /* Report stages and tools share the checklist's one reducer. */
          const activityFrame = frame
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    activityLegs: applyActivityFrame(
                      t.activityLegs ?? [],
                      activityFrame,
                    ),
                  }
                : t,
            ),
          )
        } else if (frame.type === 'done') {
          doneFrame = frame
        } else if (frame.type === 'error') {
          throw createAssistantStreamFailure(frame)
        }
      }

      if (!isCurrentObservation(controller)) return
      const done = doneFrame
      if (!done) {
        throw new Error('The assistant stopped before finishing that turn.')
      }

      const notice = done.text.trim() || streamed.trim()
      const assistantMessageToken = done.assistantMessageToken
      const reportLink = reportLinkFromDone(done)
      /* `turn`, not `t`: the i18n `t` is in scope here and the map parameter
         everywhere else on this page is named for the shadowing it would
         otherwise cause. */
      setTurns((prev) =>
        prev.map((turn) =>
          turn.id === assistantId
            ? {
                ...turn,
                text: notice || t('threads.detail.command.report.ready'),
                streaming: false,
                refused: done.refused,
                ...(assistantMessageToken
                  ? { serverToken: assistantMessageToken }
                  : {}),
                ...(reportLink ? { report: reportLink, reportBrief: trimmed } : {}),
              }
            : turn,
        ),
      )

      /* The card comes from the library, not from this frame: Nest wrote the
         artifact before it sent `done`, so one refetch is what puts it on
         screen without a reload. No tail means nothing was written and there
         is nothing to refetch. */
      if (done.report?.markdown) {
        await refreshLivingArtifacts()
      }

      const refreshed = await knowledgeThreadsApi.get(token)
      if (!isCurrentObservation(controller)) return
      setThread(refreshed)
      setTitleDraft(refreshed.title)
    } catch (error: unknown) {
      if (!isCurrentObservation(controller)) return
      if (['assistant_concurrency_limit_reached', 'assistant_thread_busy'].includes((error as { code?: string }).code ?? '')) {
        setTurns((prev) => prev.filter((turn) => turn.id !== assistantId))
        setComposerAskSeed((current) => ({ text: trimmed, nonce: (current?.nonce ?? 0) + 1 }))
        toast.error((error as Error).message)
        return
      }
      if (controller.signal.aborted || ((error as { name?: string } | null)?.name === 'AbortError')) {
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantId
              ? {
                  ...t,
                  text: t.text.trim()
                    ? t.text
                    : 'Stopped before the answer started.',
                  streaming: false,
                  stopped: true,
                }
              : t,
          ),
        )
        return
      }
      const failed = t('threads.detail.command.report.failed')
      toast.error(reportTurnErrorMessage(error, failed))
      setTurns((prev) =>
        prev.map((turn) =>
          turn.id === assistantId
            ? { ...turn, text: failed, streaming: false, refused: true }
            : turn,
        ),
      )
    } finally {
      await finishObservation(controller)
    }
  }

  /** What a finished approval saves live: its try, and what it rendered from while this tab held it. */
  function approvalBrand(approval: { requestId: string; step?: 'first' | 'try' | 'rest'; tries?: number; selection?: CreativeContextSelection; quoteBrandToken?: string | null }): ImageApprovalSaved {
    return { ...approvalTry(approval), brandToken: approval.selection ? approval.selection.brandProfileToken ?? null : approval.quoteBrandToken ?? null,
      render: imageApprovalRenderRef.current[approval.requestId] ?? null }
  }

  function updateImageRequestExecution(snapshot: AssistantTurnSnapshot) {
    if (!snapshot.imageApprovalRequestId) return
    imageApprovalRenderRef.current[snapshot.imageApprovalRequestId] = snapshot.imageApprovalSummary && snapshot.imageApprovalPurpose
      ? { summary: snapshot.imageApprovalSummary, purpose: snapshot.imageApprovalPurpose } : null
    // A turn that ended holding its image made it: the image was charged, whatever ended the turn.
    const status = snapshot.imageApprovalUnstarted ? null : snapshot.imageApprovalMade ? 'completed' : snapshot.status
    setImageRequestStatus(snapshot.imageApprovalRequestId, status, snapshot.imageApprovalFormatId,
      snapshot.imageApprovalUnstarted
        ? { tryNumber: snapshot.imageApprovalTry, step: snapshot.imageApprovalStep }
        : { quality: snapshot.imageApprovalQuality, formats: snapshot.imageApprovalFormats, tryNumber: snapshot.imageApprovalTry, step: snapshot.imageApprovalStep, render: imageApprovalRenderRef.current[snapshot.imageApprovalRequestId] })
  }

  function setImageRequestStatus(requestId: string, status: AssistantTurnSnapshot['status'] | null, formatId?: string, saved?: ImageApprovalSaved) {
    setTurns((prev) => prev.map((turn) => turn.imageRequests?.some((item) => item.requestId === requestId)
      ? { ...turn, imageRequests: turn.imageRequests.map((item) => item.requestId === requestId
          ? withImageRequestStatus(item, status, formatId, saved) : item) }
      : turn))
  }

  /** Restore the server-owned execution after navigation, without submitting again. */
  async function resumeBackgroundTurn(initial: AssistantTurnSnapshot) {
    if (observedThreadRef.current !== initial.threadToken || askingRef.current) return
    const controller = new AbortController()
    abortRef.current = controller
    stopRequestedRef.current = false
    nextContinuationRef.current = null
    acceptedExecution(initial)
    beginTurn()
    const assistantId = initial.status === 'completed' && initial.assistantMessageToken
      ? initial.assistantMessageToken : `background:${initial.token}`
    setTurns((prev) => {
      const userPresent = !initial.message || prev.some((turn) =>
        turn.id === initial.userMessageToken || turn.serverToken === initial.userMessageToken)
      return [
        ...prev,
        ...(!userPresent ? [{ id: initial.userMessageToken ?? `user:${initial.token}`, role: 'user' as const, text: initial.message }] : []),
        ...(!prev.some((turn) => turn.id === assistantId || turn.serverToken === assistantId)
          ? [{ id: assistantId, role: 'assistant' as const, text: '', streaming: true }] : []),
      ]
    })
    try {
      for await (const snapshot of observeAssistantTurnSnapshots(initial.threadToken, initial, controller.signal)) {
        if (!isCurrentObservation(controller)) return
        notePausedContinuation(snapshot)
        updateImageRequestExecution(snapshot)
        let projected: LocalTurn = { id: assistantId, role: 'assistant', text: '', streaming: isAssistantTurnActive(snapshot), imageApprovalRequestId: snapshot.imageApprovalRequestId, imageApprovalFormatId: snapshot.imageApprovalFormatId }
        let done: AssistantTurnDoneFrame | null = null
        for (const raw of snapshot.events) {
          for (const frame of normalizeAssistantTurnFrame(raw)) {
            if (frame.type === 'delta') projected.text += frame.text
            else if (frame.type === 'image_progress') {
              projected.imageProgress = applyImageProgressFrame(projected.imageProgress, frame)
              if (frame.stage === 'done' && frame.asset) {
                const asset = frame.asset
                setThreadAssets((prev) => prev.some((item) => item.token === asset.token) ? prev : [...prev, asset])
              }
            } else if (frame.type === 'tool_start' || frame.type === 'tool_end') {
              projected.activityLegs = applyActivityFrame(projected.activityLegs ?? [], frame)
            } else if (frame.type === 'citations') {
              const partitioned = partitionAssistantCitations(frame.items)
              projected.citations = partitioned.documentCitations
              projected.webCitations = partitioned.webCitations
            } else if (frame.type === 'judgment') projected.judgmentNotes = [...(projected.judgmentNotes ?? []), frame.note]
            else if (frame.type === 'suggestions') setSuggestions(frame.suggestions)
            else if (frame.type === 'done') done = frame
          }
        }
        if (done && snapshot.status === 'completed') projected = completedAssistantTurn(projected, done, projected.text, projected.judgmentNotes)
        if (snapshot.imageApprovalMade) {
          projected.streaming = false
          projected.imageProgress = stopPendingImages(projected.imageProgress)
        } else if (snapshot.status === 'failed' || snapshot.status === 'cancelled') {
          projected.streaming = false
          projected.stopped = snapshot.status === 'cancelled'
          projected.refused = snapshot.status === 'failed'
          const reason = snapshot.status === 'failed'
            ? assistantTurnFailureText(createAssistantStreamFailure({ code: snapshot.error?.code }), t)
            : 'Stopped before the answer finished.'
          projected.text = [projected.text.trim(), reason].filter(Boolean).join('\n\n')
          projected.imageProgress = stopPendingImages(projected.imageProgress)
        }
        setTurns((prev) => prev.map((turn) => turn.id === assistantId ? projected : turn))
        if (isAssistantTurnActive(snapshot) || snapshot.continuation?.status === 'pending') continue
        const [messages, assets, refreshed] = await Promise.all([
          knowledgeThreadsApi.listMessages(initial.threadToken),
          threadAssetsApi.list(initial.threadToken),
          knowledgeThreadsApi.get(initial.threadToken),
        ])
        if (!isCurrentObservation(controller)) return
        if (snapshot.assistantMessageToken && messages.some((message) => message.token === snapshot.assistantMessageToken)) {
          projected.text = snapshot.status === 'failed'
            ? assistantTurnFailureText(createAssistantStreamFailure({ code: snapshot.error?.code }), t)
            : 'Stopped before the answer finished.'
        }
        setThreadAssets(assets)
        setThread(refreshed)
        setTitleDraft(refreshed.title)
        setTurns(withRunCommandChip<LocalTurn>(messages.map((message) => {
          const turn = messageToTurn(message)
          const attachments = turnAttachmentsFromAssets(assets, message.token)
          return attachments ? { ...turn, attachments } : turn
        }), run, runAudienceChipName).concat(snapshot.status === 'completed' || snapshot.imageApprovalMade ? [] : [projected]))
        for (const message of messages) {
          if (message.role === 'assistant' && message.plan?.cardEligible) {
            const proposal = planTailToProposal(message.token, message.plan)
            if (proposal) registerProposal(proposal)
          }
        }
        const latest = messages[messages.length - 1]
        if (latest?.role === 'assistant') {
          setSuggestions(latest.suggestions ?? [])
          setSuggestionTurnKey(latest.token)
        }
        if (done && snapshot.status === 'completed') {
          setContextUsage(done.contextUsage)
          setHistoryTruncated(done.truncated)
          if (done.preparedQuickStudy) {
            clearArmedWorkflow(); setIntakeDirection(null); setShowIntakeRetry(false); await nativeQuickStudies.refresh()
          } else if (done.preparedStudy?.presentation === 'quick') {
            clearArmedWorkflow()
            setIntakeDirection(null)
            setShowIntakeRetry(false)
            if (authUser?.id) await threadStudies.refresh()
          } else if (done.intake?.status === 'direction') setIntakeDirection(done.intake.ask?.trim() || null)
        }
        await refreshAfterTurn()
        if (isCurrentObservation(controller)) void refreshLivingArtifacts()
      }
    } catch (error) {
      if (!isCurrentObservation(controller)) return
      toast.error(error instanceof Error ? error.message : 'Could not reconnect to the assistant.')
      // Keep the partial answer visible; execution may still be running on the server.
      setTurns((prev) => prev.map((turn) => turn.id === assistantId ? { ...turn, streaming: false } : turn))
    } finally {
      await finishObservation(controller)
    }
  }

  const confirmIntakeDirectionRef = useRef(confirmIntakeDirection)
  confirmIntakeDirectionRef.current = confirmIntakeDirection
  const firedIntakeStartKeyRef = useRef<string | null>(null)

  function handleKeepTalking() {
    setIntakeStartUiDismissed(true)
    setIntakeDirection(null)
    setShowIntakeRetry(false)
    firedIntakeStartKeyRef.current = null
  }

  function handleArmedProceedWithoutMedia() {
    if (!blockedArmedAsk) {
      return
    }
    armedProceedWithoutStimulusRef.current = true
    void startArmedWorkflow(blockedArmedAsk)
  }

  /**
   * Latch before the request: a failed start (including one-active-run 409)
   * must not re-enter on the same "direction + audience exist" predicate.
   * Retry is a human click and calls `confirmIntakeDirection` without
   * clearing the latch.
   */
  useEffect(() => {
    if (!intakeStartKey) {
      firedIntakeStartKeyRef.current = null
    }
    if (!intakeStartKey || latchedStartKey !== intakeStartKey) {
      setShowIntakeRetry(false)
    }
  }, [intakeStartKey, latchedStartKey])

  useEffect(() => {
    setShowIntakeRetry(
      Boolean(loadArmedWorkflowState(token).state.latchedStartKey),
    )
  }, [token])

  useEffect(() => {
    if (!canAutoStartIntake || !intakeStartKey || isIntakeStartLatched) {
      return
    }
    if (firedIntakeStartKeyRef.current === intakeStartKey) {
      return
    }
    firedIntakeStartKeyRef.current = intakeStartKey
    latchIntakeStart(intakeStartKey)
    void confirmIntakeDirectionRef.current()
  }, [
    canAutoStartIntake,
    intakeStartKey,
    isIntakeStartLatched,
    latchIntakeStart,
  ])

  /**
   * ONE INTAKE TURN: the same endpoint every other turn on this page uses, in
   * `intake` mode.
   *
   * It renders as an ordinary exchange — the reader's message, then the
   * agent's prose — because that is what it is: the plan makes clarify
   * exchanges real thread turns, persisted, visible and metered, rather than
   * chrome bolted to the composer. The only thing the mode adds is the
   * terminal frame's tail, which says whether the conversation is still going
   * (`need_more`, and the composer stays armed) or has settled on a direction
   * (the confirm affordance appears).
   *
   * A missing tail is `need_more` by omission, which is the safe degrade: the
   * conversation continues and nothing spends.
   *
   * TWO PHASES, one turn. `scoping` is the conversation before a run exists.
   * `revision` is the same turn with an open gate's staged questions attached,
   * and the gate — not this page — decides what comes back is worth staging.
   * The handle is passed in rather than read from state at the end, because a
   * turn OUTLIVES the gate that started it: the captured copy is what turns a
   * gate that moved on into a notice instead of silence.
   *
   * The context ring is deliberately NOT fed from here. Intake runs on a fast
   * route with no retrieval and a small budget, and reporting its numbers as
   * the thread's would make the ring swing on a side conversation.
   */
  async function runIntakeTurn(
    rawMessage: string,
    revisionHandle?: GateRevisionHandle,
    options?: { kind?: 'report' },
  ) {
    const trimmed = rawMessage.trim()
    if (isCanonicalQuickStudyArmed && !revisionHandle && !settledAudience) {toast.error(t('threads.quickStudy.chooseAudience')); return}
    if (!token || !beginTurn()) {
      return
    }

    /* D12. The explicit option is for the tick on which `/report` arms, before
       `armedCommand` has caught up; every later turn of the same conversation
       reads the marker off the armed command. Twin-test resolves to undefined
       on both paths, which is what keeps `intake.kind` OFF its request. */
    const intakeKind = options?.kind ?? armedCommand?.intakeKind

    const assistantId = newId()
    const userTurnId = newId()
    setTurns((prev) => [
      ...prev,
      /* An empty opening message is normal under this mode and inserts no user
         turn: there is no question to show, and the server skips the empty row
         for the same reason. */
      ...(trimmed
        ? [
            {
              id: userTurnId,
              role: 'user' as const,
              text: trimmed,
              createdAt: new Date().toISOString(),
              commandDisplayName: armedWorkflow?.displayName,
              commandAudienceName: armedAudienceChipName ?? undefined,
            },
          ]
        : []),
      { id: assistantId, role: 'assistant' as const, text: '', streaming: true },
    ])

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    activeExecutionRef.current = null
    stopRequestedRef.current = false
    nextContinuationRef.current = null

    try {
      let streamed = ''
      let doneFrame: AssistantTurnDoneFrame | null = null

      /* Read AT SEND TIME, never captured earlier: `readStagedQuestions` reads
         what the reviewer sees right now, so a card edited and saved between
         the gate opening and this Enter still travels with the message. */
      const intakePayload: AssistantIntakeRequest = revisionHandle
        ? { phase: 'revision', questions: revisionHandle.readStagedQuestions() }
        : { phase: 'scoping' }
      /* Added only when it is not the default. A twin-test intake request has
         to stay byte-identical to the one that ships today. */
      if (intakeKind && intakeKind !== 'twin_test') {
        intakePayload.kind = intakeKind
      }
      /* D4's resolver order: the confirm-card pick first, then whatever the
         page can name from what it already holds: the armed name, then the
         page-level audience list, then the remembered match. Report-kind
         intake never carries an audience name. Truncated client-side so a
         request never exceeds the DB column (`populations.name varchar(255)`)
         even if a caller ever hands this a longer name. A request with no
         bound audience must stay byte-identical to today's payload, so the
         key is added only when a non-empty name resolves. */
      if (intakePayload.kind !== 'report' && armedTemplate) {
        const resolvedAudienceName =
          armedAudienceName ||
          audienceNameForInputs(
            armedTemplate,
            armedInputs,
            pageAudiences,
            rememberedAudience,
          )
        if (resolvedAudienceName) {
          intakePayload.audienceName = resolvedAudienceName.slice(0, 255)
        }
      }
      if (intakePayload.phase === 'scoping' && intakePayload.kind !== 'report' && (armedWorkflow || isCanonicalQuickStudyArmed)) {
        const audienceToken = settledAudienceId(armedWorkflow, armedInputs)
        if (audienceToken && armedWorkflowSlug) {
          intakePayload.audience = {audienceToken, ...workflowRunSegmentFieldsForSlug(commandSeed, armedWorkflowSlug)}
        }
      }
      const attachmentTokens = await collectAttachmentTokens()

      if (!isCurrentObservation(controller)) return
      for await (const frame of assistantTurnStream(token, trimmed, {
        ...(creativeContext.revision !== undefined ? { creativeContextRevision: creativeContext.revision } : {}),
        signal: controller.signal,
        onAccepted: (snapshot) => {
          if (isCurrentObservation(controller)) acceptedExecution(snapshot)
        },
        onSnapshot: (snapshot) => {
          if (isCurrentObservation(controller)) notePausedContinuation(snapshot)
        },
        ...(isCanonicalQuickStudyArmed && !revisionHandle ? {
          quickStudy: {kind: 'start' as const, audience: {audienceToken: settledAudience!, plannedSampleSize: DEFAULT_PLANNED_SAMPLE_SIZE,
            ...workflowRunSegmentFieldsForSlug(commandSeed, armedWorkflowSlug!)}},
        } : {mode: 'intake' as const, intake: intakePayload}),
        attachmentTokens,
      })) {
        if (!isCurrentObservation(controller)) return
        if (frame.type === 'delta') {
          streamed += frame.text
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? { ...t, text: streamed, streaming: true }
                : t,
            ),
          )
        } else if (frame.type === 'done') {
          doneFrame = frame
        } else if (frame.type === 'error') {
          throw createAssistantStreamFailure(frame)
        }
      }

      if (!isCurrentObservation(controller)) return
      const done = doneFrame
      if (!done) {
        throw new Error('The assistant stopped before finishing that turn.')
      }

      const answer = done.text.trim() ? done.text : streamed
      /* Same reconciliation as the main sender: record the server's name for
         each half so run completion can recognise them later. An intake turn
         opened with an empty composer stored no user row, so its token is
         absent and there is nothing to patch. */
      const intakeUserToken = done.userMessageToken
      const intakeAssistantToken = done.assistantMessageToken
      /* Hoisted out of the map below because the i18n `t` is shadowed by that
         callback's own parameter. The twin-test line is left exactly as it
         shipped; only the report beat, which asks about something else
         entirely, gets its own. */
      const emptyIntakeAnswer =
        intakeKind === 'report'
          ? t('threads.detail.command.report.scoping')
          : 'Tell me what you want to test.'
      /* A settled direction asks nothing, so a reply with no words has no bubble
         at all: the empty-answer prompt would read as asking again. */
      const isSilentlySettled =
        !answer.trim() && done.intake?.status === 'direction' && Boolean(done.intake.ask?.trim())
      setTurns((prev) =>
        (isSilentlySettled ? prev.filter((turn) => turn.id !== assistantId) : prev).map((t) =>
          t.id === userTurnId && intakeUserToken
            ? { ...t, serverToken: intakeUserToken }
            : t.id === assistantId
            ? {
                ...t,
                text: answer.trim() ? answer : emptyIntakeAnswer,
                streaming: false,
                ...(intakeAssistantToken
                  ? { serverToken: intakeAssistantToken }
                  : {}),
              }
            : t,
        ),
      )

      if (done.preparedQuickStudy) {
        clearArmedWorkflow(); setIntakeDirection(null); setShowIntakeRetry(false); await nativeQuickStudies.refresh()
        await refreshPreparedStudyOrigin(done.preparedQuickStudy.threadOrigin.originMessageToken)
      } else if (done.preparedStudy?.presentation === 'quick') {
        clearArmedWorkflow()
        setIntakeDirection(null)
        setIntakeStartUiDismissed(false)
        setShowIntakeRetry(false)
        if (authUser?.id) await threadStudies.refresh()
        await refreshPreparedStudyOrigin(done.preparedStudy.threadOrigin?.originMessageToken)
        await refreshAfterTurn()
      } else if (isCanonicalQuickStudyArmed) {
        clearArmedWorkflow(); setIntakeDirection(null); setShowIntakeRetry(false); await nativeQuickStudies.refresh()
      } else if (done.intake?.status === 'handoff' && trimmed) {
        // The server completed the ordinary continuation under this same turn.
        // This tail disarms intake; it never submits the user's request again.
        clearArmedWorkflow()
        setIntakeStartUiDismissed(false)
        setIntakeDirection(null)
        setTurns((prev) => prev.map((turn) => turn.id === assistantId ? completedAssistantTurn(turn, done, streamed) : turn))
        if (done.plan?.cardEligible) {
          const proposal = planTailToProposal(done.assistantMessageToken ?? assistantId, done.plan)
          if (proposal) registerProposal(proposal)
        }
        if (done.report || done.living || done.creativeDrafts?.length) void refreshLivingArtifacts()
        void refreshThreadAssets()
      } else if (revisionHandle) {
        /* Called on the CAPTURED handle even if the gate has since published
           null: that late call is what produces the "gate already closed"
           notice, and skipping it would let a moved gate fail silently. The
           outcome is informational and every message the reviewer reads is
           emitted inside the gate, so nothing is toasted here. */
        revisionHandle.applyRevision(done.intake, trimmed)
      } else {
        const settledAsk =
          done.intake?.status === 'direction'
            ? done.intake.ask?.trim()
            : undefined
        /* A `direction` with no ask is a tail that did not survive its own
           contract, so it is treated as one more question rather than as an
           agreement to spend. */
        setIntakeStartUiDismissed(false)
        setIntakeDirection(settledAsk ? settledAsk : null)
      }

      const refreshed = await knowledgeThreadsApi.get(token)
      if (!isCurrentObservation(controller)) return
      setThread(refreshed)
      setTitleDraft(refreshed.title)
      if (attachmentTokens.length > 0) {
        void refreshLivingArtifacts()
      }
    } catch (error: unknown) {
      if (!isCurrentObservation(controller)) return
      if (['assistant_concurrency_limit_reached', 'assistant_thread_busy'].includes((error as { code?: string }).code ?? '')) {
        setTurns((prev) => prev.filter((turn) => turn.id !== assistantId && turn.id !== userTurnId))
        setComposerAskSeed((current) => ({ text: trimmed, nonce: (current?.nonce ?? 0) + 1 }))
        toast.error((error as Error).message)
        return
      }
      if (controller.signal.aborted || ((error as { name?: string } | null)?.name === 'AbortError')) {
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantId
              ? {
                  ...t,
                  text: t.text.trim()
                    ? t.text
                    : 'Stopped before the answer started.',
                  streaming: false,
                  stopped: true,
                }
              : t,
          ),
        )
        return
      }
      /* Never the thrown text: a stream failure maps by code to our copy. */
      const failureText = assistantTurnFailureText(error, t)
      const messageText =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? failureText
      toast.error(messageText)
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId
            ? {
                ...t,
                text: failureText,
                streaming: false,
                refused: true,
              }
            : t,
        ),
      )
      /* Still armed. A failed intake turn is a turn to retry, not a reason to
         throw away the audience the reader already picked. */
    } finally {
      await finishObservation(controller)
    }

  }

  /**
   * Start the armed workflow in THIS thread.
   *
   * The typed sentence rides along as `message`: the hook maps it onto the
   * template's first `text` input, which is the same input whose label this
   * composer is already showing as its placeholder. Nothing is mapped here,
   * and the refusal for a template whose required inputs no composer can fill
   * (`audience`, `select`) is not repeated here either: both live inside
   * `useStartWorkflowRun`, once, and a second copy on this page would double
   * the toast and drift from the hook's rule the first time either changed.
   *
   * `startRun` never rejects; it resolves the created run, or null after any
   * refusal it already toasted. Only a real run is adopted: adoption is what
   * fills the pane without a reload (creating from this page is a same-path
   * navigation, so the thread-keyed fetch never re-fires) and it lights the
   * sidebar dot through the same path every other run update uses, so a
   * refusal lights nothing.
   */
  async function startArmedWorkflow(text: string) {
    if (!armedWorkflow || !token) {
      return
    }
    const trimmed = text.trim()
    const askKey = askInputKey(armedWorkflow)
    const runInputs = {
      ...armedInputs,
      ...(askKey ? { [askKey]: trimmed } : {}),
    }
    /* Insert the user bubble BEFORE the first await so a concurrently flushed
       `runAsk` (entry 2+) sees it in order (H7). If the stimulus gate then
       fails the optimistic turn is removed — same as if the reader never sent
       it, consistent with `setBlockedArmedAsk` today. */
    const optimisticTurnId = trimmed ? newId() : null
    if (optimisticTurnId) {
      setTurns((prev) => [
        ...prev,
        {
          id: optimisticTurnId,
          role: 'user' as const,
          text: trimmed,
          createdAt: new Date().toISOString(),
          commandDisplayName: armedWorkflow.displayName,
          /* The SAME resolver the composer chip reads (D4), stamped at send so
             the sent turn keeps both chips after the composer clears. */
          ...(armedAudienceChipName
            ? { commandAudienceName: armedAudienceChipName }
            : {}),
        },
      ])
    }
    if (
      !(await isStimulusGateSatisfiedAtRunStart({
        template: armedWorkflow,
        inputs: runInputs,
        ask: trimmed,
        proceedWithoutStimulus: armedProceedWithoutStimulusRef.current,
      }))
    ) {
      setBlockedArmedAsk(trimmed)
      if (optimisticTurnId) {
        setTurns((prev) => prev.filter((t) => t.id !== optimisticTurnId))
      }
      return
    }
    setBlockedArmedAsk(null)
    const startedRun = await startWorkflowRun({
      template: armedWorkflow,
      threadToken: token,
      message: text,
      inputs: runInputs,
      /* A thread action armed from the command menu (`/report`) is tagged the
         same way the menu's own start-now path tags it. */
      ...(armedCommand ? { trigger: 'thread_action' as const } : {}),
      ...workflowRunSegmentFieldsForSlug(commandSeed, armedWorkflow.slug),
    })
    if (startedRun) {
      armedProceedWithoutStimulusRef.current = false
      setBlockedArmedAsk(null)
      clearThreadComposerAttachments()
      /* Claim the run's ask id for the bubble already on screen. `createRun`
         persists the same sentence as a `workflow-run-ask:<token>` message,
         and `mergeRunCompletionAnnouncements` now pulls every `workflow-run-*`
         line in on the next settled-step refresh; without this the reader
         would watch their own sentence appear a second time mid-run. The
         askId is what that merge dedupes on, and it is also what
         `withRunCommandChip` and `userTurnCommandChipLabel` read. */
      if (optimisticTurnId) {
        const askId = workflowRunAskTurnAskId(startedRun.token)
        setTurns((prev) =>
          prev.map((turn) =>
            turn.id === optimisticTurnId ? { ...turn, askId } : turn,
          ),
        )
      }
      adoptRun(startedRun)
      /* Started is no longer armed: the same clear `confirmIntakeDirection`
         makes on success, so the textarea goes back to ordinary asking and
         the next Enter cannot post a second run into the server's
         one-active-run 409. The run itself stays visible as the locked chip
         in the header slot. */
      clearArmedWorkflow()
    } else if (optimisticTurnId) {
      /* Refused, and `startWorkflowRun` has already toasted why: take the
         bubble back down (nothing was persisted, so a reload would drop it
         anyway) and give the sentence back to the composer, where the reader
         can edit it and send again. The composer stays armed. */
      setTurns((prev) => prev.filter((turn) => turn.id !== optimisticTurnId))
      setComposerAskSeed((current) => ({
        text: trimmed,
        nonce: (current?.nonce ?? 0) + 1,
      }))
    }
  }

  /**
   * The context ring's escape hatch. Mints a fresh thread and lands on it,
   * mirroring `useHomeLabSubmit.ts:422-433` — the same `knowledgeThreadsApi.create`
   * call — rather than inventing a second way to start a thread. Language is
   * this thread's content language when set; if the parent never stored one,
   * fall back to `defaultContentLanguageFromUi()` so the child still gets a
   * session language (same default as a new thread). The scope carried over is
   * this thread's own: continuing a conversation into a new thread should not
   * quietly widen it back to the whole library.
   */
  async function continueInNewThread() {
    try {
      const created = await knowledgeThreadsApi.create({
        scope: continueThreadScope(scope),
        language: thread?.language || defaultContentLanguageFromUi(),
      })
      upsertSidebarThread(created)
      navigate(ROUTES.thread(created.token))
    } catch {
      toast.error(t('threads.detail.toast.startThreadFailed'))
    }
  }

  async function autosaveCreativeDraft(
    turn: LocalTurn,
    key: string,
    patch: { title: string; body: string },
  ) {
    const messageToken = turnMessageToken(turn)
    if (stoppedCreativeAutosaveRef.current.has(messageToken)) {
      return
    }
    setTurns((prev) =>
      prev.map((item) => {
        if (turnMessageToken(item) !== messageToken) {
          return item
        }
        if (!item.creativeDrafts) {
          return item
        }
        return {
          ...item,
          creativeDrafts: item.creativeDrafts.map((draft) =>
            draft.key === key
              ? { ...draft, title: patch.title, body: patch.body }
              : draft,
          ),
        }
      }),
    )
    setSavedDraftMessageTokens((prev) => ({ ...prev, [messageToken]: true }))
    const work = (async () => {
      try {
        const result = await knowledgeThreadsApi.patchCreativeDraft(
          token,
          messageToken,
          key,
          patch,
        )
        if (result?.creativeDrafts) {
          setTurns((prev) =>
            prev.map((item) =>
              turnMessageToken(item) === messageToken
                ? { ...item, creativeDrafts: result.creativeDrafts }
                : item,
            ),
          )
        }
      } catch (error: unknown) {
        if (isConflictStatus(error)) {
          stoppedCreativeAutosaveRef.current.add(messageToken)
          return
        }
        toast.error(confirmErrorMessage(error))
      }
    })()
    let byKey = pendingCreativeAutosavesRef.current.get(messageToken)
    if (!byKey) {
      byKey = new Map<string, Promise<void>>()
      pendingCreativeAutosavesRef.current.set(messageToken, byKey)
    }
    byKey.set(key, work)
    try {
      await work
    } finally {
      const current = pendingCreativeAutosavesRef.current.get(messageToken)
      if (current?.get(key) === work) {
        current.delete(key)
        if (current.size === 0) {
          pendingCreativeAutosavesRef.current.delete(messageToken)
        }
      }
    }
  }

  async function confirmCreativeDraftSet(turn: LocalTurn) {
    const messageToken = turnMessageToken(turn)
    const pendingByKey = pendingCreativeAutosavesRef.current.get(messageToken)
    if (pendingByKey && pendingByKey.size > 0) {
      const leftovers = Array.from(pendingByKey.values())
      for (const leftover of leftovers) {
        await leftover
      }
    }
    setConfirmingDraftMessageToken(messageToken)
    try {
      const result = await knowledgeThreadsApi.confirmCreativeDrafts(
        token,
        messageToken,
      )
      const artifacts = result?.artifacts ?? []
      await refreshLivingArtifacts()
      const confirmedAt = new Date().toISOString()
      setTurns((prev) =>
        prev.map((item) => {
          if (turnMessageToken(item) !== messageToken) {
            return item
          }
          const drafts = item.creativeDrafts ?? []
          return {
            ...item,
            creativeDrafts: stampCreativeDraftsFromConfirm({
              drafts,
              artifacts,
              creativeDrafts: result?.creativeDrafts,
              confirmedAt,
            }),
          }
        }),
      )
      stoppedCreativeAutosaveRef.current.add(messageToken)
      await nativeQuickStudies.refresh()
      if (result.quickStudyContinuation) void resumeBackgroundTurn(result.quickStudyContinuation)
    } catch (error: unknown) {
      if (isConflictStatus(error)) {
        stoppedCreativeAutosaveRef.current.add(messageToken)
      }
      toast.error(confirmErrorMessage(error))
    } finally {
      setConfirmingDraftMessageToken(null)
    }
  }

  async function cancelCreativeDraftSet(turn: LocalTurn) {
    const messageToken = turnMessageToken(turn)
    try {
      await knowledgeThreadsApi.clearCreativeDrafts(token, messageToken)
      setTurns((prev) =>
        prev.map((item) =>
          turnMessageToken(item) === messageToken
            ? { ...item, creativeDrafts: undefined }
            : item,
        ),
      )
      stoppedCreativeAutosaveRef.current.add(messageToken)
    } catch (error: unknown) {
      toast.error(confirmErrorMessage(error))
    }
  }

  /**
   * The reader answering a pending draft set with a bare "yes" / "confirm" /
   * "save". Same callback as the Confirm button. "go" is the plan door.
   */
  function handlePlainCreativeConfirm(rawMessage: string): boolean {
    if (!isPlainCreativeConfirm(rawMessage)) {
      return false
    }
    const target = resolveCreativeDraftConfirmTarget(turns)
    if (!target) {
      return false
    }
    const targetTurn = turns.find((item) => item.id === target.messageId)
    if (!targetTurn) {
      return false
    }
    setTurns((prev) => [
      ...prev,
      {
        id: newId(),
        role: 'user' as const,
        text: rawMessage.trim(),
        createdAt: new Date().toISOString(),
      },
    ])
    void confirmCreativeDraftSet(targetTurn)
    return true
  }

  /**
   * The reader answering the announcement's recommendation with a bare "go".
   *
   * The announcement promises that "go" starts the next step, so it has to,
   * or the copy is lying and the reader is left with a dialog as the only
   * way forward. This runs the SAME callbacks the plan card's own buttons
   * run: the conversational path and the card are two doors onto one action,
   * never two implementations of it.
   *
   * Returns true when it handled the message. A "go" typed inside a plan step
   * turn, or with anything else attached, is left alone: see `isPlainGo`.
   */
  function handlePlainGo(rawMessage: string): boolean {
    if (!isPlainGo(rawMessage)) {
      return false
    }
    const target = resolveGoTarget(turns.map((turn) => ({ ...turn, id: turn.serverToken ?? turn.id })), plansByMessageId)
    if (!target) {
      return false
    }
    /* The reader said it, so the thread shows it. Without this the column
       reads as the announcement followed straight by the next step's output,
       with nothing between them explaining why anything started. The turn is
       local only: agreeing is not a question, so there is no server message
       to reconcile it against, and a reload replays the thread from what the
       step actually did. */
    setTurns((prev) => [
      ...prev,
      {
        id: newId(),
        role: 'user' as const,
        text: rawMessage.trim(),
        createdAt: new Date().toISOString(),
      },
    ])
    if (target.mode === 'confirm') {
      applyPlanStepHandoff(target.messageId, confirmSoundsGood(target.messageId))
      return true
    }
    const plan = plansByMessageId[target.messageId]
    applyPlanStepHandoff(target.messageId, plan ? stepHandoffFrom(plan) : null)
    return true
  }

  /** Resolves once no turn is running, so a queued image approval is not refused as busy. */
  async function waitForTurnIdle(maxMs = 120000) {
    const startedAt = Date.now()
    while (askingRef.current && Date.now() - startedAt < maxMs) {
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
  }

  async function runAsk(
    rawMessage: string,
    turnOptions?: {
      quickStudy?: QuickStudyCommand
      clientTurnId?: string
      stepSkillSlug?: string
      planMessageId?: string
      disarmsSkillTag?: boolean
      /** An image retry resends the earlier turn's attachments, not the composer's. */
      resend?: ImageRetryRequest
      imageApproval?: {
        messageToken: string
        requestId: string
        quality?: 'medium' | 'high'
        formatId?: CreativeFormatId
        formats?: Array<CreativeFormatId>
        /** The format this turn renders, also with no selection on the wire. */
        turnFormatId?: CreativeFormatId
        step?: 'first' | 'try' | 'rest'
        tries?: number
        /** The card's choices. The server rebuilds the quote only when they differ from it. */
        selection?: CreativeContextSelection
        quoteBrandToken?: string | null
      }
      onImageAccepted?: () => void
    },
  ) {
    const resend = turnOptions?.resend
    const hostedAttachments = turnOptions?.imageApproval ? [] : resend
      ? resend.attachments
      : chatImagesToTurnAttachments(threadAttachmentsRef.current)
    const trimmed =
      rawMessage.trim() ||
      (hostedAttachments.length > 0 ? ATTACHMENT_ONLY_ASK : '')
    if (!trimmed || askingRef.current || !token) return
    /* AFTER the busy guard, deliberately: agreeing to start something while a
       turn is still streaming would activate the plan and then bounce off the
       guard when the step tried to send, leaving the plan `active` with
       nothing running and no explanation. Only for a message the reader typed
       too: a step's own intent is dispatched through
       `turnOptions.planMessageId` and must never be re-read as an agreement. */
    if (!turnOptions?.planMessageId && handlePlainCreativeConfirm(rawMessage)) {
      return
    }
    if (!turnOptions?.planMessageId && handlePlainGo(rawMessage)) {
      return
    }
    /* Claimed HERE and not at the top: `handlePlainGo` dispatches the plan's
       own `runAsk` synchronously, and a guard claimed before it would refuse
       that nested turn. Nothing between the top check and this line awaits,
       so nothing can slip in either. */
    if (!beginTurn()) {
      return
    }
    if (turnOptions?.disarmsSkillTag) {
      setArmedSkillCommand(null)
    }
    const planMessageId = turnOptions?.planMessageId
    const stepSkillSlug = turnOptions?.stepSkillSlug
    const assistantId = newId()
    const userTurnId = newId()
    const userTurn: LocalTurn = {
      id: userTurnId as string,
      role: 'user',
      text: trimmed,
      createdAt: new Date().toISOString(),
      ...(turnOptions?.imageApproval ? { imageApprovalRequestId: turnOptions.imageApproval.requestId, imageApprovalFormatId: turnOptions.imageApproval.turnFormatId } : {}),
      ...(hostedAttachments.length > 0
        ? { attachments: hostedAttachments }
        : {}),
    }
    setTurns((prev) => [
      // A new attempt replaces the one that never rendered: the server keeps one ask for the image.
      ...(turnOptions?.imageApproval
        ? withoutEndedImageAttempt(prev, turnOptions.imageApproval.requestId, turnOptions.imageApproval.turnFormatId)
        : prev),
      userTurn,
      {
        id: assistantId,
        role: 'assistant' as const,
        text: '',
        streaming: true,
        /* Marks this turn as a plan step's own answer. Without it, a chain
           that stops at a workflow step leaves the newest turn looking like
           ordinary prose, and a plain "go" has nothing to bind to. */
        ...(planMessageId ? { planMessageId } : {}),
        ...(turnOptions?.imageApproval ? { imageApprovalRequestId: turnOptions.imageApproval.requestId, imageApprovalFormatId: turnOptions.imageApproval.turnFormatId } : {}),
      },
    ])
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    activeExecutionRef.current = null
    stopRequestedRef.current = false
    nextContinuationRef.current = null

    // A stopped turn keeps whatever text arrived, but must never keep its
    // streaming state — the caret would otherwise blink forever on a turn that
    // will receive nothing more. Citations only ship with the final frame, so a
    // stopped answer legitimately has none.
    // The image was registered and charged before the turn ended: it counts as made.
    const settleMadeImage = () => {
      setTurns((prev) => prev.map((t) => t.id === assistantId ? { ...t, streaming: false, imageProgress: stopPendingImages(t.imageProgress) } : t))
      setImageRequestStatus(turnOptions!.imageApproval!.requestId, 'completed', turnOptions!.imageApproval!.formatId, approvalBrand(turnOptions!.imageApproval!))
    }
    const finalizeStopped = () => {
      if (completedImage && turnOptions?.imageApproval) {
        settleMadeImage()
        return
      }
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId
            ? {
                ...t,
                text: t.text.trim()
                  ? t.text
                  : 'Stopped before the answer started.',
                streaming: false,
                stopped: true,
                imageProgress: stopPendingImages(t.imageProgress),
              }
            : t,
        ),
      )
    }

    const scopePayload = {
      sourceMode: scope.sourceMode ?? 'auto',
      web: scope.web !== false,
      library: scope.library !== false,
      ...(scope.mode === 'selected'
        ? {
            mode: 'selected' as const,
            documentTokens: scope.documentTokens,
            collectionTokens: scope.collectionTokens,
          }
        : {}),
      ...(scope.libraryCollectionTokens?.length
        ? { libraryCollectionTokens: scope.libraryCollectionTokens }
        : {}),
      ...(scope.sourceKinds ? { sourceKinds: scope.sourceKinds } : {}),
      ...(scope.socialWindow ? { socialWindow: scope.socialWindow } : {}),
    }

    /* Any image this turn started may already be committed (D3 runs inside
       the tool), so the rail refetches on done, stop and failure alike. */
    let sawImageProgress = false
    let completedImage = false
    let imageTurnFailed = false
    let imageCardMoved = false
    let imageFailureReason: string | undefined
    let imageRenderStarted = false

    try {
      let streamed = ''
      let doneFrame: AssistantTurnDoneFrame | null = null
      const notes: Array<string> = []

      const attachmentTokens = turnOptions?.imageApproval ? [] : resend
        ? resend.attachmentTokens
        : await collectAttachmentTokens()
      imageRetryRequestsRef.current.set(assistantId, {
        text: trimmed,
        attachments: hostedAttachments,
        attachmentTokens,
      })
      const planCursor = planMessageId ? await prepareStepExecution(planMessageId) : undefined
      if (!isCurrentObservation(controller)) return
      for await (const frame of assistantTurnStream(token, trimmed, {
        ...(!turnOptions?.imageApproval && creativeContext.revision !== undefined ? { creativeContextRevision: creativeContext.revision } : {}),
        signal: controller.signal,
        onAccepted: (snapshot) => {
          if (isCurrentObservation(controller)) { acceptedExecution(snapshot); updateImageRequestExecution(snapshot); turnOptions?.onImageAccepted?.() }
        },
        onSnapshot: (snapshot) => {
          if (isCurrentObservation(controller)) { notePausedContinuation(snapshot); updateImageRequestExecution(snapshot) }
        },
        ...(turnOptions?.clientTurnId ? {clientTurnId: turnOptions.clientTurnId} : {}),
        ...(turnOptions?.imageApproval ? {
          clientTurnId: turnOptions.imageApproval.requestId,
          imageApprovalMessageToken: turnOptions.imageApproval.messageToken,
          imageApprovalRequestId: turnOptions.imageApproval.requestId,
          ...(turnOptions.imageApproval.quality ? { imageQuality: turnOptions.imageApproval.quality } : {}),
          ...(turnOptions.imageApproval.formatId ? { imageFormat: turnOptions.imageApproval.formatId, imageFormats: turnOptions.imageApproval.formats } : {}),
          ...(turnOptions.imageApproval.step ? { imageApprovalStep: turnOptions.imageApproval.step } : {}),
          ...(turnOptions.imageApproval.tries ? { imageTry: turnOptions.imageApproval.tries } : {}),
          ...(turnOptions.imageApproval.selection ? { imageCreativeSelection: turnOptions.imageApproval.selection } : {}),
        } : {}),
        scope: scopePayload,
        ...(turnOptions?.quickStudy ? {quickStudy: turnOptions.quickStudy} : {}),
        ...(turnProfileRef.current ? { profile: turnProfileRef.current } : { effort }),
        attachmentTokens,
        ...(stepSkillSlug ? { stepSkillSlug } : {}),
        ...(planCursor ? { planCursor } : {}),
      })) {
        if (!isCurrentObservation(controller)) return
        if (frame.type === 'delta') {
          streamed += frame.text
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? { ...t, text: streamed, streaming: true }
                : t,
            ),
          )
        } else if (frame.type === 'suggestions') {
          setSuggestions(frame.suggestions)
        } else if (frame.type === 'image_progress') {
          sawImageProgress = true
          const imageFrame = frame
          if (imageFrame.stage === 'failed') imageFailureReason = imageFrame.reason ?? undefined
          if (imageFrame.stage === 'rendering' || imageFrame.stage === 'partial') imageRenderStarted = true
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    imageProgress: applyImageProgressFrame(t.imageProgress, imageFrame),
                  }
                : t,
            ),
          )
          const generated = imageFrame.stage === 'done' ? imageFrame.asset : null
          if (generated?.token) {
            completedImage = true
            /* Optimistic: the answer's `asset:<token>` resolves the moment
               it streams, before the rail's own refetch lands. */
            setThreadAssets((prev) =>
              prev.some((item) => item.token === generated.token)
                ? prev
                : [...prev, generated],
            )
          }
        } else if (frame.type === 'citations_v2') {
          // Evidence arrives before prose; resolved anchors only arrive at done.
          setTurns((prev) => prev.map((turn) => turn.id === assistantId
            ? { ...turn, citationV2: { targets: [], answerSpans: [], documentDisplayIndexes: [] } }
            : turn))
        } else if (frame.type === 'citations') {
          /* The citations frame lands BEFORE the first delta, so splitting it
             here is what lets the web cards arrive with the answer rather than
             only once it is finished. */
          const partitioned = partitionAssistantCitations(frame.items)
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    citations: partitioned.documentCitations,
                    webCitations: partitioned.webCitations,
                  }
                : t,
            ),
          )
        } else if (frame.type === 'judgment') {
          const note = frame.note.trim()
          if (note) {
            notes.push(note)
            setTurns((prev) =>
              prev.map((t) =>
                t.id === assistantId
                  ? { ...t, judgmentNotes: [...notes] }
                  : t,
              ),
            )
          }
        } else if (frame.type === 'tool_start' || frame.type === 'tool_end') {
          /* The checklist's only source of truth, folded by the one reducer
             both loops share (`applyActivityFrame`): starts append in arrival
             order, and each end pairs with its own start by `callId`, else
             with the first open leg of the same tool. */
          const activityFrame = frame
          if (
            activityFrame.type === 'tool_end' &&
            isLivingArtifactMutationTool(activityFrame.tool)
          ) {
            void refreshLivingArtifacts()
          }
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    activityLegs: applyActivityFrame(
                      t.activityLegs ?? [],
                      activityFrame,
                    ),
                  }
                : t,
            ),
          )
        } else if (frame.type === 'done') {
          doneFrame = frame
        } else if (frame.type === 'error') {
          imageTurnFailed = Boolean(turnOptions?.imageApproval)
          throw createAssistantStreamFailure(frame)
        }
      }

      // Narrowed into a const: `doneFrame` is assigned inside a loop, so the
      // closures below would otherwise see it as nullable again.
      if (!isCurrentObservation(controller)) return
      const done = doneFrame
      if (!done) {
        throw new Error('The assistant stopped before finishing that turn.')
      }

      const answer = done.text.trim() ? done.text : streamed
      // --- Agent C: attach plan to the turn + register with D's cursor hook ---
      const preparedSeed = done.preparedRun?.commandSeed
      const donePlan =
        done.plan && done.plan.cardEligible ? done.plan : undefined
      /* One `done` frame can carry BOTH a prepared run and a card-eligible
         plan, and python does not arbitrate between them. Opening the command
         dialog while a plan card is still waiting for Sounds good puts two
         gates on screen at once and lets the reader answer the second one
         first. The card is the only gate for a plan it is proposing: its
         Sounds good already opens the workflow step with the right audience
         through `confirmSoundsGood` -> `applyPlanStepHandoff`.

         `planMessageId` set means this turn is running INSIDE an already
         approved plan, so its prepared run must still open: that plan was
         agreed to, and this is the step being executed, not a new proposal. */
      const registersNewPlanCard = Boolean(donePlan && !planMessageId)
      if (preparedSeed?.slug && !registersNewPlanCard) {
        /* D3: a real ask (`planMessageId` null) hands its own words to
           `handleArmCommand` for when the armed template turns out to be
           the composer-owned-optional-ask kind. The approved-plan "go
           ahead" turn clears this so that path keeps reading `rest`. */
        pendingPreparedReaderAskRef.current =
          planMessageId == null
            ? {
                slug: preparedSeed.slug,
                message: trimmed,
                rest: preparedSeed.rest || trimmed,
              }
            : null
        seedPlanWorkflowStart(
          assistantId,
          preparedSeed.slug,
          preparedSeed.rest || trimmed,
          preparedSeed.audience ?? '',
          '',
          {
            populationFilter: preparedSeed.populationFilter,
            segmentLabel: preparedSeed.segmentLabel,
          },
        )
      }
      if (donePlan && !planMessageId) {
        const proposal = planTailToProposal(done.assistantMessageToken ?? assistantId, donePlan)
        if (proposal) {
          registerProposal(proposal)
        }
      } else if (!preparedSeed?.slug && done.plan && !planMessageId) {
        const singleSlug = singleStepWorkflowSlug(done.plan)
        if (singleSlug) {
          const step = done.plan.steps[0]
          const stepWhy = step?.why?.trim() ?? ''
          seedPlanWorkflowStart(
            assistantId,
            singleSlug,
            trimmed || stepWhy,
            step?.inputs?.audience ?? '',
          )
        }
      }
      // --- end Agent C ---
      /* Both halves of this exchange learn the name the server gave them.
         The ids stay local (see `serverToken` on LocalTurn); this only adds
         the second name, so the next `listMessages` can tell that these two
         rows are already on screen. `userMessageToken` is absent, never
         null, when the turn stored no user row. */
      const userMessageToken = done.userMessageToken
      const assistantMessageToken = done.assistantMessageToken
      setTurns((prev) =>
        prev.map((t) => {
          if (userTurnId && t.id === userTurnId) {
            return userMessageToken
              ? { ...t, serverToken: userMessageToken }
              : t
          }
          if (t.id !== assistantId) {
            return t
          }
          return completedAssistantTurn(t, done, streamed, notes)
        }),
      )
      // Reconcile optimistic message identities before adopting a saved origin.
      // Otherwise the same user message mounts two live editors until reload.
      if (done.preparedQuickStudy) {
        clearArmedWorkflow(); setIntakeDirection(null); setShowIntakeRetry(false); await nativeQuickStudies.refresh()
        await refreshPreparedStudyOrigin(done.preparedQuickStudy.threadOrigin.originMessageToken)
      } else if (done.preparedStudy?.presentation === 'quick') {
        if (authUser?.id) await threadStudies.refresh()
        await refreshPreparedStudyOrigin(done.preparedStudy.threadOrigin?.originMessageToken)
      } else if (done.preparedStudy?.draftToken) {
        openStudyFrame(done.preparedStudy.draftToken, 'draft')
      }
      if (sawImageProgress) {
        void refreshThreadAssets()
      }
      /* D7: dismissals made under the temporary per-turn key move to the
         stored message token, so they survive a reload. */
      if (assistantMessageToken && token) {
        moveSuggestionDismissals(token, suggestionTurnKeyRef.current, assistantMessageToken)
        suggestionTurnKeyRef.current = assistantMessageToken
        setSuggestionTurnKey(assistantMessageToken)
      }
      setContextUsage(done.contextUsage)
      setHistoryTruncated(done.truncated)
      if (done.switch || done.report || done.living || attachmentTokens.length > 0) {
        if (run?.token) {
          void mutate(['artifact-tips', run.token]).catch(() => undefined)
        }
        void refreshLivingArtifacts()
      }

      const refreshed = await knowledgeThreadsApi.get(token)
      if (!isCurrentObservation(controller)) return
      setThread(refreshed)
      setTitleDraft(refreshed.title)
      if (!resend && !turnOptions?.imageApproval) {
        clearThreadComposerAttachments()
      }
      await refreshAfterTurn(planMessageId)
      // The accepted execution reported `running`; the finished image closes it.
      if (turnOptions?.imageApproval && completedImage) {
        setImageRequestStatus(turnOptions.imageApproval.requestId, 'completed', turnOptions.imageApproval.formatId, approvalBrand(turnOptions.imageApproval))
      }
      return !controller.signal.aborted && !stopRequestedRef.current &&
        (!turnOptions?.imageApproval || completedImage)
    } catch (error: unknown) {
      if (!isCurrentObservation(controller)) return
      if (sawImageProgress) {
        void refreshThreadAssets()
      }
      if (controller.signal.aborted || ((error as { name?: string } | null)?.name === 'AbortError')) {
        finalizeStopped()
        return
      }
      if (completedImage && turnOptions?.imageApproval) {
        settleMadeImage()
        return true
      }
      if (['assistant_concurrency_limit_reached', 'assistant_thread_busy'].includes((error as { code?: string }).code ?? '')) {
        setTurns((prev) => prev.filter((turn) => turn.id !== assistantId && turn.id !== userTurnId))
        setComposerAskSeed((current) => ({ text: trimmed, nonce: (current?.nonce ?? 0) + 1 }))
        toast.error((error as Error).message)
        return
      }
      if ((error as { code?: string }).code === 'creative_context_changed') {
        // A card in another tab moved the chat's choices: nothing ran. This tab rereads them and keeps the message.
        setTurns((prev) => prev.filter((turn) => turn.id !== assistantId && turn.id !== userTurnId))
        setComposerAskSeed((current) => ({ text: trimmed, nonce: (current?.nonce ?? 0) + 1 }))
        void creativeContext.reload()
        toast.error(t('threads.detail.turnError.creative_context_changed'))
        return
      }
      /* A refused set format never ran: no bubble stays, and the card says what happened. */
      const masterRefusal = turnOptions?.imageApproval ? (error as { code?: string }).code : undefined
      if (masterRefusal === 'image_master_not_ready' || masterRefusal === 'image_master_failed' || masterRefusal === 'image_approval_required' || masterRefusal === 'image_approval_conflict' || masterRefusal === 'image_try_closed' || masterRefusal === 'image_context_unavailable') {
        setTurns((prev) => prev.filter((turn) => turn.id !== assistantId && turn.id !== userTurnId))
        if (masterRefusal === 'image_master_not_ready' || masterRefusal === 'image_approval_required') return
        if (masterRefusal === 'image_context_unavailable') {
          // Nothing ran and nothing is charged: the card pauses, and the server names what is gone.
          toast.error((error as Error).message || t('threadImage.approval.contextUnavailable'))
          return 'context_unavailable' as const
        }
        if (masterRefusal === 'image_approval_conflict' || masterRefusal === 'image_try_closed') {
          // Another tab moved this card: card and images come from one read, and nothing starts.
          toast.info(t('threadImage.approval.cardChanged'))
          imageCardMoved = true
          await reloadThreadTurns().catch(() => undefined)
          return 'conflict' as const
        }
        setImageRequestStatus(turnOptions!.imageApproval!.requestId, 'failed', turnOptions!.imageApproval!.formatId)
        return 'master_failed' as const
      }
      /* The turn FAILS VISIBLY, and no second path answers in its place.
         This is the thread's only answer path and the only one that reports
         context usage, so a quiet re-ask elsewhere would hand the reader a
         different answer under the same question and call it a success. */
      /* Never the thrown text: a stream failure maps by code to our copy. */
      const failureText = assistantTurnFailureText(error, t)
      const messageText =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? failureText
      toast.error(messageText)
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId
            ? {
                ...t,
                text: failureText,
                streaming: false,
                refused: true,
                imageProgress: stopPendingImages(t.imageProgress),
              }
            : t,
        ),
      )
      if (imageTurnFailed && turnOptions?.imageApproval) {
        // Out of credits, or a brand that cannot compile, fails every image the same way: pause, so the rest stay retryable.
        const isQuoteRefusal = imageFailureReason === 'insufficient_credits' || imageFailureReason === 'billing' || imageFailureReason === 'creative_inputs'
        // Refused before the provider: nothing charged, no failed status, the same click runs it again.
        setImageRequestStatus(turnOptions.imageApproval.requestId, isQuoteRefusal && !imageRenderStarted ? null : 'failed', turnOptions.imageApproval.formatId, approvalTry(turnOptions.imageApproval))
        if (!isQuoteRefusal) return 'failed' as const
        if (!imageRenderStarted) return 'unstarted' as const
      }
    } finally {
      await finishObservation(controller)
      // No other turn to follow, so that one has ended: read its final state, in case the first read caught it running.
      if (imageCardMoved && !askingRef.current) {
        await reloadThreadTurns().catch(() => undefined)
      }
      // Server completion owns approved skill advancement; refresh never starts a turn.

    }
  }

  /**
   * A12: the side pane's one list. Living outputs split by kind (Reports vs
   * Artifacts), thread assets not folded into a stimulus are Files, and the
   * newest answer's calculations keep their own section (O1). Each node is
   * null when empty, which hides its section.
   */
  function renderSessionSections() {
    const cardProps = {
      onOpenFullscreen: openArtifactFullscreen,
      threadAssets,
      docsByToken,
      onOpenCitation: openCitation,
      onOpenStudyReport: (studyToken: string) => openStudyFrame(studyToken, 'results'),
      plots: studyDocPlots,
      quotes: studyDocQuotes,
      populationId: studyDocPopulationId,
      strips: studyDocStrips,
    }
    const reportItems = runArtifacts.filter((item) => sessionSectionForArtifact(item.kind) === 'reports')
    const artifactItems = runArtifacts.filter((item) => sessionSectionForArtifact(item.kind) === 'artifacts')
    const livingFailed = Boolean(livingArtifactsError) && !livingArtifactsLoading
    const stimulusAssetTokens = new Set(
      runArtifacts.filter((item) => item.kind === 'stimulus' && item.assetToken).map((item) => item.assetToken as string),
    )
    const fileAssets = threadAssets.filter((asset) => !stimulusAssetTokens.has(asset.token))
    const reports = reportItems.length || livingFailed ? (
      <>
        {livingFailed ? (
          <div className="flex items-center gap-2 px-1 py-1" role="alert" data-testid="thread-artifact-living-error">
            <Text size="sm" color="muted">{t('threads.detail.rail.loadOutputsFailed')}</Text>
            <Button variant="ghost" size="sm" data-testid="thread-session-retry-artifacts" onClick={() => void refreshLivingArtifacts()}>
              {t('threads.sessions.retry')}
            </Button>
          </div>
        ) : null}
        {reportItems.length ? <ThreadArtifactCardList items={reportItems} {...cardProps} /> : null}
      </>
    ) : null
    const artifacts = artifactItems.length || pendingCreativeDraftCount > 0 ? (
      <>
        {pendingCreativeDraftCount > 0 ? (
          <Text size="sm" color="muted" className="px-1 pb-2" data-testid="thread-artifacts-pending-drafts">
            {t('threads.detail.rail.pendingCreativeDrafts', { count: pendingCreativeDraftCount })}
          </Text>
        ) : null}
        <ThreadArtifactCardList items={artifactItems} indexOffset={reportItems.length} {...cardProps} />
      </>
    ) : null
    const files = fileAssets.length ? (
      <ThreadAssetTiles assets={fileAssets} onRemoveAsset={isReadOnlyForViewer ? undefined : handleRemoveAsset} />
    ) : null
    const calculations = calculationSources.length ? (
      <ThreadCalculationSources
        calculations={calculationSources}
        highlightedId={calculationFocus?.evidenceId}
        focusNonce={calculationFocus?.nonce}
      />
    ) : null
    // A row opens exactly like that source's chip in an answer.
    const sources = threadSources.length ? (
      <ThreadSourcesSection
        entries={threadSources}
        ctx={{ t, resolveLabel: citationLabel, docsByToken, onOpenCitation: openCitation, onNavigate: navigate }}
      />
    ) : null
    const progress = showPaneProgress ? (
      <ThreadRunPane
        run={run}
        template={runTemplate}
        isLoading={runLoading}
        error={runError}
        includeGateCards={false}
        onRetryStep={isReadOnlyForViewer ? undefined : retryStep}
        onSubmitGate={submitGate}
        onCancelRun={cancelRun}
        language={thread?.language}
      />
    ) : null
    return { reports, artifacts, files, sources, calculations, progress }
  }

  /** A12: the pane's drill-in over the list (citation tabs, an expanded artifact). */
  function renderSessionDetail() {
    if (sourceTabs.length > 0 || expandedArtifact) {
      return renderSessionContent()
    }
    return undefined
  }

  function closeSessionDetail() {
    if (sourceTabs.length > 0) {
      setSourceTabs([])
      setActiveSourceKey(null)
      return
    }
    collapseExpandedArtifact()
  }

  function renderSessionContent(sourcesOnly = false, selectedRun?: WorkflowRun) {
    const sessionRun = selectedRun?.token === run?.token ? run : selectedRun ?? run;
    const historical = Boolean(selectedRun && selectedRun.token !== run?.token);
    return (!selectedRun && sourceTabs.length > 0 ? (
                    <ThreadSourcePanel
                      tabs={sourceTabs}
                      activeKey={activeSourceKey}
                      onCloseAll={() => {
                        setSourceTabs([])
                        setActiveSourceKey(null)
                      }}
                      onRelabel={relabelSourceTab}
                      onReportWrongPage={(citation) =>
                        void handleWrongPage(citation)
                      }
                    />
                  ) : expandedArtifact ? (
                    <ThreadArtifactExpandedView
                      item={expandedArtifact}
                      brief={turns.find((turn) => turn.report?.publicId === expandedArtifact.publicId)?.reportBrief}
                      onClose={collapseExpandedArtifact}
                      onFullscreen={() =>
                        openArtifactFullscreen(expandedArtifact)
                      }
                      threadAssets={threadAssets}
                      docsByToken={docsByToken}
                      onOpenCitation={openCitation}
                      plots={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedPlots
                          : expandedArtifact.kind === 'report'
                            ? reportPlots
                            : undefined
                      }
                      quotes={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedQuotes
                          : expandedArtifact.kind === 'report'
                            ? reportQuotes
                            : undefined
                      }
                      populationId={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedPopulationId
                          : undefined
                      }
                      strips={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedStrips
                          : expandedArtifact.kind === 'report'
                            ? reportStrips
                            : undefined
                      }
                      decisionConfidence={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedDecisionConfidence
                          : undefined
                      }
                      plotRegion="plot"
                    />
                  ) : (
                    <ThreadArtifactPane
                      artifacts={sourcesOnly ? [] : selectedRun ? runArtifacts.filter((item) => item.runToken === selectedRun.token) : runArtifacts}
                      run={sourcesOnly ? null : sessionRun}
                      template={historical ? null : runTemplate}
                      isLoading={runLoading}
                      error={runError}
                      livingIsLoading={livingArtifactsLoading}
                      livingError={livingArtifactsError}
                      pendingCreativeDraftCount={pendingCreativeDraftCount}
                      onRetryLiving={() => {
                        void refreshLivingArtifacts()
                      }}
                      showProgress={!sourcesOnly && (historical || showRailProgress)}
                      calculations={calculationSources}
                      focusedCalculation={calculationFocus}
                      docsByToken={docsByToken}
                      onOpenCitation={openCitation}
                      onRetryStep={isReadOnlyForViewer ? undefined : historical ? async (stepId) => {
                        const step = selectedRun?.steps.find((item) => item.stepId === stepId)
                        if (!selectedRun || !step) return
                        try {
                          const retried = await workflowRunsApi.retry(selectedRun.token, { stepIndex: step.stepIndex })
                          adoptRun(retried)
                        } catch {
                          toast.error(t('threads.sessions.loadFailed'))
                        }
                      } : retryStep}
                      onSubmitGate={submitGate}
                      onCancelRun={cancelRun}
                      onOpenArtifactFullscreen={openArtifactFullscreen}
                      onOpenStudyReport={(studyToken) => openStudyFrame(studyToken, 'results')}
                      language={thread?.language}
                      assets={threadAssets}
                      onRemoveAsset={isReadOnlyForViewer ? undefined : handleRemoveAsset}
                      plots={studyDocPlots}
                      quotes={studyDocQuotes}
                      populationId={studyDocPopulationId}
                      strips={studyDocStrips}
                    />
                  ))
  }

  /** The thread's turns and assets from one read, as a page load sets them. */
  async function reloadThreadTurns() {
    if (!token) return
    const [messages, assets] = await Promise.all([knowledgeThreadsApi.listMessages(token), threadAssetsApi.list(token)])
    if (preparedOriginThreadRef.current !== token) return
    setThreadAssets(assets)
    setTurns(withRunCommandChip<LocalTurn>(messages.map((message) => {
      const turn = messageToTurn(message)
      const attachments = turnAttachmentsFromAssets(assets, message.token)
      return attachments ? { ...turn, attachments } : turn
    }), run, runAudienceChipName))
  }

  async function refreshPreparedStudyOrigin(originMessageToken: string | null | undefined, existing: 'preserve' | 'replace' = 'preserve') {
    if (!token || !originMessageToken) return
    const messages = await knowledgeThreadsApi.listMessages(token)
    if (preparedOriginThreadRef.current !== token) return
    const origin = messages.find((message) => message.token === originMessageToken)
    if (!origin) return
    // Adopt only the saved origin this preparation names; current streamed
    // exchanges retain their existing local identities and ordering.
    setTurns((current) => adoptPersistedMessage<LocalTurn>(current, messageToTurn(origin), existing))
  }

  async function selectSuggestion(suggestion: TurnSuggestions["suggestions"][number]) {
    if (!token || pickingTwins) return
    if (suggestion.kind === 'image') {
      await runAsk(suggestion.spec.ask, {
        clientTurnId: await suggestionActionId(token, suggestionTurnKey, suggestion),
      })
      return
    }
    if (suggestion.kind === 'quick_study') {
      await runAsk(suggestion.spec.ask, {clientTurnId: await suggestionActionId(token, suggestionTurnKey, suggestion), quickStudy: {kind: 'start', audience: {audienceToken: suggestion.spec.audience.audienceToken,
        plannedSampleSize: suggestion.spec.audience.plannedSampleSize ?? DEFAULT_PLANNED_SAMPLE_SIZE, ...(suggestion.spec.audience.populationFilter ? {populationFilter: suggestion.spec.audience.populationFilter} : {}),
        ...(suggestion.spec.audience.segmentLabel ? {segmentLabel: suggestion.spec.audience.segmentLabel} : {})}}})
      return
    }
    if (suggestion.kind === 'study') {
      if (preparingStudyRef.current) {
        return
      }
      preparingStudyRef.current = true
      try {
        const body = studySuggestionPreparation(suggestion)
        const prepared = await knowledgeThreadsApi.prepareStudy(token, {
          ...body,

        })
        if (prepared.presentation === 'quick') {
          if (authUser?.id) await threadStudies.refresh()
          await refreshPreparedStudyOrigin(prepared.threadOrigin?.originMessageToken)
          await refreshAfterTurn()
        } else if (prepared.draftToken) {
          openStudyFrame(prepared.draftToken, 'draft')
        }
      } catch {
        toast.error(t('threads.sessions.studyPrepareFailed'))
      } finally {
        preparingStudyRef.current = false
      }
      return
    }
    setPickingTwins(true)
    try {
      const picked = await assistantSessionsApi.pickTwins(suggestion.spec.twins.audienceToken, {
        populationFilter: suggestion.spec.twins.populationFilter,
        keywords: suggestion.spec.twins.keywords,
        count: 6,
        language: resolveThreadLanguage(thread?.language),
      })
      setRoomSuggestion({ suggestion, ...picked })
    } catch {
      toast.error(t('threads.sessions.pickFailed'))
    } finally { setPickingTwins(false) }
  }

  /** Fork (a viewer) or Duplicate (the owner): a private copy, then open it. */
  /** `fork` for a viewer, `duplicate` for the owner: same copy, its own toast. */
  async function handleForkThread(action: 'fork' | 'duplicate') {
    if (!thread || isForking) {
      return
    }
    setIsForking(true)
    try {
      const created = await knowledgeThreadsApi.fork(thread.token)
      upsertSidebarThread(created)
      toast.success(
        action === 'duplicate'
          ? t('threads.detail.fork.duplicated')
          : t('threads.detail.fork.done'),
      )
      navigate(ROUTES.thread(created.token))
    } catch {
      toast.error(t('threads.detail.fork.failed'))
    } finally {
      setIsForking(false)
    }
  }

  /* Only the sharing fields move. Keeping the thread's own activity times means
     the rail sync below patches the row in place instead of lifting it to the
     top, since a share is not activity. */
  function handleThreadShareChange(updated: KnowledgeThreadView) {
    setThread((previous) =>
      previous
        ? { ...previous, visibility: updated.visibility, isShared: updated.isShared }
        : updated,
    )
  }

  async function handleScopeApply(next: KnowledgeScopeSelection) {
    const previousScope = scope
    setScope(next)
    if (!thread) {
      return
    }
    try {
      const updated = await knowledgeThreadsApi.update(thread.token, {
        scope: {
          sourceMode: next.sourceMode ?? 'auto',
          ...(next.sourceKinds ? { sourceKinds: next.sourceKinds } : {}),
          ...(next.socialWindow ? { socialWindow: next.socialWindow } : {}),
          web: next.web !== false,
          library: next.library !== false,
          mode: next.mode,
          documentTokens: next.documentTokens,
          collectionTokens: next.collectionTokens,
          // The picker has no Collections tab yet: applying it replaces a
          // collection scope with the picked sources, as in the lobby.
          libraryCollectionTokens: next.libraryCollectionTokens ?? [],
        },
      })
      setThread(updated)
    } catch {
      // The server kept the old scope, so the chip must too.
      setScope(previousScope)
      toast.error(t('chatChrome.updateScopeFailed'))
    }
  }

  if (loading || !thread) {
    return (
      <PageLayout variant="fullBleed">
        <PageContent
          className={THREAD_PAGE_SHELL_CLASS}
          data-testid="thread-detail-page"
        >
          <div className={THREAD_HEADER_CLASS}>
            <Skeleton className="h-5 w-64" />
          </div>
          <div
            className={cn(THREAD_BODY_ROW_CLASS, THREAD_GUTTER_CLASS)}
            data-testid="thread-body-row"
          >
            <ThreadDetailLoadingShell />
          </div>
        </PageContent>
      </PageLayout>
    )
  }

  const chipLabel = scopeChipLabel(scope)

  /* Turns before the current exchange. `turns.length` when the thread holds no
     question at all, which leaves the group empty rather than swallowing a
     column of answers that has no question to park. */
  const exchangeStart = lastQuestionIndex >= 0 ? lastQuestionIndex : turns.length
  /* Where the results sit. The run's own completion announcement first, the
     originating ask as the fallback for a run that predates announcements; on
     a live run, the run's own opening turn. Null leaves the pin at the end of
     the current exchange, which is what a just-finished run, and a live run
     with no opening turn of its own, want. */
  const pinAfterIndex = studyPinAfterIndex(
    turns,
    run?.token,
    originatingCommandTurnId,
    exchangeStart,
    { isLive: isRunActive },
  )

  /* The live run's cards render ABOVE the current exchange, so a message typed
     mid-run reads under them (D6). Two things follow: the pin in the historic
     loop is this run's, not a finished one's, and the exchange's viewport-tall
     parking floor has to go — it would push the message a screen below the
     cards it answers. */
  const livePinAboveExchange =
    isRunActive && pinAfterIndex !== null && pinAfterIndex < exchangeStart
  const appliedExchangeMinHeight = livePinAboveExchange ? 0 : exchangeMinHeight

  /* The inline gate is where approve, reject, revise and cancel live. A viewer
     of a shared thread can act on none of them, so it is not rendered. */
  const threadRunGate = isReadOnlyForViewer ? null : (
    <ThreadRunGate
      run={run}
      template={runTemplate}
      onSubmitGate={submitGate}
      onReviseStep={reviseStep}
      onUpdateSegment={updateSegment}
      onCancelRun={cancelRun}
      onRevisionHandleChange={setGateRevisionHandle}
      onDockTweenChange={handleDockTweenChange}
      language={thread?.language}
      collapseSignal={gateCollapseSignal}
    />
  )

  /* Computed before any `renderTurn` call. The function is hoisted, and the
     step-note pin below calls it; reading these `let`s first would throw
     before the run pane paints. */
  let latestAssistantTurnId = ''
  let latestAssistantStreaming = false
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const candidate = turns[index]
    if (candidate?.role === 'assistant') {
      latestAssistantTurnId = candidate.id
      latestAssistantStreaming = Boolean(candidate.streaming)
      break
    }
  }

  /* The pin sits at the end of the current exchange on a live run, so the
     run's step-handoff and gate-decision lines would otherwise render above
     ALL of the run's cards in the turns column instead of between the cards
     of consecutive steps. Pull them out of the column here (both loops
     below skip `pinnedNoteTurnIds`) and hand the rendered nodes to
     `ThreadInlineArtifacts`, which interleaves them with the step groups.
     Legacy runs without step attribution (`run?.steps` empty) keep today's
     column placement. */
  const runStepNotes =
    (run?.steps?.length ?? 0) > 0
      ? currentAttemptWorkflowRunStepNotes(
          collectWorkflowRunStepNotes(turns, run?.token),
          run?.steps ?? [],
        )
      : []
  function openQuickStudyResults(studyToken: string) {
    const receipt = findingsAttention.filter((item) => item.target.threadToken === token && item.target.studyToken === studyToken)
      .sort((left, right) => right.target.artifactRevision - left.target.artifactRevision)[0]
    setQuickFindingsTarget(receipt?.target ?? null)
    openStudyFrame(studyToken, 'results')
  }

  function acknowledgeRenderedFindings(target: StudyArtifactAttentionTarget) {
    setConfirmedFindingsTarget((current) => current && sameStudyArtifactTarget(current, target) ? current : target)
    const receipt = findingsAttention.find((item) => sameStudyArtifactTarget(item.target, target))
    if (!receipt) return
    void refreshLivingArtifacts().then(() => studyAttention.acknowledge(receipt.attentionToken, 'viewed')).catch(() => toast.error(t('threads.quickStudy.attentionFailed')))
  }

  async function postStudyFindings(target: StudyArtifactAttentionTarget) {
    if (postingFindingsRef.current) {toast.info(t('threads.quickStudy.posting')); return false}
    if (target.threadToken !== token) return false
    const actionKey = `${target.artifactToken}:${target.artifactRevision}`
    postingFindingsRef.current = actionKey
    setPostingFindings(actionKey)
    try {
      const result = await studyFindingsApi.post(target)
      if (!sameStudyArtifactTarget(result.target, target)) throw new Error('Unexpected posted findings target')
      await refreshPreparedStudyOrigin(result.messageToken).catch(() => toast.error(t('threads.quickStudy.postRefreshFailed')))
      const receipt = findingsAttention.find((item) => sameStudyArtifactTarget(item.target, target))
      if (receipt) await studyAttention.acknowledge(receipt.attentionToken, 'posted').catch(() => toast.error(t('threads.quickStudy.attentionFailed')))
      await studyAttention.refresh().catch(() => toast.error(t('threads.quickStudy.attentionFailed')))
      if (preparedOriginThreadRef.current === target.threadToken) requestAnimationFrame(() => {
        if (preparedOriginThreadRef.current === target.threadToken) originNavigation.returnToLatest()
      })
      return true
    } catch {
      toast.error(t('threads.quickStudy.postFailed'))
      return false
    } finally {
      if (postingFindingsRef.current === actionKey) {postingFindingsRef.current = null; setPostingFindings(null)}
    }
  }

  function acknowledgeNativeQuick(draftToken: string, revision: number, stage: 'questions' | 'results', runToken?: string) {
    const receipt = studyAttention.items.find(item => isQuickStudyAttentionTarget(item.target) && item.target.threadToken === token &&
      item.target.draftToken === draftToken && item.target.draftRevision === revision && item.target.stage === stage &&
      (item.target.stage !== 'results' || item.target.runToken === runToken))
    if (receipt) void studyAttention.acknowledge(receipt.attentionToken, 'viewed').catch(() => toast.error(t('threads.quickStudy.attentionFailed')))
  }

  async function postNativeQuickFindings(draftToken: string, runToken: string) {
    const existing = turns.find(turn => turn.quickStudyFindings?.draftToken === draftToken && turn.quickStudyFindings.runToken === runToken)
    if (existing) {
      closeNativeQuickStudy()
      requestAnimationFrame(() => originNavigation.showMessage(turnMessageToken(existing)))
      return
    }
    if (postingQuickRun) return
    setPostingQuickRun(runToken)
    try {
      const posted = await quickStudiesApi.postFindings(token, draftToken, runToken)
      setPostedQuickRuns(previous => new Set([...previous, runToken]))
      await refreshPreparedStudyOrigin(posted.messageToken, 'replace')
      const receipt = studyAttention.items.find(item => isQuickStudyAttentionTarget(item.target) && item.target.stage === 'results' && item.target.runToken === runToken)
      if (receipt) await studyAttention.acknowledge(receipt.attentionToken, 'posted')
      await nativeQuickStudies.refresh()
      closeNativeQuickStudy()
      requestAnimationFrame(() => originNavigation.showMessage(posted.messageToken))
    } catch {toast.error(t('threads.quickStudy.postFailed'))}
    finally {setPostingQuickRun(null)}
  }

  function acknowledgeDisplayedQuestions(draftToken: string, revision: number) {
    const draft = threadStudies.items.find((item) => item.id === draftToken)
    if (!draft || draft.revision !== revision || draft.threadOrigin.sourceThreadToken !== token || !draft.threadOrigin.originMessageToken) return
    const receipt = studyAttention.items.find((item) => isStudyQuestionnaireAttentionTarget(item.target) &&
      item.target.threadToken === token && item.target.draftToken === draftToken && item.target.draftRevision === revision &&
      item.target.originMessageToken === draft.threadOrigin.originMessageToken)
    if (!receipt || questionAttentionInFlight.current.has(receipt.attentionToken)) return
    questionAttentionInFlight.current.add(receipt.attentionToken)
    void studyAttention.acknowledge(receipt.attentionToken, 'viewed').catch(() => {
      toast.error(t('threads.quickStudy.attentionFailed'))
    }).finally(() => questionAttentionInFlight.current.delete(receipt.attentionToken))
  }

  function renderCanonicalStudies(turn: LocalTurn) {
    if (turn.role !== 'user') return null
    const messageToken = turn.serverToken ?? turn.id
    return <>{threadStudies.items.filter((draft) => draft.presentation === 'quick' &&
      draft.threadOrigin.sourceThreadToken === token && draft.threadOrigin.originMessageToken === messageToken &&
      draft.threadOrigin.completionDelivery === 'origin_card').map((draft) => <ThreadStudyCard key={draft.id} draft={draft}
        onQuestionsDisplayed={acknowledgeDisplayedQuestions}
        questionAttentionKey={studyAttention.items.filter((receipt) => isStudyQuestionnaireAttentionTarget(receipt.target) && receipt.target.draftToken === draft.id).map((receipt) => receipt.attentionToken).join(',')}
        readOnly
        findingsReady={Boolean(draft.launchedStudyToken && (resolveStudyOrigin({threadToken: token, studyToken: draft.launchedStudyToken, originMessageToken: draft.threadOrigin.originMessageToken ?? ''}, studyAttention.items, livingArtifactDtos, confirmedFindingsTarget) || hasStudyFindingsSource({threadToken: token, studyToken: draft.launchedStudyToken, originMessageToken: draft.threadOrigin.originMessageToken ?? ''}, livingArtifactDtos)))}
        onLaunched={async (studyToken) => {setLaunchedStudyToken(studyToken); await threadStudies.refresh(); await studyAttention.refresh()}}
        onOpenResults={openQuickStudyResults} />)}
      {nativeQuickStudies.items.filter(draft => draft.originMessageToken === messageToken).map(draft => <ThreadQuickStudyCard
        key={draft.draftToken} threadToken={token} draft={draft} readOnly={isReadOnlyForViewer}
        openingResults={postingQuickRun === draft.activeRun?.runToken}
        onReview={() => draft.activeRun?.status === 'completed' ? void postNativeQuickFindings(draft.draftToken, draft.activeRun.runToken) : openNativeQuickStudy(draft.draftToken)} onRefresh={() => nativeQuickStudies.refresh()}
        onCancelled={() => {clearArmedWorkflow(); setIntakeDirection(null)}} />)}
    </>
  }

  const pinnedNoteTurnIds = new Set(runStepNotes.map((note) => note.turn.id))
  const stepNoteNodes = runStepNotes.map((note) => ({
    key: note.turn.id,
    stepIndex: note.stepIndex,
    placement: note.placement,
    attempt: note.attempt,
    node: renderTurn(note.turn),
  }))

  const studyPin = (
    <>
      <ThreadRunResults
        run={run}
        className="mt-2"
        onSegmentByChange={setResultsSegmentBy}
        onExpandArtifact={openArtifactFullscreen}
      />
      <ThreadInlineArtifacts
        items={studyPinArtifacts}
        onExpand={openArtifactFullscreen}
        threadToken={token || null}
        steps={run?.steps ?? []}
        templateSlug={run?.templateSlug}
        language={thread?.language}
        stepNotes={stepNoteNodes}
      />
      {docksGateOnComposer ? null : threadRunGate}
    </>
  )

  /**
   * One turn, rendered the same wherever it sits in the column. Lifted out of
   * the JSX so the trailing exchange can be grouped without a second copy of
   * this: see `exchangeMinHeight`.
   */
  /** The retry chip: the same user message, attachments included, as a new turn. */
  function retryImageTurn(turnId: string) {
    const request = imageRetryRequestsRef.current.get(turnId)
    if (!request) {
      return
    }
    void runAsk(request.text, { resend: request })
  }

  function questionBeforeTurn(turnId: string): string {
    const index = turns.findIndex((item) => item.id === turnId)
    for (let i = index - 1; i >= 0; i -= 1) {
      if (turns[i].role === 'user') {
        return turns[i].text
      }
    }
    return ''
  }

  /** A turn as the thread shows it, with an image batch folded into one ask and one image block. */
  function renderThreadTurn(turn: LocalTurn) {
    const slot = imageApprovalSlotsByTurn.get(turn.id)
    if (!slot) return renderTurn(turn)
    if (slot.placement === 'hidden') return null
    const { group } = slot
    if (slot.placement === 'bubble') {
      return renderTurn({
        ...turn,
        text: t('threadImage.approval.batchAsk', { count: group.pairs.length }),
      })
    }
    return renderImageApprovalImages(group, turn.id)
  }

  /** The batch's images, in the thread or on their own card. */
  function renderImageApprovalImages(group: ImageApprovalGroup<LocalTurn>, key: string) {
    const batchFormats = imageApprovalBatches.batches[group.cardToken]?.formats
    // Earlier tries stay in the thread's files: the card shows one image per format.
    const imageTurns = newestImageTurns(group.imageTurns)
    return (
      <div
        key={key}
        className={cn('grid gap-3 sm:gap-4', imageTurns.length > 1 && 'grid-cols-2')}
        data-testid="thread-image-approval-images"
      >
        {imageTurns.map((imageTurn, index) => {
          const request = group.requests.find((item) => item.requestId === imageTurn.imageApprovalRequestId)
          const caption = imageTurn.streaming
            ? null
            : imageFormatCaption(t, request, imageTurn.imageApprovalFormatId, batchFormats ?? request?.selectedFormats)
          return (
            <div key={imageTurn.id} className="min-w-0">
              {renderTurn(imageTurn)}
              {caption ? (
                <Text size="xs" color="muted" data-testid={`thread-image-approval-caption-${index}`}>
                  {caption}
                </Text>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  function renderTurn(sourceTurn: LocalTurn) {
    const turn = withSourcePreviews(sourceTurn, sourcePreviews.get(sourcePreviewMessageToken(sourceTurn)))
    const planMessageToken = turn.serverToken ?? turn.id
    if (turn.role === 'user') {
      const commandLabel = userTurnCommandChipLabel(turn, {
        lastQuestionId,
        originatingTurnId: originatingCommandTurnId,
        armedDisplayName: armedWorkflow?.displayName ?? null,
        runDisplayName: run?.templateDisplayName ?? null,
      })
      const audienceLabel = userTurnAudienceChipLabel(turn, {
        lastQuestionId,
        originatingTurnId: originatingCommandTurnId,
        armedAudienceName: armedAudienceChipName,
        runAudienceName: runAudienceChipName,
      })
      /* TWO CHIPS above the sent message, the same pair the composer showed
         (D2): the command, and the audience it was bound to. The audience chip
         renders only when a name resolves — never a blank second chip. */
      const commandChip = commandLabel ? (
        <span className="flex min-w-0 items-center gap-1.5">
          <ActiveWorkflowChip
            displayName={commandLabel}
            testId="thread-user-command-chip"
          />
          {audienceLabel ? (
            <ActiveWorkflowChip
              displayName={audienceLabel}
              icon="audience"
              testId="thread-user-command-chip-audience"
            />
          ) : null}
        </span>
      ) : null

      return (
        <div
          key={turn.id}
          ref={
            turn.id === lastQuestionId ? lastQuestionRef : null
          }
          className="ml-auto flex w-fit max-w-[80%] flex-col items-start gap-1.5"
          data-thread-message-token={turnMessageToken(turn)}
          data-testid="thread-user-turn"
        >
          {commandChip}
          {turn.attachments && turn.attachments.length > 0 ? (
            <AttachmentTiles
              items={turn.attachments}
              testIdPrefix="thread-user-attachment"
            />
          ) : null}
          <div
            className="whitespace-pre-wrap break-words rounded-2xl bg-primary/10 px-4 py-3 text-base leading-[1.7] text-foreground"
            data-testid="thread-user-bubble"
          >
            {turn.text}
          </div>
        </div>
      )
    }

    const awaitingFirstToken = Boolean(
      turn.streaming && !turn.text.trim(),
    )

    /* Image tiles sit right after the answer body until the answer places
       that image inline (`asset:<token>`), so it never shows twice. */
    const imageTiles = visibleImageTiles(
      turn.imageProgress,
      placedAssetTokensByTurn[turn.id] ?? NO_PLACED_ASSET_TOKENS,
    )
    /* One retry for the whole turn: it resends the message, so a retry per
       failed tile would only offer the same thing several times. */
    const hasRetryableImage = imageTiles.some(
      (image) => image.stage === 'failed' && image.reason !== 'policy',
    )
    const imageTileRow =
      imageTiles.length > 0 ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-3" data-testid="thread-generated-images">
            {imageTiles.map((image) => (
              <GeneratingImageTile
                key={image.callId}
                image={image}
                summary={
                  turn.activityLegs?.find((leg) => leg.callId === image.callId)?.query
                }
              />
            ))}
          </div>
          {hasRetryableImage && !isReadOnlyForViewer && !turn.imageApprovalRequestId ? (
            <button
              type="button"
              onClick={() => retryImageTurn(turn.id)}
              data-testid="thread-generated-images-retry"
              className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              {t('threadImage.retry')}
            </button>
          ) : null}
        </div>
      ) : null

    return (
      <div
        key={turn.id}
        className="space-y-3"
        data-thread-message-token={turnMessageToken(turn)}
        data-testid={
          isWorkflowRunCompletionAskId(turn.askId)
            ? 'thread-run-completion-announcement'
            : 'thread-assistant-bubble'
        }
      >
        {awaitingFirstToken ? (
          <>
            <ThreadActivityIndicator
              language={thread?.language}
              legs={turn.activityLegs}
              pageData={activityPageData}
            />
            {/* No answer body yet, so the tiles hold their place here. */}
            {imageTileRow}
          </>
        ) : (
          <>
            {/* The checklist folds into one line above the answer at the
                first token, and rebuilds from the saved `toolCalls` after a
                reload (docs/plans/thread-activity-steps.md, Step 4). */}
            <ThreadActivityTrail
              legs={turn.activityLegs}
              language={thread?.language}
              pageData={activityPageData}
              isStreaming={turn.streaming}
            />
            {turn.quickStudyFindings ? <ThreadQuickStudyFindingsMessage findings={turn.quickStudyFindings} threadToken={token} text={turn.text} /> : <ThreadAnswerBody
              text={turn.text}
              citations={turn.citations}
              refused={turn.refused}
              streaming={turn.streaming}
              resolveLabel={citationLabel}
              onOpenCitation={openCitation}
              webCitations={turn.webCitations}
              citationV2={turn.citationV2}
              cardImages={turn.cardImages}
              sourceImagesLoading={turn.sourceImagesLoading}
              onOpenCalculation={(evidenceId) => openCalculation(turn.id, evidenceId)}
              onNavigate={navigate}
              furtherReading={turn.furtherReading}
              judgmentNotes={turn.judgmentNotes}
              threadAssets={threadAssets}
              docsByToken={docsByToken}
              onPlacedAssetTokensChange={(tokens) =>
                recordPlacedAssetTokens(turn.id, tokens)
              }
              imageTiles={imageTileRow}
              language={thread?.language}
              onAnswerPendingChange={
                turn.id === latestAssistantTurnId ? setAnswerStillRevealing : undefined
              }
            />}
          </>
        )}
        {!turn.streaming ? (
          <AssistantReplyCost credits={threadCredits.byMessageToken.get(turnMessageToken(turn))} />
        ) : null}
        {!turn.quickStudyFindings && !turn.streaming && (turn.charts?.length || turn.tables?.length) ? (
          <ThreadFeaturedExhibits charts={turn.charts} tables={turn.tables} />
        ) : null}
        {/* A12: the saved report, inline under the message that announced it. */}
        {!turn.streaming && turn.report ? (
          <ThreadReportCard
            report={turn.report}
            item={runArtifacts.find((artifact) => artifact.publicId === turn.report?.publicId) ?? null}
            onExpand={expandArtifact}
            onFullscreen={openArtifactFullscreen}
            hasPaneAction
          />
        ) : null}
        {/* Agent C: Suggested plan card — sibling of prose, never on composer.
            Callbacks go to D's useThreadPlanExecution; Sounds good / Skip
            auto-open ThreadCommandSeed. */}
        {!turn.streaming &&
        turn.creativeDrafts &&
        turn.creativeDrafts.length > 0 ? (
          <ThreadCreativeDraftSet
            drafts={turn.creativeDrafts}
            onAutosave={(key, patch) =>
              autosaveCreativeDraft(turn, key, patch)
            }
            onConfirm={() => confirmCreativeDraftSet(turn)}
            onCancel={() => void cancelCreativeDraftSet(turn)}
            onExpand={openArtifactFullscreen}
            isConfirming={
              confirmingDraftMessageToken === turnMessageToken(turn)
            }
            isDraftSaved={Boolean(
              savedDraftMessageTokens[turnMessageToken(turn)],
            )}
            threadAssets={threadAssets}
            docsByToken={docsByToken}
          />
        ) : null}
        {!isReadOnlyForViewer && !turn.streaming && Boolean(turn.imageRequests?.length) && (
          <ThreadImageApprovalGroup key={`${token}:${turn.imageRequests!.map((item) => item.requestId).join(',')}`} requests={turn.imageRequests!} disabled={asking}
            batch={imageApprovalBatches.batches[turnMessageToken(turn)]}
            results={imageApprovalCards.get(turnMessageToken(turn))?.imageTurns.length
              ? renderImageApprovalImages(imageApprovalCards.get(turnMessageToken(turn))!, `${turn.id}:images`)
              : null}
            onGenerate={(pending, quality, formats, step, tries, selection) => { imageCardClickRef.current[turnMessageToken(turn)] = selection; void imageApprovalBatches.generate(turnMessageToken(turn), pending, quality, formats, step, tries) }}
            onTryAgain={(pair, quality, formats, tryNumber, selection) => { imageCardClickRef.current[turnMessageToken(turn)] = selection; void imageApprovalBatches.generate(turnMessageToken(turn), [pair], quality, formats, 'try', tryNumber) }}
            onRequote={(reason) => setComposerAskSeed((current) => ({ text: t(reason === 'context' ? 'threadImage.approval.contextRequoteAsk' : 'threadImage.approval.requoteAsk'), nonce: (current?.nonce ?? 0) + 1, keepDraft: true }))}
            threadToken={token}
            messageToken={turnMessageToken(turn)}
            selection={imageCardSelections[turnMessageToken(turn)] ?? null}
            onSelectionChange={assistantPlatformEnabled ? (selection) => setImageCardSelections((prev) => {
              const { [turnMessageToken(turn)]: _previous, ...rest } = prev
              return selection ? { ...rest, [turnMessageToken(turn)]: selection } : rest
            }) : undefined} />
        )}
        {!turn.streaming && turn.socialTrackerProposal ? (
          <ThreadSocialTrackerCard
            proposal={turn.socialTrackerProposal}
            threadToken={token}
            messageToken={turnMessageToken(turn)}
            question={questionBeforeTurn(turn.id)}
            onProposalChange={(proposal) =>
              setTurns((prev) =>
                prev.map((item) =>
                  item.id === turn.id
                    ? { ...item, socialTrackerProposal: proposal }
                    : item,
                ),
              )
            }
          />
        ) : null}
        {!isReadOnlyForViewer && !turn.streaming && turn.preparedConversation ? (
          <ThreadConversationLaunchCard
            prepared={turn.preparedConversation}
            threadToken={token || ''}
            sourceMessageToken={turn.serverToken}
            threadLanguage={thread?.language}
            linkedRooms={launchRooms.rooms}
            linkedRoomsReadMark={launchRooms.readMark}
            hasRoomsReadFailed={launchRooms.hasReadFailed}
            reviewedRoom={turn.serverToken ? reviewedLaunchRooms[turn.serverToken] : undefined}
            onRoomStarted={openStartedRoom}
            onOpenRoom={(roomToken) => {
              setLaunchRoomsReads((reads) => reads + 1)
              dispatchSession({ type: 'select', selection: { kind: 'rooms', token: roomToken } })
              changeRailOpen(true)
            }}
            onReviewTwins={(picked) => {
              const prepared = turn.preparedConversation!
              setRoomSuggestion({
                suggestion: preparedConversationSuggestion(prepared),
                ...picked,
                launch: { participantCount: prepared.participantCount, sourceMessageToken: turn.serverToken },
              })
            }}
          />
        ) : null}
        {!turn.streaming && plansByMessageId[planMessageToken] ? (
          <ThreadPlanCard
            plan={plansByMessageId[planMessageToken]}
            onSoundsGood={() => {
              applyPlanStepHandoff(planMessageToken, confirmSoundsGood(planMessageToken))
            }}
            onDismiss={() => dismissPlan(planMessageToken)}
            onSkip={() => {
              applyPlanStepHandoff(planMessageToken, skipCurrent(planMessageToken))
            }}
            onRetry={() => {
              applyPlanStepHandoff(planMessageToken, retryCurrent(planMessageToken))
            }}
            onStartStep={(stepIndex) => {
              /* Same action as a typed "go", from the card instead of the
                 composer. The card only offers this on the step the plan is
                 actually on, and the index guard keeps it that way if that
                 ever changes. */
              const plan = plansByMessageId[planMessageToken]
              if (!plan || plan.currentIndex !== stepIndex) {
                return
              }
              applyPlanStepHandoff(planMessageToken, stepHandoffFrom(plan))
            }}
          />
        ) : null}
        {turn.stopped ? (
          <Text
            size="xs"
            color="muted"
            data-testid="thread-stopped-note"
          >
            Stopped. This answer is incomplete and has no
            sources.
          </Text>
        ) : null}

      </div>
    )
  }

  const showIntakeStartRow =
    Boolean(intakeDirection) &&
    !isStartingRun &&
    !isConfirmingIntake &&
    !intakeStartUiDismissed
  const intakeAskKey = armedWorkflow ? askInputKey(armedWorkflow) : undefined
  const showIntakeProceedWithoutMedia = Boolean(
    showIntakeStartRow &&
      armedWorkflow &&
      !hasStimulusGateSatisfied(
        armedWorkflow.inputs,
        {
          ...armedInputs,
          ...(intakeAskKey && intakeDirection
            ? { [intakeAskKey]: intakeDirection }
            : {}),
        },
        false,
        referencesStimulus,
      ),
  )
  const showArmedProceedWithoutMedia = Boolean(
    !showIntakeStartRow &&
      armedWorkflow &&
      !isIntakeArmed &&
      blockedArmedAsk &&
      !isStartingRun,
  )

  return (
    <QuickStudyCutPlotsContext.Provider value={cutPlotsSource}>
      {/* Quick-study results in a thread say respondents (owner decision for
          this surface); the Studies report keeps twins. */}
      <QuickStudyNounProvider noun="respondents">
      {reportPageTitle && (
        <Helmet>
          <title>{reportPageTitle}</title>
        </Helmet>
      )}
      <PageLayout variant="fullBleed">
      <PageContent
        className={THREAD_PAGE_SHELL_CLASS}
        data-testid="thread-detail-page"
      >
        <div className={THREAD_HEADER_CLASS} data-testid="thread-header">
            <div className="flex min-w-0 flex-1 items-center gap-1">
              {/* The title names the thread in lists, never in its own header. */}
              <h1 className="sr-only" data-testid="thread-title">
                {titleDraft}
              </h1>
              {/* The menu carries only the schedule's recent runs; scope
                  lives in the composer. */}
              {thread.scheduleSiblings && thread.scheduleSiblings.length > 0 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="thread-title-menu"
                      aria-label={t('chatChrome.threadDetails')}
                      className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel
                      className="font-normal text-xs text-muted-foreground"
                      data-testid="thread-schedule-runs-label"
                    >
                      {t('threads.detail.scheduleRecentRuns', 'Recent runs')}
                    </DropdownMenuLabel>
                    {thread.scheduleSiblings.map((sibling) => (
                      <DropdownMenuItem
                        key={sibling.token}
                        asChild
                        data-testid={`thread-schedule-sibling-${sibling.token}`}
                      >
                        <Link href={ROUTES.thread(sibling.token)}>
                          {sibling.title}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              {thread.forkedFrom ? (
                <Link
                  href={ROUTES.thread(thread.forkedFrom.token)}
                  className="min-w-0 truncate text-xs text-muted-foreground hover:text-foreground"
                  data-testid="thread-forked-from-link"
                >
                  {t('threads.detail.fork.forkedFrom', { title: thread.forkedFrom.title })}
                </Link>
              ) : null}
              {thread.scope?.socialFocus?.topicToken && !isReadOnlyForViewer ? (
                <ThreadSocialTopicLink topicToken={thread.scope.socialFocus.topicToken} />
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {isReadOnlyForViewer ? null : (
                <ThreadShareControl
                  threadToken={thread.token}
                  isShared={isThreadShared}
                  onThreadChange={handleThreadShareChange}
                />
              )}
              {/* One toggle for one drawer. The pane now stacks outputs,
                  assets, workflows, context, and progress, so the control is
                  the same expand glyph as the left sidebar rather than a
                  content label. */}
              {sourceTabs.length === 0 &&
              (assistantPlatformEnabled || hasRunPane ||
                hasArtifacts ||
                hasThreadAssets ||
                calculationSources.length > 0 ||
                threadSources.length > 0) ? (
                <button
                  type="button"
                  data-testid="thread-sources-open"
                  title={railToggleLabel}
                  aria-label={railToggleLabel}
                  aria-expanded={railOpen}
                  className={cn(
                    'relative inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                    railOpen
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                  onClick={() => changeRailOpen(!railOpen)}
                >
                  <PanelRight className="h-[18px] w-[18px]" strokeWidth={1.5} />
                </button>
              ) : null}
            </div>
        </div>

        <div className={THREAD_PAGE_ROW_CLASS} data-testid="thread-page-row">
          <div
            className={THREAD_BODY_ROW_CLASS}
            data-testid="thread-body-row"
          >
          <div className="-mt-14 flex min-w-0 flex-1 flex-col" data-testid="thread-reading-area">
              <div className="relative flex min-h-0 w-full flex-1 flex-col">
              <div
                ref={conversationRef}
                className={cn(THREAD_GUTTER_CLASS, "min-h-0 flex-1 overflow-y-auto overscroll-contain [container-type:inline-size] pb-28 pt-16 [mask-image:linear-gradient(to_bottom,transparent_0,black_56px)] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0,black_56px)]")}
                style={
                  composerHeight > 0
                    ? /* The wrap's own `pt-8` is a transparent fade, so the
                         reservation clears that too: content must end above the
                         gradient, not inside it. */
                      { paddingBottom: `${composerHeight + 40}px` }
                    : undefined
                }
                data-testid="thread-conversation"
              >
                <div
                  className={cn('mx-auto w-full space-y-4', THREAD_COLUMN_CLASS)}
                  data-testid="thread-reading-column"
                >
                  {/* D3: guarded on loading and an active run too, not just an empty
                      `turns` array — a bare lobby start and F5 mid-run both have zero
                      turns for a beat, and this must not flash between "loaded" and
                      "the first turn actually rendered". */}
                  {turns.length === 0 && !loading && !isRunActive ? (
                    <Text
                      size="sm"
                      color="muted"
                      data-testid="thread-empty-placeholder"
                    >
                      {t('threads.detail.emptyThread')}
                    </Text>
                  ) : null}
                  {turns.slice(0, exchangeStart).map((turn, index) => (
                    <Fragment key={turn.id}>
                      {pinnedNoteTurnIds.has(turn.id) ? null : renderThreadTurn(turn)}
                      {renderCanonicalStudies(turn)}
                      {pinAfterIndex === index ? (
                        <div
                          className="space-y-2"
                          data-testid={
                            livePinAboveExchange
                              ? 'thread-live-study-pin'
                              : 'thread-historic-study-pin'
                          }
                        >
                          {studyPin}
                        </div>
                      ) : null}
                    </Fragment>
                  ))}

                  {/* THE CURRENT EXCHANGE: the newest question, its answer, and
                      — when this ask originated the latest run — the study
                      results and artifacts. Parking floor stays on this box.
                      A historic pin is rendered above, next to an earlier ask,
                      when the reader continued the thread after the run. The
                      box remains the reading column's last child either way. */}
                  <div
                    className="space-y-4"
                    data-testid="thread-current-exchange"
                    style={
                      appliedExchangeMinHeight > 0
                        ? { minHeight: appliedExchangeMinHeight }
                        : undefined
                    }
                  >
                    {turns.slice(exchangeStart).map((turn, offset) => (
                      <Fragment key={turn.id}>
                        {pinnedNoteTurnIds.has(turn.id) ? null : renderThreadTurn(turn)}
                      {renderCanonicalStudies(turn)}
                        {/* The announcement can land inside the current
                            exchange too, and a follow-up typed after it must
                            not drag the results below itself. */}
                        {pinAfterIndex === exchangeStart + offset ? (
                          <div className="space-y-2">{studyPin}</div>
                        ) : null}
                      </Fragment>
                    ))}

                    {/* Study cards that fail to load render nothing; the hooks log it internally. */}
                    <SuggestionSlot threadToken={token || ''} turnKey={suggestionTurnKey} pending={asking} revealed={!latestAssistantStreaming && !answerStillRevealing} suggestions={suggestions} selecting={pickingTwins} onSelect={(suggestion) => void selectSuggestion(suggestion)} />

                    {/* Intake start / keep talking sit in the thread, same
                        place as creative Confirm — not inside the composer. */}
                    <div ref={setCommandConfirmationTarget} data-testid="thread-command-confirmation-target" />
                    <ThreadIntakeStartActions
                      showIntakeRow={showIntakeStartRow}
                      showProceedWithoutMedia={showIntakeProceedWithoutMedia}
                      showRetry={showIntakeRetry}
                      showArmedProceed={showArmedProceedWithoutMedia}
                      onProceedWithoutMedia={() => {
                        armedProceedWithoutStimulusRef.current = true
                        void confirmIntakeDirection()
                      }}
                      onKeepTalking={handleKeepTalking}
                      onRetry={() => void confirmIntakeDirection()}
                      onArmedProceedWithoutMedia={
                        handleArmedProceedWithoutMedia
                      }
                    />

                    {pinAfterIndex === null ? studyPin : null}
                    {/* Busy line for the live step, UNDER the cards already
                        delivered: what is happening now reads after what has
                        happened, never above it. Prompt/report use the step
                        title; twin sampling keeps its own placeholder.
                        Prose-gate Continue sits with the artifacts above. */}
                    <ThreadRunActivityIndicator
                      run={run}
                      isStartingRun={isStartingRun}
                      language={thread?.language}
                    />
                  </div>
                  </div>
              </div>

              <div
                ref={measureComposerWrap}
                className={cn(THREAD_GUTTER_CLASS, "pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-background from-[33%] to-transparent pt-8 [&>*]:pointer-events-auto pb-[env(safe-area-inset-bottom)]")}
                data-testid="thread-composer-wrap"
              >
                {!isReadOnlyForViewer && findingsAttention.length > 0 ? <div className={cn('mx-auto mb-2 flex flex-wrap justify-center gap-2', THREAD_COLUMN_CLASS)} data-testid="thread-study-ready-chips">
                  {findingsAttention.filter((receipt) => receipt.target.threadToken === token).map((receipt) => <Button key={receipt.attentionToken}
                    variant="outline" size="sm" className="rounded-full bg-card" disabled={postingFindings !== null}
                    aria-label={t('threads.quickStudy.postResults')} onClick={() => void postStudyFindings(receipt.target)}>
                    {postingFindings === `${receipt.target.artifactToken}:${receipt.target.artifactRevision}` ? t('threads.quickStudy.posting') : t('threads.quickStudy.resultsReadyChip')}
                  </Button>)}
                </div> : null}
                {!isReadOnlyForViewer ? <div className={cn('mx-auto mb-2 flex flex-wrap justify-center gap-2', THREAD_COLUMN_CLASS)} data-testid="thread-native-quick-ready-chips">
                  {nativeQuickStudies.items.filter(draft => draft.activeRun?.status === 'completed' && !draft.activeRun.findingsPosted && !postedQuickRuns.has(draft.activeRun.runToken)).map(draft => <Button
                    key={draft.activeRun!.runToken} variant="outline" size="sm" className="rounded-full bg-background" disabled={postingQuickRun !== null}
                    onClick={() => void postNativeQuickFindings(draft.draftToken, draft.activeRun!.runToken)}>
                    {postingQuickRun === draft.activeRun!.runToken ? t('threads.quickStudy.posting') : t('threads.quickStudy.resultsReadyChip')}
                  </Button>)}
                </div> : null}
                {originNavigation.awayFromBottom ? (
                  <div className={cn('mx-auto mb-2 flex w-full justify-center', THREAD_COLUMN_CLASS)}>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="rounded-full bg-background shadow-sm"
                      data-testid="thread-return-to-latest"
                      aria-label={t('threads.detail.returnToLatest')}
                      onClick={originNavigation.returnToLatest}
                    >
                      <ArrowDown className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                ) : null}
                {isReadOnlyForViewer ? (
                  /* Somebody else's thread. The composer and its whole control
                     row are replaced rather than disabled: every control down
                     there — attach, commands, scope, effort, arm a workflow —
                     is a write this reader cannot make, and a row of dimmed
                     buttons invites the click that earns the 404 anyway. The
                     conversation above stays fully readable, which is the
                     entire point of sharing it. */
                  <div className={cn('mx-auto w-full', THREAD_COLUMN_CLASS)}>
                    <div
                      className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-twin-chat-surface px-5 py-4 text-sm text-muted-foreground"
                      data-testid="thread-composer-read-only"
                    >
                      <span>{readOnlyThreadNotice(thread?.language)}</span>
                      {/* The one way forward for a viewer: a private copy they can keep asking in. */}
                      <Button
                        type="button"
                        size="sm"
                        disabled={isForking}
                        onClick={() => void handleForkThread('fork')}
                        data-testid="thread-fork-button"
                      >
                        {t('threads.detail.fork.button')}
                      </Button>
                    </div>
                  </div>
                ) : (
                <div className={cn('mx-auto w-full', THREAD_COLUMN_CLASS)}>
                  {/* Step 2 backstop: a persisted arm existed but failed
                      rehydrate validation. Storage-triggered only — messages
                      carry no mode, so turn history cannot infer this. */}
                  {wasArmedWorkflowDropped && !armedWorkflowSlug ? (
                    <div
                      className="mb-2 flex items-center justify-between gap-3"
                      data-testid="thread-armed-resume-offer"
                    >
                      <p className="text-xs text-muted-foreground">
                        {t('threads.detail.armedCleared')}
                      </p>
                      <button
                        type="button"
                        onClick={dismissArmedWorkflowDrop}
                        className="shrink-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                        data-testid="thread-armed-resume-dismiss"
                      >
                        {t('dismiss', { ns: 'common' })}
                      </button>
                    </div>
                  ) : null}
                  {token && assistantPlatformEnabled && !loading && thread?.token === token && !turns.some((turn) => turn.role === 'user') && <CreativePresetLine selection={creativeContext.selection} className="mb-2" />}
                  <ChatComposer
                    messageLimit={ASSISTANT_MESSAGE_MAX_LENGTH}
                    onSend={handleComposerSend}
                    seed={composerAskSeed}
                    isBusy={asking || isStartingRun || creativeContext.saving}
                    onStop={handleStop}
                    onAddFiles={ingestThreadFiles}
                    sendBlockedHint={threadSendBlockedHint}
                    requireText={composerTiles.length === 0}
                    /* A revisable docked gate owns the box: a send is routed
                       into the gate as a revision, so the prompt says so.
                       Keyed on the handle and not on `docksGateOnComposer`,
                       because a non-owner sees the same card while their send
                       still falls through to an ordinary ask. Ahead of the
                       armed-workflow label, which is unreachable anyway while
                       a run is live. */
                    placeholder={
                      gateRevisionHandle
                        ? threadCopy.askChanges
                        : armedWorkflow
                          ? (armedWorkflow.inputs.find(
                              (input) => input.type === 'text',
                            )?.label ?? threadCopy.askNext)
                          : threadCopy.askNext
                    }
                    dataTestId="thread-composer"
                    textareaDataTestId="thread-composer-input"
                    /* Same thin layout as rooms: one row at rest, with controls
                       inside the surface and a full-width field as text grows. */
                    flat
                    density="thin"
                    commands={composerCommands}
                    onCommand={
                      armedWorkflowSlug ? undefined : handleTypedCommand
                    }
                    commandPreviewPlacement="above"
                    /* Questions preview is the only composer variation: same
                       surface as the revision textarea, hairline between
                       them. Scan / brief / report Continue stays in the
                       thread. `onRevisionHandleChange` still routes typed
                       sends from whichever mount is live. */
                    aboveSlot={docksGateOnComposer ? threadRunGate : undefined}
                    headerSlot={
                      /* An armed workflow labels what the textarea is for (the
                         placeholder becomes its own input label), so it reads
                         as a label on the field rather than as chrome beside
                         it. `undefined` when nothing is armed, so the box does
                         not pay for the header row's margin. */
                      run && isRunActive ? (
                        /* The LIVE run's own name, read off the run rather
                           than the armed state: a run underway is a fact, not
                           a choice to dismiss. Locked (no `onClear`), and so is
                           the audience chip beside it — the panel a running
                           study is drawn from is no more dismissible than the
                           study itself. */
                        <div className="flex min-w-0 items-center gap-2">
                          <ActiveWorkflowChip
                            displayName={run.templateDisplayName}
                          />
                          {runAudienceChipName ? (
                            <ActiveWorkflowChip
                              displayName={runAudienceChipName}
                              icon="audience"
                              testId="home-lab-active-workflow-audience"
                            />
                          ) : null}
                          {/* Who answers, while the run is still waiting on
                              the review panel: the segment it will draw from,
                              edited on the panel's chips above. */}
                          {run.status === 'awaiting_approval' && runSegmentChipLabel ? (
                            <ActiveWorkflowChip
                              displayName={runSegmentChipLabel}
                              icon="audience"
                              testId="home-lab-active-workflow-segment"
                            />
                          ) : null}
                        </div>
                      ) : armedSkillCommand ? (
                        /* A skill tag names itself by the skill's own name;
                           its `×` disarms the tag and nothing else. */
                        <ActiveWorkflowChip
                          displayName={armedSkillCommand.label}
                          onClear={() => setArmedSkillCommand(null)}
                          testId="thread-armed-skill"
                        />
                      ) : armedCommand && isReportIntakeArmed ? (
                        /* A native command has no template to name the chip
                           after, and without a chip there is no way out of the
                           scoping beat: the composer's own `/` list is off
                           while anything is armed. The command's label is the
                           name the reader picked, so it is the honest one. */
                        <ActiveWorkflowChip
                          displayName={armedCommand.label}
                          onClear={handleClearArmedWorkflow}
                        />
                      ) : armedWorkflow ? (
                        <div className="flex min-w-0 items-center gap-2">
                          {/* TWO CHIPS, NEVER ONE RENAMED CHIP (D2). The bound
                              audience used to REPLACE the command's name here,
                              so the reader saw one or the other and never both.
                              The command keeps its own name; the audience gets
                              a chip of its own, inert and without a dismiss
                              (D3) — the command's `×` clears the pair. */}
                          <ActiveWorkflowChip
                            displayName={armedWorkflow.displayName}
                            onClear={handleClearArmedWorkflow}
                          />
                          {armedAudienceChipName ? (
                            <ActiveWorkflowChip
                              displayName={armedAudienceChipName}
                              icon="audience"
                              testId="home-lab-active-workflow-audience"
                            />
                          ) : null}
                          {hasArmedStimulus ? (
                            <Text
                              size="sm"
                              color="muted"
                              as="span"
                              data-testid="thread-armed-stimulus-bound"
                            >
                              Stimulus bound
                            </Text>
                          ) : null}
                        </div>
                      ) : undefined
                    }
                    leadingSlot={
                      <>
                        <div
                          className="flex min-w-0 items-center gap-2"
                          data-testid="thread-composer-attach"
                        >
                          <ChatFileUpload
                            images={composerTiles}
                            onImagesChange={setThreadAttachments}
                            onImagesMerge={setThreadAttachments}
                            onRemoveTile={handleRemoveComposerTile}
                            pdfText={threadPdfText}
                            pdfName={threadPdfName}
                            onPdfChange={handleThreadPdfChange}
                            maxImages={3}
                            disabled={asking || isStartingRun}
                            compact
                            acceptVideo
                            uploadHosted={uploadHostedChatStimulus}
                            pendingCount={threadFilePendingCount}
                            buttonClassName={composerTriggerClassName('external')}
                          />
                        </div>
                        <ThreadCommandMenu
                          confirmationTarget={commandConfirmationTarget}
                          showTrigger={false}
                          threadToken={thread ? (token ?? null) : null}
                          activeRun={run}
                          cutGroups={cutGroupsFromRun(run)}
                          segmentByPrefill={resultsSegmentBy}
                          onRunStarted={handleCommandRunStarted}
                          onArm={handleArmCommand}
                          onArmReport={handleArmReportCommand}
                          onArmSkill={handleArmSkillCommand}
                          rememberedAudience={rememberedAudience}
                          onRememberAudience={rememberAudience}
                          onForgetRememberedAudience={forgetRememberedAudience}
                          prelatchAudiences={audiencesPrelatched}
                          seed={commandSeed}
                          threadAssets={threadAssets}
                          composerAttachments={composerTiles}
                          appearance="external"
                        />
                      </>
                    }
                    beforeSendSlot={
                      <div className="flex shrink-0 items-center gap-0.5 md:gap-1" data-testid="thread-composer-controls">
                        <ScopeChip
                          label={chipLabel}
                          onClick={() => setPickerOpen(true)}
                          appearance="external"
                          testId="thread-scope-chip"
                        />
                        {/* Every thread turn IS an assistant turn, so unlike the
                            home composer, where effort only means something in
                            Ask, this control is always live here.
                            Frontend review BLOCKER 1: gated on the flag regardless, same
                            as the home composer's own copy of this control. */}
                        {assistantPlatformEnabled ? (
                          <AssistantProfilePicker
                            value={assistantProfile.profileId}
                            options={assistantProfile.options}
                            onChange={assistantProfile.setProfileId}
                            appearance="external"
                            testIdPrefix="thread-assistant-profile"
                          />
                        ) : null}
                        {assistantPlatformEnabled ? (
                          <ContextRing
                            contextUsage={contextUsage}
                            truncated={historyTruncated}
                            effort={contextRingEffort(assistantProfile.profileId, effort)}
                            onContinueInNewThread={continueInNewThread}
                            className="shrink-0"
                          />
                        ) : null}
                      </div>
                    }
                  />
                </div>
                )}
              </div>
            </div>

            <KnowledgeScopePicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              value={scope}
              onApply={(next) => void handleScopeApply(next)}
            />
          </div>
          </div>

          {/* One pane: stacked Outputs / Workflows / Context / Progress, an
              opened source, or one artifact expanded to its full body — each
              takes over the inside of the same drawer in turn. */}
          {assistantPlatformEnabled || hasRunPane ||
          hasArtifacts ||
          hasThreadAssets ||
          calculationSources.length > 0 ||
          threadSources.length > 0 ||
          sourceTabs.length > 0 ? (
            <ThreadSourcesDrawer
              open={railSlidIn}
              studyFrame={sessionSelection.kind === 'study'}
              onClose={() => {
                if (sessionSelection.kind === 'study') {
                  requestStudyCloseRef.current?.()
                  return
                }
                if (sourceTabs.length > 0) {
                  // Escape steps back to the sources list first: the drawer
                  // itself only closes from the header toggle.
                  setSourceTabs([])
                  setActiveSourceKey(null)
                  return
                }
                if (expandedArtifactKey !== null) {
                  // Same idea one layer in: back out of one expanded artifact
                  // to the Outputs list before the drawer closes, rather than
                  // losing the reader's place in the list entirely.
                  collapseExpandedArtifact()
                  return
                }
                changeRailOpen(false)
              }}
            >
              {/* The drawer does not change size or position when the reader
                  follows a citation or expands an artifact, so the swap inside
                  it should not snap either: keying the fade on which content
                  is showing restarts it on every move between layers. */}
              {railMounted ? (
                <div
                  key={
                    sourceTabs.length > 0
                      ? 'source'
                      : expandedArtifact
                        ? 'artifact-expanded'
                        : 'artifacts'
                  }
                  className="flex min-h-0 min-w-0 flex-1 flex-col motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
                >
                  <SessionPane
                    key={token}
                    threadToken={token || ''}
                    selection={sessionSelection}
                    onSelect={(selection) => {
                      if (selection.kind !== 'quick_study') setSearchParams(previous => {const next = new URLSearchParams(previous); next.delete('quickStudy'); next.delete('quickRun'); next.delete('quickRevision'); return next}, {replace: true})
                      setSessionTabChosen(true)
                      if (selection.kind === 'runs') {
                        collapseExpandedArtifact()
                        setSourceTabs([])
                        setActiveSourceKey(null)
                      }
                      dispatchSession({ type: 'select', selection })
                    }}
                    onMinimize={() => changeRailOpen(false)}
                    onHide={() => changeRailOpen(false)}
                    onOpenStudy={(studyToken) => openStudyFrame(studyToken, 'results')}
                    highlightStudyToken={launchedStudyToken}
                    findingsStudyTokens={findingsStudyTokens}
                    studyFrame={
                      sessionSelection.kind === 'quick_study' && sessionSelection.token ? (() => {
                        const draft = selectedNativeQuick.data
                        if (draft?.activeRun?.status === 'completed') return null
                        return draft ? <ThreadQuickStudyPanel key={`${token}:${draft.draftToken}`} threadToken={token} threadAssets={threadAssets} draft={draft} onClose={closeNativeQuickStudy} onViewed={(stage) => acknowledgeNativeQuick(draft.draftToken, draft.revision, stage, draft.activeRun?.runToken)} onSaved={next => {setSearchParams(previous => {const params = new URLSearchParams(previous); params.set('quickRevision', String(next.revision)); params.delete('quickRun'); return params}, {replace: true}); void nativeQuickStudies.refresh()}} /> : <Text size="sm" color="muted">{t(selectedNativeQuick.error ? 'threads.quickStudy.resultsFailed' : 'threads.quickStudy.loadingResults')}</Text>
                      })() : sessionSelection.kind === 'study' && sessionSelection.token ? (
                        sessionSelection.studySurface === 'results' && findingsStudyTokens.has(sessionSelection.token) ? <ThreadStudyFindings key={`${token}:${sessionSelection.token}:${quickFindingsTarget?.studyToken === sessionSelection.token ? quickFindingsTarget.artifactRevision : 'latest'}`}
                          threadToken={token} studyToken={sessionSelection.token}
                          target={quickFindingsTarget?.studyToken === sessionSelection.token && quickFindingsTarget.threadToken === token ? quickFindingsTarget : null}
                          onRendered={acknowledgeRenderedFindings} onPost={postStudyFindings} onClose={() => clearStudyFrame('hide')} isQuickStudy={quickFindingsStudyTokens.has(sessionSelection.token)} /> : <StudyFramePane
                          src={
                            sessionSelection.studySurface === 'results'
                              ? studyEmbedResultsPath(sessionSelection.token)
                              : studyEmbedDraftPath(sessionSelection.token)
                          }
                          onLaunched={handleStudyLaunched}
                          onClose={() => clearStudyFrame('hide')}
                          onBack={() => clearStudyFrame('studies')}
                          bindRequestClose={bindStudyClose}
                        />
                      ) : null
                    }
                    refreshKey={`${launchedStudyToken ?? ''}|${sessionState.roomTokens.join(',')}|${run?.token ?? ''}|${run?.status ?? ''}`}
                    {...renderSessionSections()}
                    detail={renderSessionDetail()}
                    onCloseDetail={closeSessionDetail}
                    activeRunToken={isLatestRunInProgress ? run?.token ?? null : null}
                    calculationsFocusNonce={calculationFocus?.nonce}
                    quickStudies={nativeQuickStudies.items}
                    /* A12: same action as the inline card's Review. A completed quick
                       study has no pane view (the studyFrame below renders null for it);
                       its results live in the chat as the findings message. So the row
                       jumps to that message, posting it first only if it is not there
                       yet. The server post is idempotent per run (askId
                       `quick-study-findings:<runToken>`, returns alreadyPosted), so a
                       second click never writes a second message. */
                    onOpenQuickStudy={(draft) => draft.activeRun?.status === 'completed'
                      ? void postNativeQuickFindings(draft.draftToken, draft.activeRun.runToken)
                      : openNativeQuickStudy(draft.draftToken)}
                    renderRun={(selectedRun) => renderSessionContent(false, selectedRun)}
                    room={sessionState.selection.kind === 'rooms' && sessionState.selection.token ? <RoomSession
                      key={sessionState.selection.token}
                      roomToken={sessionState.selection.token}
                      initialMessage={roomOpeningMessages[sessionState.selection.token]}
                      onInitialMessageQueued={() => {
                        const roomToken = sessionState.selection.token!
                        setRoomOpeningMessages((current) => {
                          const next = { ...current }
                          delete next[roomToken]
                          return next
                        })
                      }}
                      onClose={() => dispatchSession({ type: 'select', selection: { kind: 'rooms' } })}
                    /> : null}
                  />
                </div>
              ) : null}
            </ThreadSourcesDrawer>
          ) : null}
        </div>
      </PageContent>
      {roomSuggestion ? <ComposeModal
        open onOpenChange={(open) => { if (!open) setRoomSuggestion(null) }}
        {...roomSuggestionModalProps(roomSuggestion.suggestion, roomSuggestion, token || '', thread?.language, roomSuggestion.launch)}
        onRoomCreated={(roomToken) => {
          const launchMessageToken = roomSuggestion.launch?.sourceMessageToken
          if (launchMessageToken) {
            setReviewedLaunchRooms((current) => ({ ...current, [launchMessageToken]: { token: roomToken, createdMark: takeRoomsReadMark() } }))
          }
          openStartedRoom(roomToken, roomSuggestion.suggestion.spec.openingQuestion)
          setRoomSuggestion(null)
        }}
      /> : null}
      {fullscreenArtifact ? (
        <ThreadArtifactFullscreenView
          item={fullscreenArtifact}
          onClose={closeArtifactFullscreen}
          threadAssets={threadAssets}
          docsByToken={docsByToken}
          onOpenCitation={openCitation}
          plots={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedPlots
              : fullscreenArtifact.kind === 'report'
                ? reportPlots
                : undefined
          }
          quotes={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedQuotes
              : fullscreenArtifact.kind === 'report'
                ? reportQuotes
                : undefined
          }
          populationId={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedPopulationId
              : undefined
          }
          strips={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedStrips
              : fullscreenArtifact.kind === 'report'
                ? reportStrips
                : undefined
          }
          decisionConfidence={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedDecisionConfidence
              : undefined
          }
          plotRegion="plot"
        />
      ) : null}
      </PageLayout>
      </QuickStudyNounProvider>
    </QuickStudyCutPlotsContext.Provider>
  )
}
